<template>
  <div class="body">


    <h2>Bibliography</h2>

    <blockquote>
      <p align="justify" style="margin-left: 0; text-indent: 0">Listed below are all the roughly 1,000 references cited
        in <strong>senescence.info</strong>. I often cite my own <a
            href="http://pcwww.liv.ac.uk/~aging/publications.html">academic publications</a> for the simple reason that
        I am more familiar with them, though I always aim to provide a broad overview of the literature on any given
        topic. For the sake of brevity I do not cite every single paper related to a given topic, and I frequently cite
        review papers and books--yet I try to cite the most important primary research papers. Lastly, please see my
        list of <router-link to="/links">links</router-link> and <router-link to="/books">books</router-link> for additional sources of information.
      </P>
    </blockquote>

    <hr size="1" width="80%">


    <p align="justify">Abele,
      D., Strahl, J., Brey, T., and Philipp, E. E. (2008). &quot;Imperceptible
      senescence: ageing in the ocean quahog Arctica islandica.&quot; <i>Free Radic
        Res</i> <b>42</b>(5):474-480. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18484411">PubMed</a></p>

    <p align="justify">Ackermann,
      M., Stearns, S. C., and Jenal, U. (2003). &quot;Senescence in a bacterium with
      asymmetric division.&quot; <i>Science</i> <b>300</b>(5627):1920. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12817142">PubMed</a></p>

    <p align="justify">Acosta,
      J. C., O'Loghlen, A., Banito, A., Guijarro, M. V., Augert, A., Raguz, S.,
      Fumagalli, M., Da Costa, M., Brown, C., Popov, N.<i>, et al.</i> (2008).
      &quot;Chemokine signaling via the CXCR2 receptor reinforces senescence.&quot; <i>Cell</i>
      <b>133</b>(6):1006-1018. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18555777">PubMed</a></p>

    <p align="justify">Adelman,
      R. C. (1998). &quot;The Alzheimerization of aging: a brief update.&quot; <i>Exp
        Gerontol</i> <b>33</b>(1-2):155-157. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9467725">PubMed</a></p>

    <p align="justify">Ahmed,
      S., and Hodgkin, J. (2000). &quot;MRT-2 checkpoint protein is required for
      germline immortality and telomere replication in C. elegans.&quot; <i>Nature</i>
      <b>403</b>(6766):159-164. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10646593">PubMed</a></p>

    <p align="justify">Ahmed,
      S., Passos, J. F., Birket, M. J., Beckmann, T., Brings, S., Peters, H.,
      Birch-Machin, M. A., von Zglinicki, T., and Saretzki, G. (2008).
      &quot;Telomerase does not counteract telomere shortening but protects
      mitochondrial function under oxidative stress.&quot; <i>J Cell Sci</i> <b>121</b>(Pt
      7):1046-1053. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18334557">PubMed</a></p>

    <p align="justify">Aikata,
      H., Takaishi, H., Kawakami, Y., Takahashi, S., Kitamoto, M., Nakanishi, T.,
      Nakamura, Y., Shimamoto, F., Kajiyama, G., and Ide, T. (2000). &quot;Telomere
      reduction in human liver tissues with age and chronic inflammation.&quot; <i>Exp
        Cell Res</i> <b>256</b>(2):578-582. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10772830">PubMed</a></p>

    <p align="justify">Alcendor,
      R. R., Gao, S., Zhai, P., Zablocki, D., Holle, E., Yu, X., Tian, B., Wagner,
      T., Vatner, S. F., and Sadoshima, J. (2007). &quot;Sirt1 regulates aging and
      resistance to oxidative stress in the heart.&quot; <i>Circ Res</i> <b>100</b>(10):1512-1521.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17446436">PubMed</a></p>

    <p align="justify">Alcorta,
      D. A., Xiong, Y., Phelps, D., Hannon, G., Beach, D., and Barrett, J. C. (1996).
      &quot;Involvement of the cyclin-dependent kinase inhibitor p16 (INK4a) in
      replicative senescence of normal human fibroblasts.&quot; <i>Proc Natl Acad Sci
        U S A</i> <b>93</b>(24):13742-13747. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8943005">PubMed</a></p>

    <p align="justify">Aledo,
      J. C., Li, Y., de Magalhaes, J. P., Ruiz-Camacho, M., and Perez-Claros, J. A.
      (2011). &quot;Mitochondrially encoded methionine is inversely related to
      longevity in mammals.&quot; <i>Aging Cell</i> <b>10</b>(2):198-207. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/21108730">PubMed</a></p>

    <p align="justify">Allard,
      M., Lebre, V., Robine, J., and Calment, J. (1998). <i>Jeanne Calment: From Van
        Gogh's time to ours: 122 extraordinary years</i>. W.H. Freeman &amp; Co., New
      York.</p>

    <p align="justify">Allman,
      J., McLaughlin, T., and Hakeem, A. (1993). &quot;Brain weight and life-span in
      primate species.&quot; <i>Proc Natl Acad Sci U S A</i> <b>90</b>(1):118-122.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8419913">PubMed</a></p>

    <p align="justify">Allsopp,
      R. C., Vaziri, H., Patterson, C., Goldstein, S., Younglai, E. V., Futcher, A.
      B., Greider, C. W., and Harley, C. B. (1992). &quot;Telomere length predicts
      replicative capacity of human fibroblasts.&quot; <i>Proc Natl Acad Sci U S A</i>
      <b>89</b>(21):10114-10118. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1438199">PubMed</a></p>

    <p align="justify">Altman,
      J., and Das, G. D. (1965). &quot;Autoradiographic and histological evidence of
      postnatal hippocampal neurogenesis in rats.&quot; <i>J Comp Neurol</i> <b>124</b>(3):319-335.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5861717">PubMed</a></p>

    <p align="justify">Alvarez-Buylla,
      A., and Garcia-Verdugo, J. M. (2002). &quot;Neurogenesis in adult
      subventricular zone.&quot; <i>J Neurosci</i> <b>22</b>(3):629-634. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11826091">PubMed</a></p>

    <p align="justify">Alvarez-Erviti,
      L., Seow, Y., Yin, H., Betts, C., Lakhal, S., and Wood, M. J. (2011).
      &quot;Delivery of siRNA to the mouse brain by systemic injection of targeted
      exosomes.&quot; <i>Nat Biotechnol</i> <b>29</b>(4):341-345. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21423189">PubMed</a>
    </p>

    <p align="justify">Ames,
      B. N., Cathcart, R., Schwiers, E., and Hochstein, P. (1981). &quot;Uric acid
      provides an antioxidant defense in humans against oxidant- and radical-caused
      aging and cancer: a hypothesis.&quot; <i>Proc Natl Acad Sci U S A</i> <b>78</b>(11):6858-6862.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6947260">PubMed</a></p>

    <p align="justify">Ames,
      B. N., Shigenaga, M. K., and Hagen, T. M. (1993). &quot;Oxidants, antioxidants,
      and the degenerative diseases of aging.&quot; <i>Proc Natl Acad Sci U S A</i> <b>90</b>(17):7915-7922.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8367443">PubMed</a></p>

    <p align="justify">Ameur,
      A., Stewart, J. B., Freyer, C., Hagstrom, E., Ingman, M., Larsson, N. G., and
      Gyllensten, U. (2011). &quot;Ultra-deep sequencing of mouse mitochondrial DNA:
      mutational patterns and their origins.&quot; <i>PLoS Genet</i> <b>7</b>(3):e1002028.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21455489">PubMed</a></p>

    <p align="justify">Andziak,
      B., O'Connor T, P., and Buffenstein, R. (2005). &quot;Antioxidants do not
      explain the disparate longevity between mice and the longest-living rodent, the
      naked mole-rat.&quot; <i>Mech Ageing Dev</i> <b>126</b>(11):1206-1212.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16087218">PubMed</a></p>

    <p align="justify">Anisimov,
      V. N., Zavarzina, N. Y., Zabezhinski, M. A., Popovich, I. G., Zimina, O. A.,
      Shtylick, A. V., Arutjunyan, A. V., Oparina, T. I., Prokopenko, V. M.,
      Mikhalski, A. I.<i>, et al.</i> (2001). &quot;Melatonin increases both life
      span and tumor incidence in female CBA mice.&quot; <i>J Gerontol A Biol Sci Med
        Sci</i> <b>56</b>(7):B311-323. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11445596">PubMed</a></p>

    <p align="justify">Anisimov,
      V. N., Berstein, L. M., Egormin, P. A., Piskunova, T. S., Popovich, I. G.,
      Zabezhinski, M. A., Kovalenko, I. G., Poroshina, T. E., Semenchenko, A. V.,
      Provinciali, M.<i>, et al.</i> (2005). &quot;Effect of metformin on life span
      and on the development of spontaneous mammary tumors in HER-2/neu transgenic
      mice.&quot; <i>Exp Gerontol</i> <b>40</b>(8-9):685-693. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16125352">PubMed</a>
    </p>

    <p align="justify">Antonawich,
      F. J., Federoff, H. J., and Davis, J. N. (1999). &quot;BCL-2 transduction,
      using a herpes simplex virus amplicon, protects hippocampal neurons from
      transient global ischemia.&quot; <i>Exp Neurol</i> <b>156</b>(1):130-137.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10192784">PubMed</a></p>

    <p align="justify">Apfeld,
      J., and Kenyon, C. (1998). &quot;Cell nonautonomy of C. elegans daf-2 function
      in the regulation of diapause and life span.&quot; <i>Cell</i> <b>95</b>(2):199-210.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9790527">PubMed</a></p>

    <p align="justify">Arantes-Oliveira,
      N., Apfeld, J., Dillin, A., and Kenyon, C. (2002). &quot;Regulation of
      life-span by germ-line stem cells in Caenorhabditis elegans.&quot; <i>Science</i>
      <b>295</b>(5554):502-505. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11799246">PubMed</a></p>

    <p align="justify">Arboleda,
      G., Ramirez, N., and Arboleda, H. (2007). &quot;The neonatal progeroid syndrome
      (Wiedemann-Rautenstrauch): a model for the study of human aging?&quot; <i>Exp
        Gerontol</i> <b>42</b>(10):939-943. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17728088">PubMed</a></p>

    <p align="justify">Arking,
      D. E., Krebsova, A., Macek, M., Sr., Macek, M., Jr., Arking, A., Mian, I. S.,
      Fried, L., Hamosh, A., Dey, S., McIntosh, I.<i>, et al.</i> (2002).
      &quot;Association of human aging with a functional variant of klotho.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>99</b>(2):856-861. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11792841">PubMed</a>
    </p>

    <p align="justify">Arnold,
      J. M., and Carlson, B. A. (1986). &quot;Living Nautilus embryos: Preliminary
      observations.&quot; <i>Science</i> <b>232</b>:73-76. </p>

    <p align="justify">Artandi,
      S. E., Alson, S., Tietze, M. K., Sharpless, N. E., Ye, S., Greenberg, R. A.,
      Castrillon, D. H., Horner, J. W., Weiler, S. R., Carrasco, R. D.<i>, et al.</i>
      (2002). &quot;Constitutive telomerase expression promotes mammary carcinomas in
      aging mice.&quot; <i>Proc Natl Acad Sci U S A</i> <b>99</b>(12):8191-8196. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12034875">PubMed</a></p>

    <p align="justify">Atadja,
      P., Wong, H., Garkavtsev, I., Veillette, C., and Riabowol, K. (1995).
      &quot;Increased activity of p53 in senescing fibroblasts.&quot; <i>Proc Natl
        Acad Sci U S A</i> <b>92</b>(18):8348-8352. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7667293">PubMed</a></p>

    <p align="justify">Atzmon,
      G., Schechter, C., Greiner, W., Davidson, D., Rennert, G., and Barzilai, N.
      (2004). &quot;Clinical phenotype of families with longevity.&quot; <i>J Am
        Geriatr Soc</i> <b>52</b>(2):274-277. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14728640">PubMed</a></p>

    <p align="justify">Atzmon,
      G., Cho, M., Cawthon, R. M., Budagov, T., Katz, M., Yang, X., Siegel, G.,
      Bergman, A., Huffman, D. M., Schechter, C. B.<i>, et al.</i> (2010).
      &quot;Evolution in health and medicine Sackler colloquium: Genetic variation in
      human telomerase is associated with telomere length in Ashkenazi
      centenarians.&quot; <i>Proc Natl Acad Sci U S A</i> <b>107 Suppl 1</b>:1710-1717.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19915151">PubMed</a></p>

    <p align="justify">Austad,
      S. N. (1988). &quot;The adaptable opossum.&quot; <i>Sci Am</i> (Feb.):98-104. </p>

    <p align="justify">Austad,
      S. N. (1989). &quot;Life extension by dietary restriction in the bowl and doily
      spider, Frontinella pyramitela.&quot; <i>Exp Gerontol</i> <b>24</b>(1):83-92.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2707314">PubMed</a></p>

    <p align="justify">Austad,
      S. N., and Fischer, K. E. (1991). &quot;Mammalian aging, metabolism, and
      ecology: evidence from the bats and marsupials.&quot; <i>J Gerontol</i> <b>46</b>(2):B47-53.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1997563">PubMed</a></p>

    <p align="justify">Austad,
      S. N., and Fischer, K. E. (1992). &quot;Primate longevity: Its place in the
      mammalian scheme.&quot; <i>Am J Primatol</i> <b>28</b>(4):251-261. </p>

    <p align="justify">Austad,
      S. N. (1997a). <i>Why We Age: What Science Is Discovering about the Body's
        Journey through Life</i>. John Wiley &amp; Sons, New York.</p>

    <p align="justify">Austad,
      S. N. (1997b). &quot;Comparative aging and life histories in mammals.&quot; <i>Exp
        Gerontol</i> <b>32</b>(1-2):23-38. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9088899">PubMed</a></p>

    <p align="justify">Austad,
      S. N. (1997c). &quot;Small nonhuman primates as potential models of human
      aging.&quot; <i>Ilar J</i> <b>38</b>(3):142-147. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11528056">PubMed</a>
    </p>

    <p align="justify">Austad,
      S. N. (2001). &quot;An experimental paradigm for the study of slowly aging
      organisms.&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):599-605. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11295502">PubMed</a></p>

    <p align="justify">Austad,
      S. N. (2004). &quot;Is aging programed?&quot; <i>Aging Cell</i> <b>3</b>(5):249-251.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15379846">PubMed</a></p>

    <p align="justify">Austad,
      S. N. (2005). &quot;Diverse aging rates in metazoans: targets for functional
      genomics.&quot; <i>Mech Ageing Dev</i> <b>126</b>(1):43-49. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15610761">PubMed</a>
    </p>

    <p align="justify">Austad,
      S. N. (2006). &quot;Why women live longer than men: sex differences in
      longevity.&quot; <i>Gend Med</i> <b>3</b>(2):79-92. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16860268">PubMed</a></p>

    <p align="justify">Avraam,
      D., de Magalhaes, J. P., and Vasiev, B. (2013). &quot;A mathematical model of
      mortality dynamics across the lifespan combining heterogeneity and stochastic effects.&quot;
      <i>Exp Gerontol</i> <b>48</b>(8):801-811. <a href="http://www.ncbi.nlm.nih.gov/pubmed/23707231">PubMed</a></p>

    <p align="justify">Ayyadevara,
      S., Alla, R., Thaden, J. J., and Shmookler Reis, R. J. (2008). &quot;Remarkable
      longevity and stress resistance of nematode PI3K-null mutants.&quot; <i>Aging
        Cell</i> <b>7</b>(1):13-22. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17996009">PubMed</a></p>

    <p align="justify">Baker,
      D. J., Wijshake, T., Tchkonia, T., LeBrasseur, N. K., Childs, B. G., van de
      Sluis, B., Kirkland, J. L., and van Deursen, J. M. (2011). &quot;Clearance of
      p16Ink4a-positive senescent cells delays ageing-associated disorders.&quot; <i>Nature</i>
      <b>479</b>(7372):232-236. <a href="http://www.ncbi.nlm.nih.gov/pubmed/22048312">PubMed</a></p>

    <p align="justify">Baker,
      D. J., Dawlaty, M. M., Wijshake, T., Jeganathan, K. B., Malureanu, L., van Ree,
      J. H., Crespo-Diaz, R., Reyes, S., Seaburg, L., Shapiro, V.<i>, et al.</i>
      (2013). &quot;Increased expression of BubR1 protects against aneuploidy and
      cancer and extends healthy lifespan.&quot; <i>Nat Cell Biol</i> <b>15</b>(1):96-102.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23242215">PubMed</a></p>

    <p align="justify">Baker,
      D. J., Childs, B. G., Durik, M., Wijers, M. E., Sieben, C. J., Zhong, J.,
      Saltness, R. A., Jeganathan, K. B., Verzosa, G. C., Pezeshki, A.<i>, et al.</i>
      (2016). &quot;Naturally occurring p16(Ink4a)-positive cells shorten healthy
      lifespan.&quot; <i>Nature</i> <b>530</b>(7589):184-189. <a href="http://www.ncbi.nlm.nih.gov/pubmed/26840489">PubMed</a>
    </p>

    <p align="justify">Balin,
      A. K. E. (1994). <i>Practical Handbook of Biologic Age Determination</i>. CRC
      Press, Boca Raton, FL.</p>

    <p align="justify">Barja,
      G., and Herrero, A. (2000). &quot;Oxidative damage to mitochondrial DNA is
      inversely related to maximum life span in the heart and brain of mammals.&quot;
      <i>Faseb J</i> <b>14</b>(2):312-318. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10657987">PubMed</a></p>

    <p align="justify">Barja,
      G. (2002). &quot;Endogenous oxidative stress: relationship to aging, longevity
      and caloric restriction.&quot; <i>Ageing Res Rev</i> <b>1</b>(3):397-411.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12067594">PubMed</a></p>

    <p align="justify">Barker,
      M. G., and Walmsley, R. M. (1999). &quot;Replicative ageing in the fission
      yeast Schizosaccharomyces pombe.&quot; <i>Yeast</i> <b>15</b>(14):1511-1518.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10514568">PubMed</a></p>

    <p align="justify">Bartke,
      A., Coschigano, K., Kopchick, J., Chandrashekar, V., Mattison, J., Kinney, B.,
      and Hauck, S. (2001a). &quot;Genes that prolong life: relationships of growth
      hormone and growth to aging and life span.&quot; <i>J Gerontol A Biol Sci Med
        Sci</i> <b>56</b>(8):B340-349. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11487592">PubMed</a></p>

    <p align="justify">Bartke,
      A., Wright, J. C., Mattison, J. A., Ingram, D. K., Miller, R. A., and Roth, G.
      S. (2001b). &quot;Extending the lifespan of long-lived mice.&quot; <i>Nature</i>
      <b>414</b>(6862):412. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11719795">PubMed</a></p>

    <p align="justify">Bartke,
      A. (2003). &quot;Can growth hormone (GH) accelerate aging? Evidence from
      GH-transgenic mice.&quot; <i>Neuroendocrinology</i> <b>78</b>(4):210-216.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14583653">PubMed</a></p>

    <p align="justify">Bartke,
      A. (2005). &quot;Minireview: role of the growth hormone/insulin-like growth
      factor system in mammalian aging.&quot; <i>Endocrinology</i> <b>146</b>(9):3718-3723.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15919742">PubMed</a></p>

    <p align="justify">Barton-Davis,
      E. R., Shoturma, D. I., Musaro, A., Rosenthal, N., and Sweeney, H. L. (1998).
      &quot;Viral mediated expression of insulin-like growth factor I blocks the
      aging-related loss of skeletal muscle function.&quot; <i>Proc Natl Acad Sci U S
        A</i> <b>95</b>(26):15603-15607. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9861016">PubMed</a></p>

    <p align="justify">Bass,
      T. M., Weinkove, D., Houthoofd, K., Gems, D., and Partridge, L. (2007).
      &quot;Effects of resveratrol on lifespan in Drosophila melanogaster and
      Caenorhabditis elegans.&quot; <i>Mech Ageing Dev</i> <b>128</b>(10):546-552.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17875315">PubMed</a></p>

    <p align="justify">Bassham,
      S., Beam, A., and Shampay, J. (1998). &quot;Telomere variation in Xenopus
      laevis.&quot; <i>Mol Cell Biol</i> <b>18</b>(1):269-275. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9418874">PubMed</a>
    </p>

    <p align="justify">Bastianetto,
      S., and Quirion, R. (2002). &quot;Natural extracts as possible protective
      agents of brain aging.&quot; <i>Neurobiol Aging</i> <b>23</b>(5):891-897.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12392793">PubMed</a></p>

    <p align="justify">Bauer,
      J. H., Poon, P. C., Glatt-Deeley, H., Abrams, J. M., and Helfand, S. L. (2005).
      &quot;Neuronal expression of p53 dominant-negative proteins in adult Drosophila
      melanogaster extends life span.&quot; <i>Curr Biol</i> <b>15</b>(22):2063-2068.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16303568">PubMed</a></p>

    <p align="justify">Baur,
      J. A., Pearson, K. J., Price, N. L., Jamieson, H. A., Lerin, C., Kalra, A.,
      Prabhu, V. V., Allard, J. S., Lopez-Lluch, G., Lewis, K.<i>, et al.</i> (2006).
      &quot;Resveratrol improves health and survival of mice on a high-calorie
      diet.&quot; <i>Nature</i> <b>444</b>(7117):337-342. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/17086191">PubMed</a></p>

    <p align="justify">Baur,
      J. A., and Sinclair, D. A. (2006). &quot;Therapeutic potential of resveratrol:
      the in vivo evidence.&quot; <i>Nat Rev Drug Discov</i> <b>5</b>(6):493-506.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16732220">PubMed</a></p>

    <p align="justify">Bayne,
      A. C., Mockett, R. J., Orr, W. C., and Sohal, R. S. (2005). &quot;Enhanced
      catabolism of mitochondrial superoxide/hydrogen peroxide and aging in
      transgenic Drosophila.&quot; <i>Biochem J</i> <b>391</b>(Pt 2):277-284.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15954861">PubMed</a></p>

    <p align="justify">Baynes,
      J. W. (2000). &quot;From life to death--the struggle between chemistry and
      biology during aging: the Maillard reaction as an amplifier of genomic
      damage.&quot; <i>Biogerontology</i> <b>1</b>(3):235-246. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11707900">PubMed</a>
    </p>

    <p align="justify">Bayreuther,
      K., Rodemann, H. P., Hommel, R., Dittmann, K., Albiez, M., and Francz, P. I.
      (1988). &quot;Human skin fibroblasts in vitro differentiate along a terminal
      cell lineage.&quot; <i>Proc Natl Acad Sci U S A</i> <b>85</b>(14):5112-5116.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/3393534">PubMed</a></p>

    <p align="justify">Beckman,
      K. B., and Ames, B. N. (1998). &quot;The free radical theory of aging
      matures.&quot; <i>Physiol Rev</i> <b>78</b>(2):547-581. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9562038">PubMed</a>
    </p>

    <p align="justify">Bell,
      G. (1988). <i>Sex and Death in Protozoa. The History of an Obsession</i>.
      Cambridge University Press, Cambridge.</p>

    <p align="justify">Benard,
      C., and Hekimi, S. (2002). &quot;Long-lived mutants, the rate of aging,
      telomeres and the germline in Caenorhabditis elegans.&quot; <i>Mech Ageing Dev</i>
      <b>123</b>(8):869-880. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12044935">PubMed</a></p>

    <p align="justify">Berardi,
      P., Meyyappan, M., and Riabowol, K. T. (2003). &quot;A novel transcriptional
      inhibitory element differentially regulates the cyclin D1 gene in senescent
      cells.&quot; <i>J Biol Chem</i> <b>278</b>(9):7510-7519. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12464605">PubMed</a>
    </p>

    <p align="justify">Bergmann,
      O., Bhardwaj, R. D., Bernard, S., Zdunek, S., Barnabe-Heider, F., Walsh, S.,
      Zupicich, J., Alkass, K., Buchholz, B. A., Druid, H.<i>, et al.</i> (2009).
      &quot;Evidence for cardiomyocyte renewal in humans.&quot; <i>Science</i> <b>324</b>(5923):98-102.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19342590">PubMed</a></p>

    <p align="justify">Bergquist,
      D. C., Williams, F. M., and Fisher, C. R. (2000). &quot;Longevity record for
      deep-sea invertebrate.&quot; <i>Nature</i> <b>403</b>(6769):499-500. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10676948">PubMed</a></p>

    <p align="justify">Bernardes
      de Jesus, B., Vera, E., Schneeberger, K., Tejera, A. M., Ayuso, E., Bosch, F.,
      and Blasco, M. A. (2012). &quot;Telomerase gene therapy in adult and old mice
      delays aging and increases longevity without increasing cancer.&quot; <i>EMBO
        Mol Med</i> <b>4</b>(8):691-704. <a href="http://www.ncbi.nlm.nih.gov/pubmed/22585399">PubMed</a></p>

    <p align="justify">Berner,
      Y. N., and Stern, F. (2004). &quot;Energy restriction controls aging through
      neuroendocrine signal transduction.&quot; <i>Ageing Res Rev</i> <b>3</b>(2):189-198.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15177054">PubMed</a></p>

    <p align="justify">Bersell,
      K., Arab, S., Haring, B., and Kuhn, B. (2009). &quot;Neuregulin1/ErbB4
      signaling induces cardiomyocyte proliferation and repair of heart injury.&quot;
      <i>Cell</i> <b>138</b>(2):257-270. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19632177">PubMed</a></p>

    <p align="justify">Berzlanovich,
      A. M., Keil, W., Waldhoer, T., Sim, E., Fasching, P., and Fazeny-Dorner, B.
      (2005). &quot;Do centenarians die healthy? An autopsy study.&quot; <i>J
        Gerontol A Biol Sci Med Sci</i> <b>60</b>(7):862-865. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16079208">PubMed</a>
    </p>

    <p align="justify">Besson,
      A., Salemi, S., Gallati, S., Jenal, A., Horn, R., Mullis, P. S., and Mullis, P.
      E. (2003). &quot;Reduced longevity in untreated patients with isolated growth
      hormone deficiency.&quot; <i>J Clin Endocrinol Metab</i> <b>88</b>(8):3664-3667.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12915652">PubMed</a></p>

    <p align="justify">Bidder,
      G. P. (1932). &quot;Senescence.&quot; <i>Brit Med J</i> <b>2</b>:583-585. </p>

    <p align="justify">Birney,
      E., Stamatoyannopoulos, J. A., Dutta, A., Guigo, R., Gingeras, T. R.,
      Margulies, E. H., Weng, Z., Snyder, M., Dermitzakis, E. T., Thurman, R. E.<i>,
        et al.</i> (2007). &quot;Identification and analysis of functional elements in
      1% of the human genome by the ENCODE pilot project.&quot; <i>Nature</i> <b>447</b>(7146):799-816.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17571346">PubMed</a></p>

    <p align="justify">Bishop,
      N. A., and Guarente, L. (2007). &quot;Two neurons mediate
      diet-restriction-induced longevity in C. elegans.&quot; <i>Nature</i> <b>447</b>(7144):545-549.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17538612">PubMed</a></p>

    <p align="justify">Bjelakovic,
      G., Nikolova, D., Gluud, L. L., Simonetti, R. G., and Gluud, C. (2007).
      &quot;Mortality in randomized trials of antioxidant supplements for primary and
      secondary prevention: systematic review and meta-analysis.&quot; <i>Jama</i> <b>297</b>(8):842-857.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17327526">PubMed</a></p>

    <p align="justify">Bjelakovic,
      G., Nikolova, D., Gluud, L. L., Simonetti, R. G., and Gluud, C. (2008).
      &quot;Antioxidant supplements for prevention of mortality in healthy
      participants and patients with various diseases.&quot; <i>Cochrane Database
        Syst Rev</i> (2):CD007176. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18425980">PubMed</a></p>

    <p align="justify">Bjorklund,
      A., and Lindvall, O. (2000). &quot;Self-repair in the brain.&quot; <i>Nature</i>
      <b>405</b>(6789):892-893, 895. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10879515">PubMed</a></p>

    <p align="justify">Blackburn,
      E. H. (2000). &quot;Telomere states and cell fates.&quot; <i>Nature</i> <b>408</b>(6808):53-56.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11081503">PubMed</a></p>

    <p align="justify">Blackman,
      M. R., Sorkin, J. D., Munzer, T., Bellantoni, M. F., Busby-Whitehead, J.,
      Stevens, T. E., Jayme, J., O'Connor, K. G., Christmas, C., Tobin, J. D.<i>, et
        al.</i> (2002). &quot;Growth hormone and sex steroid administration in healthy

      aged women and men: a randomized controlled trial.&quot; <i>Jama</i> <b>288</b>(18):2282-2292.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12425705">PubMed</a></p>

    <p align="justify">Blasco,
      M. A., Lee, H. W., Hande, M. P., Samper, E., Lansdorp, P. M., DePinho, R. A.,
      and Greider, C. W. (1997). &quot;Telomere shortening and tumor formation by
      mouse cells lacking telomerase RNA.&quot; <i>Cell</i> <b>91</b>(1):25-34.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9335332">PubMed</a></p>

    <p align="justify">Blasco,
      M. A., and Hahn, W. C. (2003). &quot;Evolving views of telomerase and
      cancer.&quot; <i>Trends Cell Biol</i> <b>13</b>(6):289-294. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12791294">PubMed</a>
    </p>

    <p align="justify">Blasco,
      M. A. (2005). &quot;Telomeres and human disease: ageing, cancer and
      beyond.&quot; <i>Nat Rev Genet</i> <b>6</b>(8):611-622. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16136653">PubMed</a>
    </p>

    <p align="justify">Bodkin,
      N. L., Alexander, T. M., Ortmeyer, H. K., Johnson, E., and Hansen, B. C.
      (2003). &quot;Mortality and morbidity in laboratory-maintained Rhesus monkeys
      and effects of long-term dietary restriction.&quot; <i>J Gerontol A Biol Sci
        Med Sci</i> <b>58</b>(3):212-219. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12634286">PubMed</a></p>

    <p align="justify">Bodnar,
      A. G., Ouellette, M., Frolkis, M., Holt, S. E., Chiu, C. P., Morin, G. B.,
      Harley, C. B., Shay, J. W., Lichtsteiner, S., and Wright, W. E. (1998).
      &quot;Extension of life-span by introduction of telomerase into normal human
      cells.&quot; <i>Science</i> <b>279</b>(5349):349-352. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9454332">PubMed</a></p>

    <p align="justify">Bohr,
      V. A., Brosh, R. M., Jr., von Kobbe, C., Opresko, P., and Karmakar, P. (2002).
      &quot;Pathways defective in the human premature aging disease Werner
      syndrome.&quot; <i>Biogerontology</i> <b>3</b>(1-2):89-94. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12014850">PubMed</a>
    </p>

    <p align="justify">Bolli,
      R., Chugh, A. R., D'Amario, D., Loughran, J. H., Stoddard, M. F., Ikram, S.,
      Beache, G. M., Wagner, S. G., Leri, A., Hosoda, T.<i>, et al.</i> (2011).
      &quot;Cardiac stem cells in patients with ischaemic cardiomyopathy (SCIPIO):
      initial results of a randomised phase 1 trial.&quot; <i>Lancet</i> <b>378</b>(9806):1847-1857.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22088800">PubMed</a></p>

    <p align="justify">Bonafe,
      M., Barbieri, M., Marchegiani, F., Olivieri, F., Ragno, E., Giampieri, C.,
      Mugianesi, E., Centurelli, M., Franceschi, C., and Paolisso, G. (2003).
      &quot;Polymorphic variants of insulin-like growth factor I (IGF-I) receptor and
      phosphoinositide 3-kinase genes affect IGF-I plasma levels and human longevity:
      cues for an evolutionarily conserved mechanism of life span control.&quot; <i>J
        Clin Endocrinol Metab</i> <b>88</b>(7):3299-3304. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12843179">PubMed</a></p>

    <p align="justify">Bond,
      J., Haughton, M., Blaydes, J., Gire, V., Wynford-Thomas, D., and Wyllie, F.
      (1996). &quot;Evidence that transcriptional activation by p53 plays a direct
      role in the induction of cellular senescence.&quot; <i>Oncogene</i> <b>13</b>(10):2097-2104.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8950976">PubMed</a></p>

    <p align="justify">Bonelli,
      M. A., Alfieri, R. R., Petronini, P. G., Brigotti, M., Campanini, C., and
      Borghetti, A. F. (1999). &quot;Attenuated expression of 70-kDa heat shock
      protein in WI-38 human fibroblasts during aging in vitro.&quot; <i>Exp Cell Res</i>
      <b>252</b>(1):20-32. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10502396">PubMed</a></p>

    <p align="justify">Bonkowski,
      M. S., Rocha, J. S., Masternak, M. M., Al Regaiey, K. A., and Bartke, A.
      (2006). &quot;Targeted disruption of growth hormone receptor interferes with
      the beneficial actions of calorie restriction.&quot; <i>Proc Natl Acad Sci U S
        A</i> <b>103</b>(20):7901-7905. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16682650">PubMed</a></p>

    <p align="justify">Bons,
      N., Mestre, N., and Petter, A. (1992). &quot;Senile plaques and neurofibrillary
      changes in the brain of an aged lemurian primate, Microcebus murinus.&quot; <i>Neurobiol
        Aging</i> <b>13</b>(1):99-105. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1542387">PubMed</a></p>

    <p align="justify">Boquoi,
      A., Arora, S., Chen, T., Litwin, S., Koh, J., and Enders, G. H. (2015).
      &quot;Reversible cell cycle inhibition and premature aging features imposed by
      conditional expression of p16Ink4a.&quot; <i>Aging Cell</i> <b>14</b>(1):139-147.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/25481981">PubMed</a></p>

    <p align="justify">Bowen,
      R. L., and Atwood, C. S. (2004). &quot;Living and dying for sex. A theory of
      aging based on the modulation of cell cycle signaling by reproductive
      hormones.&quot; <i>Gerontology</i> <b>50</b>(5):265-290. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15331856">PubMed</a>
    </p>

    <p align="justify">Bowles,
      J. (2000). &quot;Shattered: Medawar's test tubes and their enduring legacy of
      chaos.&quot; <i>Med Hypotheses</i> <b>54</b>(2):326-339. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10790771">PubMed</a>
    </p>

    <p align="justify">Brack,
      C., Lithgow, G., Osiewacz, H., and Toussaint, O. (2000). &quot;EMBO WORKSHOP
      REPORT: Molecular and cellular gerontology Serpiano, Switzerland, September
      18-22, 1999.&quot; <i>Embo J</i> <b>19</b>(9):1929-1934. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10790359">PubMed</a>
    </p>

    <p align="justify">Brakefield,
      P. M., Gems, D., Cowen, T., Christensen, K., Grubeck-Loebenstein, B., Keller,
      L., Oeppen, J., Rodriguez-Pena, A., Stazi, M. A., Tatar, M.<i>, et al.</i>
      (2005). &quot;What are the effects of maternal and pre-adult environments on
      ageing in humans, and are there lessons from animal models?&quot; <i>Mech
        Ageing Dev</i> <b>126</b>(3):431-438. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15664631">PubMed</a></p>

    <p align="justify">Bredenkamp,
      N., Nowell, C. S., and Blackburn, C. C. (2014). &quot;Regeneration of the aged
      thymus by a single transcription factor.&quot; <i>Development</i> <b>141</b>(8):1627-1637.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/24715454">PubMed</a></p>

    <p align="justify">Breese,
      C. R., Ingram, R. L., and Sonntag, W. E. (1991). &quot;Influence of age and
      long-term dietary restriction on plasma insulin-like growth factor-1 (IGF-1),
      IGF-1 gene expression, and IGF-1 binding proteins.&quot; <i>J Gerontol</i> <b>46</b>(5):B180-187.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1716275">PubMed</a></p>

    <p align="justify">Brocas,
      J., and Verzar, F. (1961). &quot;The aging of Xenopus laevis, a South African
      frog.&quot; <i>Gerontologia</i> <b>5</b>:228-240. <a href="http://www.ncbi.nlm.nih.gov/pubmed/13873222">PubMed</a>
    </p>

    <p align="justify">Brock,
      M. A., and Strehler, B. L. (1963). &quot;Studies on the comparative physiology
      of aging. IV. Age and mortality of some marine Cnidaria in the
      laboratory.&quot; <i>J Gerontol</i> <b>18</b>:23-28. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/14015619">PubMed</a></p>

    <p align="justify">Brock,
      T. D. (1997). <i>Biology of Microorganisms</i>. Prentice Hall &amp; IBD, New
      Jersey.</p>

    <p align="justify">Brockes,
      J. P., Kumar, A., and Velloso, C. P. (2001). &quot;Regeneration as an
      evolutionary variable.&quot; <i>J Anat</i> <b>199</b>(Pt 1-2):3-11. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11523827">PubMed</a></p>

    <p align="justify">Bronikowski,
      A. M., Alberts, S. C., Altmann, J., Packer, C., Carey, K. D., and Tatar, M.
      (2002). &quot;The aging baboon: comparative demography in a non-human
      primate.&quot; <i>Proc Natl Acad Sci U S A</i> <b>99</b>(14):9591-9595.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12082185">PubMed</a></p>

    <p align="justify">Brown-Borg,
      H. M., Borg, K. E., Meliska, C. J., and Bartke, A. (1996). &quot;Dwarf mice and
      the ageing process.&quot; <i>Nature</i> <b>384</b>(6604):33. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8900272">PubMed</a>
    </p>

    <p align="justify">Brown-Borg,
      H. M., Rakoczy, S. G., and Uthus, E. O. (2005). &quot;Growth hormone alters
      methionine and glutathione metabolism in Ames dwarf mice.&quot; <i>Mech Ageing
        Dev</i> <b>126</b>(3):389-398. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15664625">PubMed</a></p>

    <p align="justify">Brunet-Rossinni,
      A. K., and Austad, S. N. (2004). &quot;Ageing studies on bats: a review.&quot; <i>Biogerontology</i>
      <b>5</b>(4):211-222. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15314271">PubMed</a></p>

    <p align="justify">Brunk,
      U., Ericsson, J. L., Ponten, J., and Westermark, B. (1973). &quot;Residual
      bodies and &quot;aging&quot; in cultured human glia cells. Effect of entrance
      into phase 3 and prolonged periods of confluence.&quot; <i>Exp Eye Res</i> <b>79</b>(1):1-14.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4575548">PubMed</a></p>

    <p align="justify">Brunmark,
      A., Collins, V. P., Thaw, H., and Brunk, U. (1986). &quot;Lipofuscin
      accumulation in cultured non-dividing cells as a function of time and oxygen
      tension.&quot; <i>Scan Electron Microsc</i> <b>1</b>:189-192. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/3738415">PubMed</a></p>

    <p align="justify">Bruunsgaard,
      H., Pedersen, M., and Pedersen, B. K. (2001). &quot;Aging and proinflammatory
      cytokines.&quot; <i>Curr Opin Hematol</i> <b>8</b>(3):131-136. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11303144">PubMed</a></p>

    <p align="justify">Bryan,
      T. M., Englezou, A., Gupta, J., Bacchetti, S., and Reddel, R. R. (1995).
      &quot;Telomere elongation in immortal human cells without detectable telomerase
      activity.&quot; <i>Embo J</i> <b>14</b>(17):4240-4248. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7556065">PubMed</a></p>

    <p align="justify">Buchkovich,
      K., Duffy, L. A., and Harlow, E. (1989). &quot;The retinoblastoma protein is
      phosphorylated during specific phases of the cell cycle.&quot; <i>Cell</i> <b>58</b>(6):1097-1105.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2673543">PubMed</a></p>

    <p align="justify">Budovsky,
      A., Craig, T., Wang, J., Tacutu, R., Csordas, A., Lourenco, J., Fraifeld, V.
      E., and de Magalhaes, J. P. (2013). &quot;LongevityMap: a database of human
      genetic variants associated with longevity.&quot; <i>Trends Genet</i> <b>29</b>(10):559-560.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23998809">PubMed</a></p>

    <p align="justify">Buffenstein,
      R. (2005). &quot;The naked mole-rat: a new long-living model for human aging
      research.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>60</b>(11):1369-1377.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16339321">PubMed</a></p>

    <p align="justify">Buffenstein,
      R. (2008). &quot;Negligible senescence in the longest living rodent, the naked
      mole-rat: insights from a successfully aging species.&quot; <i>J Comp Physiol B</i>
      <b>178</b>(4):439-445. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18180931">PubMed</a></p>

    <p align="justify">Bukovsky,
      A., Caudle, M. R., Svetlikova, M., Wimalasena, J., Ayala, M. E., and Dominguez,
      R. (2005). &quot;Oogenesis in adult mammals, including humans: a review.&quot; <i>Endocrine</i>
      <b>26</b>(3):301-316. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16034186">PubMed</a></p>

    <p align="justify">Burck,
      U., Goebel, H. H., Kuhlendahl, H. D., Meier, C., and Goebel, K. M. (1981).
      &quot;Neuromyopathy and vitamin E deficiency in man.&quot; <i>Neuropediatrics</i>
      <b>12</b>(3):267-278. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6945489">PubMed</a></p>

    <p align="justify">Burd,
      C. E., Sorrentino, J. A., Clark, K. S., Darr, D. B., Krishnamurthy, J., Deal,
      A. M., Bardeesy, N., Castrillon, D. H., Beach, D. H., and Sharpless, N. E.
      (2013). &quot;Monitoring tumorigenesis and senescence in vivo with a
      p16(INK4a)-luciferase model.&quot; <i>Cell</i> <b>152</b>(1-2):340-351.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23332765">PubMed</a></p>

    <p align="justify">Burnett,
      C., Valentini, S., Cabreiro, F., Goss, M., Somogyvari, M., Piper, M. D.,
      Hoddinott, M., Sutphin, G. L., Leko, V., McElwee, J. J.<i>, et al.</i> (2011).
      &quot;Absence of effects of Sir2 overexpression on lifespan in C. elegans and
      Drosophila.&quot; <i>Nature</i> <b>477</b>(7365):482-485. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21938067">PubMed</a>
    </p>

    <p align="justify">Burt,
      R. K., Loh, Y., Pearce, W., Beohar, N., Barr, W. G., Craig, R., Wen, Y., Rapp,
      J. A., and Kessler, J. (2008). &quot;Clinical applications of blood-derived and
      marrow-derived stem cells for nonmalignant diseases.&quot; <i>Jama</i> <b>299</b>(8):925-936.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18314435">PubMed</a></p>

    <p align="justify">Butler,
      R. N., Austad, S. N., Barzilai, N., Braun, A., Helfand, S., Larsen, P. L.,
      McCormick, A. M., Perls, T. T., Shuldiner, A. R., Sprott, R. L.<i>, et al.</i>
      (2003). &quot;Longevity genes: from primitive organisms to humans.&quot; <i>J
        Gerontol A Biol Sci Med Sci</i> <b>58</b>(7):581-584. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12865472">PubMed</a>
    </p>

    <p align="justify">Butler,
      R. N., Miller, R. A., Perry, D., Carnes, B. A., Williams, T. F., Cassel, C.,
      Brody, J., Bernard, M. A., Partridge, L., Kirkwood, T.<i>, et al.</i> (2008).
      &quot;New model of health promotion and disease prevention for the 21st
      century.&quot; <i>Bmj</i> <b>337</b>:a399. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18614506">PubMed</a></p>

    <p align="justify">Cailliet,
      G. M., Andrews, A. H., Burton, E. J., Watters, D. L., Kline, D. E., and
      Ferry-Graham, L. A. (2001). &quot;Age determination and validation studies of
      marine fishes: do deep- dwellers live longer?&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):739-764.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295512">PubMed</a></p>

    <p align="justify">Calder,
      W. A. (1984). <i>Size, Function, and Life History</i>. Harvard University
      Press, Cambridge.</p>

    <p align="justify">Campisi,
      J. (1999). &quot;Replicative senescence and immortalization.&quot; In: <i>The
        molecular basis of cell cycle and growth control</i>, Stein, G. S., Baserga,
      A., Giordano, A. and Denhardt, D. T. (ed.).  Wiley-Liss, New York, 348-373.</p>

    <p align="justify">Campisi,
      J. (2000). &quot;Aging, chromatin, and food restriction--connecting the dots.&quot;
      <i>Science</i> <b>289</b>(5487):2062-2063. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11032557">PubMed</a></p>

    <p align="justify">Campisi,
      J., Kim, S. H., Lim, C. S., and Rubio, M. (2001). &quot;Cellular senescence,
      cancer and aging: the telomere connection.&quot; <i>Exp Gerontol</i> <b>36</b>(10):1619-1637.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11672984">PubMed</a></p>

    <p align="justify">Campisi,
      J. (2005). &quot;Senescent cells, tumor suppression, and organismal aging: good
      citizens, bad neighbors.&quot; <i>Cell</i> <b>120</b>(4):513-522. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15734683">PubMed</a></p>

    <p align="justify">Campisi,
      J., and d'Adda di Fagagna, F. (2007). &quot;Cellular senescence: when bad
      things happen to good cells.&quot; <i>Nat Rev Mol Cell Biol</i> <b>8</b>(9):729-740.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17667954">PubMed</a></p>

    <p align="justify">Cao,
      L., Li, W., Kim, S., Brodie, S. G., and Deng, C. X. (2003). &quot;Senescence,
      aging, and malignant transformation mediated by p53 in mice lacking the Brca1
      full-length isoform.&quot; <i>Genes Dev</i> <b>17</b>(2):201-213. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12533509">PubMed</a></p>

    <p align="justify">Caplan,
      A. L. (2005). &quot;Death as an unnatural process. Why is it wrong to seek a
      cure for aging?&quot; <i>EMBO Rep</i> <b>6 Spec No</b>:S72-75. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15995668">PubMed</a></p>

    <p align="justify">Carlson,
      B. M. (2003). &quot;Muscle regeneration in amphibians and mammals: passing the
      torch.&quot; <i>Dev Dyn</i> <b>226</b>(2):167-181. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12557196">PubMed</a></p>

    <p align="justify">Carlson,
      M. E., Hsu, M., and Conboy, I. M. (2008). &quot;Imbalance between pSmad3 and
      Notch induces CDK inhibitors in old muscle stem cells.&quot; <i>Nature</i> <b>454</b>(7203):528-532.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18552838">PubMed</a></p>

    <p align="justify">Carney,
      J. P., Maser, R. S., Olivares, H., Davis, E. M., Le Beau, M., Yates, J. R.,
      3rd, Hays, L., Morgan, W. F., and Petrini, J. H. (1998). &quot;The
      hMre11/hRad50 protein complex and Nijmegen breakage syndrome: linkage of
      double-strand break repair to the cellular DNA damage response.&quot; <i>Cell</i>
      <b>93</b>(3):477-486. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9590181">PubMed</a></p>

    <p align="justify">Carroll,
      S. B. (2003). &quot;Genetics and the making of Homo sapiens.&quot; <i>Nature</i>
      <b>422</b>(6934):849-857. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12712196">PubMed</a></p>

    <p align="justify">Castro,
      P., Giri, D., Lamb, D., and Ittmann, M. (2003). &quot;Cellular senescence in
      the pathogenesis of benign prostatic hyperplasia.&quot; <i>Prostate</i> <b>55</b>(1):30-38.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12640658">PubMed</a></p>

    <p align="justify">Caulin,
      A. F., and Maley, C. C. (2011). &quot;Peto's Paradox: evolution's prescription
      for cancer prevention.&quot; <i>Trends Ecol Evol</i> <b>26</b>(4):175-182.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21296451">PubMed</a></p>

    <p align="justify">Cavazzana-Calvo,
      M., Hacein-Bey, S., de Saint Basile, G., Gross, F., Yvon, E., Nusbaum, P.,
      Selz, F., Hue, C., Certain, S., Casanova, J. L.<i>, et al.</i> (2000).
      &quot;Gene therapy of human severe combined immunodeficiency (SCID)-X1
      disease.&quot; <i>Science</i> <b>288</b>(5466):669-672. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10784449">PubMed</a>
    </p>

    <p align="justify">Cawthon,
      R. M., Smith, K. R., O'Brien, E., Sivatchenko, A., and Kerber, R. A. (2003).
      &quot;Association between telomere length in blood and mortality in people aged
      60 years or older.&quot; <i>Lancet</i> <b>361</b>(9355):393-395. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12573379">PubMed</a></p>

    <p align="justify">Celli,
      J., Duijf, P., Hamel, B. C., Bamshad, M., Kramer, B., Smits, A. P.,
      Newbury-Ecob, R., Hennekam, R. C., Van Buggenhout, G., van Haeringen, A.<i>, et
        al.</i> (1999). &quot;Heterozygous germline mutations in the p53 homolog p63
      are the cause of EEC syndrome.&quot; <i>Cell</i> <b>99</b>(2):143-153. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10535733">PubMed</a></p>

    <p align="justify">Cenci,
      G., Ciapponi, L., and Gatti, M. (2005). &quot;The mechanism of telomere
      protection: a comparison between Drosophila and humans.&quot; <i>Chromosoma</i>
      <b>114</b>(3):135-145. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16012858">PubMed</a></p>

    <p align="justify">Cevenini,
      E., Bellavista, E., Tieri, P., Castellani, G., Lescai, F., Francesconi, M.,
      Mishto, M., Santoro, A., Valensin, S., Salvioli, S.<i>, et al.</i> (2010).
      &quot;Systems biology and longevity: an emerging approach to identify
      innovative anti-aging targets and strategies.&quot; <i>Curr Pharm Des</i> <b>16</b>(7):802-813.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20388091">PubMed</a></p>

    <p align="justify">Chai,
      Y., and Slavkin, H. C. (2003). &quot;Prospects for tooth regeneration in the
      21st century: a perspective.&quot; <i>Microsc Res Tech</i> <b>60</b>(5):469-479.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12619122">PubMed</a></p>

    <p align="justify">Chan,
      A. W., Chong, K. Y., Martinovich, C., Simerly, C., and Schatten, G. (2001).
      &quot;Transgenic monkeys produced by retroviral gene transfer into mature
      oocytes.&quot; <i>Science</i> <b>291</b>(5502):309-312. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11209082">PubMed</a>
    </p>

    <p align="justify">Charlesworth,
      B. (1993). <i>Evolution in Age-Structured Populations</i>. Cambridge University
      Press, Cambridge.</p>

    <p align="justify">Charlesworth,
      B., and Hughes, K. A. (1996). &quot;Age-specific inbreeding depression and
      components of genetic variance in relation to the evolution of
      senescence.&quot; <i>Proc Natl Acad Sci U S A</i> <b>93</b>(12):6140-6145.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8650233">PubMed</a></p>

    <p align="justify">Charlesworth,
      B. (2000). &quot;Fisher, Medawar, Hamilton and the evolution of aging.&quot; <i>Genetics</i>
      <b>156</b>(3):927-931. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11063673">PubMed</a></p>

    <p align="justify">Charnov,
      E. L. (1993). <i>Life History Invariants: Some Explorations of Symmetry in
        Evolutionary Ecology</i>. Oxford University Press, Oxford.</p>

    <p align="justify">Chen,
      J. J., and Yu, B. P. (1994). &quot;Alterations in mitochondrial membrane
      fluidity by lipid peroxidation products.&quot; <i>Free Radical Biol Med</i> <b>17</b>(5):411-418.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7835747">PubMed</a></p>

    <p align="justify">Chen,
      Q., Fischer, A., Reagan, J. D., Yan, L. J., and Ames, B. N. (1995).
      &quot;Oxidative DNA damage and senescence of human diploid fibroblast
      cells.&quot; <i>Proc Natl Acad Sci U S A</i> <b>92</b>(10):4337-4341. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7753808">PubMed</a></p>

    <p align="justify">Chen,
      H. J., Liang, C. L., Lu, K., Lin, J. W., and Cho, C. L. (2000).
      &quot;Implication of telomerase activity and alternations of telomere length in
      the histologic characteristics of intracranial meningiomas.&quot; <i>Cancer</i>
      <b>89</b>(10):2092-2098. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11066050">PubMed</a></p>

    <p align="justify">Chen,
      L., Lee, L., Kudlow, B. A., Dos Santos, H. G., Sletvold, O., Shafeghati, Y.,
      Botha, E. G., Garg, A., Hanson, N. B., Martin, G. M.<i>, et al.</i> (2003).
      &quot;LMNA mutations in atypical Werner's syndrome.&quot; <i>Lancet</i> <b>362</b>(9382):440-445.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12927431">PubMed</a></p>

    <p align="justify">Chen,
      J., Lewis, E. E., Carey, J. R., Caswell, H., and Caswell-Chen, E. P. (2006).
      &quot;The ecology and biodemography of Caenorhabditis elegans.&quot; <i>Exp
        Gerontol</i> <b>41</b>(10):1059-1065. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16963216">PubMed</a></p>

    <p align="justify">Chen,
      D., Pan, K. Z., Palter, J. E., and Kapahi, P. (2007). &quot;Longevity
      determined by developmental arrest genes in Caenorhabditis elegans.&quot; <i>Aging
        Cell</i> <b>6</b>(4):525-533. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17521386">PubMed</a></p>

    <p align="justify">Chiu,
      C. P., Dragowska, W., Kim, N. W., Vaziri, H., Yui, J., Thomas, T. E., Harley,
      C. B., and Lansdorp, P. M. (1996). &quot;Differential expression of telomerase
      activity in hematopoietic progenitors from adult human bone marrow.&quot; <i>Stem
        Cells</i> <b>14</b>(2):239-248. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8991544">PubMed</a></p>

    <p align="justify">Ch'ng,
      Q., Sieburth, D., and Kaplan, J. M. (2008). &quot;Profiling synaptic proteins
      identifies regulators of insulin secretion and lifespan.&quot; <i>PLoS Genet</i>
      <b>4</b>(11):e1000283. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19043554">PubMed</a></p>

    <p align="justify">Choi,
      H. S., Lin, Z., Li, B. S., and Liu, A. Y. (1990). &quot;Age-dependent decrease
      in the heat-inducible DNA sequence-specific binding activity in human diploid
      fibroblasts.&quot; <i>J Biol Chem</i> <b>265</b>(29):18005-18011. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2211677">PubMed</a></p>

    <p align="justify">Choi,
      D., Whittier, P. S., Oshima, J., and Funk, W. D. (2001). &quot;Telomerase
      expression prevents replicative senescence but does not fully reset mRNA
      expression patterns in Werner syndrome cell strains.&quot; <i>Faseb J</i> <b>15</b>(6):1014-1020.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11292662">PubMed</a></p>

    <p align="justify">Cibelli,
      J. B., Stice, S. L., Golueke, P. J., Kane, J. J., Jerry, J., Blackwell, C.,
      Ponce de Leon, F. A., and Robl, J. M. (1998). &quot;Cloned transgenic calves
      produced from nonquiescent fetal fibroblasts.&quot; <i>Science</i> <b>280</b>(5367):1256-1258.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9596577">PubMed</a></p>

    <p align="justify">Cibelli,
      J. B., Lanza, R. P., West, M. D., and Ezzell, C. (2002). &quot;The first human
      cloned embryo.&quot; <i>Sci Am</i> <b>286</b>(1):44-51. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11799617">PubMed</a>
    </p>

    <p align="justify">Clancy,
      D. J., Gems, D., Harshman, L. G., Oldham, S., Stocker, H., Hafen, E., Leevers,
      S. J., and Partridge, L. (2001). &quot;Extension of life-span by loss of CHICO,
      a Drosophila insulin receptor substrate protein.&quot; <i>Science</i> <b>292</b>(5514):104-106.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11292874">PubMed</a></p>

    <p align="justify">Clark,
      A. M., and Rubin, M. A. (1961). &quot;The modification by x-irradiation of the
      life span of haploids and diploids of the wasp, Habrobracon sp.&quot; <i>Radiat
        Res</i> <b>15</b>:244-253. <a href="http://www.ncbi.nlm.nih.gov/pubmed/13693817">PubMed</a></p>

    <p align="justify">Clausen,
      A., Doctrow, S., and Baudry, M. (2010). &quot;Prevention of cognitive deficits
      and brain oxidative stress with superoxide dismutase/catalase mimetics in aged
      mice.&quot; <i>Neurobiol Aging</i> <b>31</b>(3):425-433. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18571288">PubMed</a>
    </p>

    <p align="justify">Codd,
      V., Nelson, C. P., Albrecht, E., Mangino, M., Deelen, J., Buxton, J. L.,
      Hottenga, J. J., Fischer, K., Esko, T., Surakka, I.<i>, et al.</i> (2013).
      &quot;Identification of seven loci affecting mean telomere length and their association
      with disease.&quot; <i>Nat Genet</i> <b>45</b>(4):422-427, 427e421-422.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23535734">PubMed</a></p>

    <p align="justify">Cohen,
      A. A. (2004). &quot;Female post-reproductive lifespan: a general mammalian
      trait.&quot; <i>Biol Rev Camb Philos Soc</i> <b>79</b>(4):733-750. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15682868">PubMed</a></p>

    <p align="justify">Cohen,
      H. Y., Miller, C., Bitterman, K. J., Wall, N. R., Hekking, B., Kessler, B.,
      Howitz, K. T., Gorospe, M., de Cabo, R., and Sinclair, D. A. (2004).
      &quot;Calorie restriction promotes mammalian cell survival by inducing the
      SIRT1 deacetylase.&quot; <i>Science</i> <b>305</b>(5682):390-392. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15205477">PubMed</a></p>

    <p align="justify">Cohen,
      E., Bieschke, J., Perciavalle, R. M., Kelly, J. W., and Dillin, A. (2006).
      &quot;Opposing activities protect against age-onset proteotoxicity.&quot; <i>Science</i>
      <b>313</b>(5793):1604-1610. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16902091">PubMed</a></p>

    <p align="justify">Coles,
      L. S. (2004). &quot;Demographics of human supercentenarians and the
      implications for longevity medicine.&quot; <i>Ann N Y Acad Sci</i> <b>1019</b>:490-495.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15247072">PubMed</a></p>

    <p align="justify">Coles,
      L. S., and Young, R. D. (2012). &quot;Supercentenarians and transthyretin
      amyloidosis: the next frontier of human life extension.&quot; <i>Prev Med</i> <b>54
        Suppl</b>:S9-11. <a href="http://www.ncbi.nlm.nih.gov/pubmed/22579241">PubMed</a></p>

    <p align="justify">Colgin,
      L. M., and Reddel, R. R. (1999). &quot;Telomere maintenance mechanisms and
      cellular immortalization.&quot; <i>Curr Opin Genet Dev</i> <b>9</b>(1):97-103.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10072358">PubMed</a></p>

    <p align="justify">Collins,
      K., and Mitchell, J. R. (2002). &quot;Telomerase in the human organism.&quot; <i>Oncogene</i>
      <b>21</b>(4):564-579. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11850781">PubMed</a></p>

    <p align="justify">Collins,
      F. S., Rossant, J., and Wurst, W. (2007). &quot;A mouse for all reasons.&quot; <i>Cell</i>
      <b>128</b>(1):9-13. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17218247">PubMed</a></p>

    <p align="justify">Colman,
      R. J., Anderson, R. M., Johnson, S. C., Kastman, E. K., Kosmatka, K. J.,
      Beasley, T. M., Allison, D. B., Cruzen, C., Simmons, H. A., Kemnitz, J. W.<i>,
        et al.</i> (2009). &quot;Caloric restriction delays disease onset and mortality
      in rhesus monkeys.&quot; <i>Science</i> <b>325</b>(5937):201-204. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19590001">PubMed</a></p>

    <p align="justify">Comfort,
      A. (1964). <i>Ageing: The Biology of Senescence</i>. Routledge &amp; Kegan
      Paul, London.</p>

    <p align="justify">Comfort,
      A. (1968). &quot;Feasibility in age research.&quot; <i>Nature</i> <b>217</b>(5126):320-322.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5639145">PubMed</a></p>

    <p align="justify">Comfort,
      A., Youhotsky-Gore, I., and Pathmanathan, K. (1971). &quot;Effect of ethoxyquin
      on the longevity of C3H mice.&quot; <i>Nature</i> <b>229</b>(5282):254-255.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4927196">PubMed</a></p>

    <p align="justify">Conboy,
      I. M., Conboy, M. J., Wagers, A. J., Girma, E. R., Weissman, I. L., and Rando,
      T. A. (2005). &quot;Rejuvenation of aged progenitor cells by exposure to a
      young systemic environment.&quot; <i>Nature</i> <b>433</b>(7027):760-764.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15716955">PubMed</a></p>

    <p align="justify">Congdon,
      J. D., Nagle, R. D., Kinney, O. M., and van Loben Sels, R. C. (2001).
      &quot;Hypotheses of aging in a long-lived vertebrate, Blanding's turtle
      (Emydoidea blandingii).&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):813-827.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295515">PubMed</a></p>

    <p align="justify">Congdon,
      J. D., Nagle, R. D., Kinney, O. M., van Loben Sels, R. C., Quinter, T., and
      Tinkle, D. W. (2003). &quot;Testing hypotheses of aging in long-lived painted
      turtles (Chrysemys picta).&quot; <i>Exp Gerontol</i> <b>38</b>(7):765-772.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12855285">PubMed</a></p>

    <p align="justify">Cook,
      R. E. (1983). &quot;Clonal plant populations.&quot; <i>Am Sci</i> <b>71</b>:244-253.
    </p>

    <p align="justify">Cooper,
      T. M., Mockett, R. J., Sohal, B. H., Sohal, R. S., and Orr, W. C. (2004).
      &quot;Effect of caloric restriction on life span of the housefly, Musca
      domestica.&quot; <i>Faseb J</i> <b>18</b>(13):1591-1593. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15319362">PubMed</a>
    </p>

    <p align="justify">Corral-Debrinski,
      M., Horton, T., Lott, M. T., Shoffner, J. M., Beal, M. F., and Wallace, D. C.
      (1992). &quot;Mitochondrial DNA deletions in human brain: regional variability
      and increase with advanced age.&quot; <i>Nat Genet</i> <b>2</b>(4):324-329.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1303288">PubMed</a></p>

    <p align="justify">Cortopassi,
      G. A., and Wang, E. (1996). &quot;There is substantial agreement among
      interspecies estimates of DNA repair activity.&quot; <i>Mech Ageing Dev</i> <b>91</b>(3):211-218.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9055244">PubMed</a></p>

    <p align="justify">Coschigano,
      K. T., Clemmons, D., Bellush, L. L., and Kopchick, J. J. (2000).
      &quot;Assessment of growth parameters and life span of GHR/BP gene-disrupted
      mice.&quot; <i>Endocrinology</i> <b>141</b>(7):2608-2613. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10875265">PubMed</a>
    </p>

    <p align="justify">Cosgrove,
      G. E., Selby, P. B., Upton, A. C., Mitchell, T. J., Steele, M. H., and Russell,
      W. L. (1993). &quot;Lifespan and autopsy findings in the first-generation
      offspring of X-irradiated male mice.&quot; <i>Mutat Res</i> <b>319</b>(1):71-79.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7690461">PubMed</a></p>

    <p align="justify">Counter,
      C. M., Avilion, A. A., LeFeuvre, C. E., Stewart, N. G., Greider, C. W., Harley,
      C. B., and Bacchetti, S. (1992). &quot;Telomere shortening associated with
      chromosome instability is arrested in immortal cells which express telomerase
      activity.&quot; <i>Embo J</i> <b>11</b>(5):1921-1929. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/1582420">PubMed</a></p>

    <p align="justify">Counter,
      C. M., Gupta, J., Harley, C. B., Leber, B., and Bacchetti, S. (1995).
      &quot;Telomerase activity in normal leukocytes and in hematologic
      malignancies.&quot; <i>Blood</i> <b>85</b>(9):2315-2320. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7727765">PubMed</a>
    </p>

    <p align="justify">Counter,
      C. M., Meyerson, M., Eaton, E. N., Ellisen, L. W., Caddle, S. D., Haber, D. A.,
      and Weinberg, R. A. (1998). &quot;Telomerase activity is restored in human
      cells by ectopic expression of hTERT (hEST2), the catalytic subunit of
      telomerase.&quot; <i>Oncogene</i> <b>16</b>(9):1217-1222. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9528864">PubMed</a>
    </p>

    <p align="justify">Craik,
      F. I. M., and Salthouse, T. A. (1992). <i>Handbook of Aging and Cognition</i>.
      Erlbaum, Hillsdale.</p>

    <p align="justify">Cristofalo,
      V. J., and Sharf, B. B. (1973). &quot;Cellular senescence and DNA synthesis.
      Thymidine incorporation as a measure of population age in human diploid
      cells.&quot; <i>Exp Cell Res</i> <b>76</b>(2):419-427. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/4568162">PubMed</a></p>

    <p align="justify">Cristofalo,
      V. J., and Pignolo, R. J. (1993). &quot;Replicative senescence of human
      fibroblast-like cells in culture.&quot; <i>Physiol Rev</i> <b>73</b>(3):617-638.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8332640">PubMed</a></p>

    <p align="justify">Cristofalo,
      V. J., Volker, C., Francis, M. K., and Tresini, M. (1998a). &quot;Age-dependent
      modifications of gene expression in human fibroblasts.&quot; <i>Crit Rev
        Eukaryot Gene Expr</i> <b>8</b>(1):43-80. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9673450">PubMed</a></p>

    <p align="justify">Cristofalo,
      V. J., Allen, R. G., Pignolo, R. J., Martin, B. G., and Beck, J. C. (1998b).
      &quot;Relationship between donor age and the replicative lifespan of human
      cells in culture: a reevaluation.&quot; <i>Proc Natl Acad Sci U S A</i> <b>95</b>(18):10614-10619.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9724752">PubMed</a></p>

    <p align="justify">Cristofalo,
      V. (2001). &quot;&quot;I no longer believe that cell death is programmed.
      ..&quot;, an interview with Vincent Cristofalo.&quot; <i>Biogerontology</i> <b>2</b>(4):283-290.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11868903">PubMed</a></p>

    <p align="justify">Cuervo,
      A. M., Bergamini, E., Brunk, U. T., Droge, W., Ffrench, M., and Terman, A.
      (2005). &quot;Autophagy and aging: the importance of maintaining
      &quot;clean&quot; cells.&quot; <i>Autophagy</i> <b>1</b>(3):131-140. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16874025">PubMed</a></p>

    <p align="justify">Cutler,
      R. G. (1975). &quot;Evolution of human longevity and the genetic complexity
      governing aging rate.&quot; <i>Proc Natl Acad Sci U S A</i> <b>72</b>(11):4664-4668.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/812099">PubMed</a></p>

    <p align="justify">Cutler,
      R. G. (1979). &quot;Evolution of human longevity: a critical overview.&quot; <i>Mech
        Ageing Dev</i> <b>9</b>(3-4):337-354. <a href="http://www.ncbi.nlm.nih.gov/pubmed/374900">PubMed</a></p>

    <p align="justify">Cutler,
      R. G. (1985). &quot;Antioxidants and longevity of mammalian species.&quot; <i>Basic
        Life Sci</i> <b>35</b>:15-73. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3904717">PubMed</a></p>

    <p align="justify">Czupryn,
      A., Zhou, Y. D., Chen, X., McNay, D., Anderson, M. P., Flier, J. S., and
      Macklis, J. D. (2011). &quot;Transplanted hypothalamic neurons restore leptin
      signaling and ameliorate obesity in db/db mice.&quot; <i>Science</i> <b>334</b>(6059):1133-1137.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22116886">PubMed</a></p>

    <p align="justify">Das,
      M., Gabriely, I., and Barzilai, N. (2004). &quot;Caloric restriction, body fat
      and ageing in experimental models.&quot; <i>Obes Rev</i> <b>5</b>(1):13-19.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14969503">PubMed</a></p>

    <p align="justify">Davaapil,
      H., Brockes, J. P., and Yun, M. H. (2017). &quot;Conserved and novel functions
      of programmed cellular senescence during vertebrate development.&quot; <i>Development</i>
      <b>144</b>(1):106-114. <a href="http://www.ncbi.nlm.nih.gov/pubmed/27888193">PubMed</a></p>

    <p align="justify">Davenport,
      R. J. (2003). &quot;Power to the people.&quot; <i>Sci Aging Knowledge Environ</i>
      <b>2003</b>(50):NS8. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14681575">PubMed</a></p>

    <p align="justify">Davidson,
      B. L., and McCray, P. B. (2011). &quot;Current prospects for RNA
      interference-based therapies.&quot; <i>Nat Rev Genet</i> <b>12</b>(5):329-340.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21499294">PubMed</a></p>

    <p align="justify">Davis,
      J. K. (2004). &quot;Collective suttee: is it unjust to develop life extension
      if it will not be possible to provide it to everyone?&quot; <i>Ann N Y Acad Sci</i>
      <b>1019</b>:535-541. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15247081">PubMed</a></p>

    <p align="justify">Davis,
      J. K. (2005). &quot;Life-extension and the Malthusian Objection.&quot; <i>J Med
        Philos</i> <b>30</b>(1):27-44. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15814366">PubMed</a></p>

    <p align="justify">de
      Boer, J., Andressoo, J. O., de Wit, J., Huijmans, J., Beems, R. B., van Steeg,
      H., Weeda, G., van der Horst, G. T., van Leeuwen, W., Themmen, A. P.<i>, et al.</i>
      (2002). &quot;Premature aging in mice deficient in DNA repair and
      transcription.&quot; <i>Science</i> <b>296</b>(5571):1276-1279. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11950998">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (1997). &quot;A proposed refinement of the mitochondrial free
      radical theory of aging.&quot; <i>Bioessays</i> <b>19</b>(2):161-166. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9046246">PubMed</a></p>

    <p align="justify">de
      Grey, A. D., Ames, B. N., Andersen, J. K., Bartke, A., Campisi, J., Heward, C.
      B., McCarter, R. J., and Stock, G. (2002a). &quot;Time to talk SENS: critiquing
      the immutability of human aging.&quot; <i>Ann N Y Acad Sci</i> <b>959</b>:452-462;
      discussion 463-455. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11976218">PubMed</a></p>

    <p align="justify">de
      Grey, A. D., Baynes, J. W., Berd, D., Heward, C. B., Pawelec, G., and Stock, G.
      (2002b). &quot;Is human aging still mysterious enough to be left only to
      scientists?&quot; <i>Bioessays</i> <b>24</b>(7):667-676. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12111727">PubMed</a>
    </p>

    <p align="justify">de
      Grey, A. D. (2003). &quot;An engineer's approach to the development of real
      anti-aging medicine.&quot; <i>Sci Aging Knowledge Environ</i> <b>2003</b>(1):VP1.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12844502">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (2005a). &quot;The unfortunate influence of the weather on the rate
      of ageing: why human caloric restriction or its emulation may only extend life
      expectancy by 2-3 years.&quot; <i>Gerontology</i> <b>51</b>(2):73-82.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15711074">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (2005b). &quot;Whole-body interdiction of lengthening of telomeres:
      a proposal for cancer prevention.&quot; <i>Front Biosci</i> <b>10</b>:2420-2429.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15970505">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (2005c). &quot;Like it or not, life-extension research extends
      beyond biogerontology.&quot; <i>EMBO Rep</i> <b>6</b>(11):1000. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16264420">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (2005d). &quot;Resistance to debate on how to postpone ageing is
      delaying progress and costing lives. Open discussions in the biogerontology
      community would attract public interest and influence funding policy.&quot; <i>EMBO
        Rep</i> <b>6 Spec No</b>:S49-53. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15995663">PubMed</a></p>

    <p align="justify">de
      Grey, A. D. (2006). &quot;Is SENS a farrago?&quot; <i>Rejuvenation Res</i> <b>9</b>(4):436-439.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17105381">PubMed</a></p>

    <p align="justify">de
      Grey, A., and Rae, M. (2008). <i>Ending Aging: The Rejuvenation Breakthroughs
        that Could Reverse Human Aging in Our Lifetime</i>. St. Martin's Press, New
      York.</p>

    <p align="justify">de
      Haan, G., and Van Zant, G. (1999). &quot;Genetic analysis of hemopoietic cell cycling
      in mice suggests its involvement in organismal life span.&quot; <i>Faseb J</i> <b>13</b>(6):707-713.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10094931">PubMed</a></p>

    <p align="justify">de
      Jesus, B. B., Schneeberger, K., Vera, E., Tejera, A., Harley, C. B., and
      Blasco, M. A. (2011). &quot;The telomerase activator TA-65 elongates short
      telomeres and increases health span of adult/old mice without increasing cancer
      incidence.&quot; <i>Aging Cell</i> <b>10</b>(4):604-621. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21426483">PubMed</a>
    </p>

    <p align="justify">De
      Luca, G., Ventura, I., Sanghez, V., Russo, M. T., Ajmone-Cat, M. A., Cacci, E.,
      Martire, A., Popoli, P., Falcone, G., Michelini, F.<i>, et al.</i> (2013).
      &quot;Prolonged lifespan with enhanced exploratory behavior in mice
      overexpressing the oxidized nucleoside triphosphatase hMTH1.&quot; <i>Aging
        Cell</i> <b>12</b>(4):695-705. <a href="http://www.ncbi.nlm.nih.gov/pubmed/23648059">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Toussaint, O. (2002). &quot;The evolution of mammalian
      aging.&quot; <i>Exp Gerontol</i> <b>37</b>(6):769-775. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12175477">PubMed</a>
    </p>

    <p align="justify">de
      Magalhaes, J. P. (2003). &quot;Is mammalian aging genetically controlled?&quot;
      <i>Biogerontology</i> <b>4</b>(2):119-120. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12766537">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2004). &quot;From cells to ageing: a review of models and
      mechanisms of cellular senescence and their impact on human ageing.&quot; <i>Exp
        Cell Res</i> <b>300</b>(1):1-10. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15383309">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Chainiaux, F., de Longueville, F., Mainfroid, V., Migeot, V.,
      Marcq, L., Remacle, J., Salmon, M., and Toussaint, O. (2004). &quot;Gene
      expression and regulation in H2O2-induced premature senescence of human
      foreskin fibroblasts expressing or not telomerase.&quot; <i>Exp Gerontol</i> <b>39</b>(9):1379-1389.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15489061">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Toussaint, O. (2004a). &quot;Telomeres and telomerase: a
      modern fountain of youth?&quot; <i>Rejuvenation Res</i> <b>7</b>(2):126-133.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15312299">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Toussaint, O. (2004b). &quot;How bioinformatics can help
      reverse engineer human aging.&quot; <i>Ageing Res Rev</i> <b>3</b>(2):125-141.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15177050">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2005a). &quot;Open-minded scepticism: inferring the causal
      mechanisms of human ageing from genetic perturbations.&quot; <i>Ageing Res Rev</i>
      <b>4</b>(1):1-22. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15619467">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2005b). &quot;Human disease-associated mitochondrial mutations
      fixed in nonhuman primates.&quot; <i>J Mol Evol</i> <b>61</b>(4):491-497.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16132471">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Cabral, J. A., and Magalhaes, D. (2005). &quot;The influence
      of genes on the aging process of mice: a statistical assessment of the genetics
      of aging.&quot; <i>Genetics</i> <b>169</b>(1):265-274. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15466429">PubMed</a>
    </p>

    <p align="justify">de
      Magalhaes, J. P., and Church, G. M. (2005). &quot;Genomes optimize
      reproduction: aging as a consequence of the developmental program.&quot; <i>Physiology
        (Bethesda)</i> <b>20</b>:252-259. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16024513">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Sandberg, A. (2005). &quot;Cognitive aging as an
      extension of brain development: A model linking learning, brain plasticity, and
      neurodegeneration.&quot; <i>Mech Ageing Dev</i> <b>126</b>(10):1026-1033.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15946728">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2006). &quot;Species selection in comparative studies of
      aging and antiaging research.&quot; In: <i>Handbook of Models for Human Aging</i>,
      Conn, P. M. (ed.).  Elsevier Academic Press, Burlington, MA, 9-20.</p>

    <p align="justify">de
      Magalhaes, J. P., and Church, G. M. (2006). &quot;Cells discover fire:
      employing reactive oxygen species in development and consequences for
      aging.&quot; <i>Exp Gerontol</i> <b>41</b>(1):1-10. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16226003">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Church, G. M. (2007). &quot;Analyses of human-chimpanzee
      orthologous gene pairs to explore evolutionary hypotheses of aging.&quot; <i>Mech
        Ageing Dev</i> <b>128</b>(5-6):355-364. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17459455">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Costa, J., and Church, G. M. (2007a). &quot;An analysis of the
      relationship between metabolism, developmental schedules, and longevity using
      phylogenetic independent contrasts.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>62</b>(2):149-160.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17339640">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Sedivy, J. M., Finch, C. E., Austad, S. N., and Church, G. M.
      (2007b). &quot;A proposal to sequence genomes of unique interest for research on
      aging.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>62</b>(6):583-584.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17595413">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Faragher, R. G. (2008). &quot;Cell divisions and
      mammalian aging: integrative biology insights from genes that regulate
      longevity.&quot; <i>Bioessays</i> <b>30</b>(6):567-578. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18478536">PubMed</a>
    </p>

    <p align="justify">de
      Magalhaes, J. P. (2009). &quot;Aging Research in the Post-Genome Era: New
      Technologies for an Old Problem.&quot; In: <i>Redox Metabolism and Longevity
        Relationships in Animals and Plants</i>, Foyer, C. H., Faragher, R. and
      Thornalley, P. J. (ed.).  Taylor and Francis, New York and Abingdon, 99-115.</p>

    <p align="justify">de
      Magalhaes, J. P., and Costa, J. (2009). &quot;A database of vertebrate
      longevity records and their relation to other life-history traits.&quot; <i>J
        Evol Biol</i> <b>22</b>(8):1770-1774. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19522730">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Budovsky, A., Lehmann, G., Costa, J., Li, Y., Fraifeld, V.,
      and Church, G. M. (2009a). &quot;The Human Ageing Genomic Resources: online
      databases and tools for biogerontologists.&quot; <i>Aging Cell</i> <b>8</b>(1):65-72.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18986374">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Curado, J., and Church, G. M. (2009b). &quot;Meta-analysis of
      age-related gene expression profiles identifies common signatures of
      aging.&quot; <i>Bioinformatics</i> <b>25</b>(7):875-881. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19189975">PubMed</a>
    </p>

    <p align="justify">de
      Magalhaes, J. P., Finch, C. E., and Janssens, G. (2010). &quot;Next-generation
      sequencing in aging research: emerging applications, problems, pitfalls and
      possible solutions.&quot; <i>Ageing Res Rev</i> <b>9</b>(3):315-323. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19900591">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2011). &quot;The biology of ageing: A primer.&quot; In: <i>An
        Introduction to Gerontology</i>, Stuart-Hamilton, I. (ed.).  Cambridge
      University Press, Cambridge, 21-47.</p>

    <p align="justify">de
      Magalhaes, J. P. (2012). &quot;Programmatic features of aging originating in
      development: aging mechanisms beyond molecular damage?&quot; <i>Faseb J</i> <b>26</b>(12):4821-4826.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22964300">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Wuttke, D., Wood, S. H., Plank, M., and Vora, C. (2012).
      &quot;Genome-environment interactions that modulate aging: powerful targets for
      drug discovery.&quot; <i>Pharmacol Rev</i> <b>64</b>(1):88-101. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/22090473">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2013). &quot;How ageing processes influence cancer.&quot; <i>Nat
        Rev Cancer</i> <b>13</b>(5):357-365. <a href="http://www.ncbi.nlm.nih.gov/pubmed/23612461">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2014a). &quot;The scientific quest for lasting youth:
      prospects for curing aging.&quot; <i>Rejuvenation Res</i> <b>17</b>(5):458-467.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/25132068">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P. (2014b). &quot;Why genes extending lifespan in model organisms
      have not been consistently associated with human longevity and what it means to
      translation research.&quot; <i>Cell Cycle</i> <b>13</b>(17):2671-2673.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/25486354">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., Stevens, M., and Thornton, D. (2017). &quot;The Business of
      Anti-Aging Science.&quot; <i>Trends Biotechnol</i> <b>35</b>(11):1062-1073.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/28778607">PubMed</a></p>

    <p align="justify">de
      Magalhaes, J. P., and Passos, J. F. (2018). &quot;Stress, cell senescence and
      organismal ageing.&quot; <i>Mech Ageing Dev</i> <b>170</b>:2-9. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/28688962">PubMed</a></p>

    <p align="justify">Decary,
      S., Mouly, V., Hamida, C. B., Sautet, A., Barbet, J. P., and Butler-Browne, G.
      S. (1997). &quot;Replicative potential and telomere length in human skeletal
      muscle: implications for satellite cell-mediated gene therapy.&quot; <i>Hum
        Gene Ther</i> <b>8</b>(12):1429-1438. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9287143">PubMed</a></p>

    <p align="justify">Dekker,
      P., Maier, A. B., van Heemst, D., de Koning-Treurniet, C., Blom, J., Dirks, R.
      W., Tanke, H. J., and Westendorp, R. G. (2009). &quot;Stress-induced responses
      of human skin fibroblasts in vitro reflect human longevity.&quot; <i>Aging Cell</i>
      <b>8</b>(5):595-603. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19681808">PubMed</a></p>

    <p align="justify">Demaria,
      M., Ohtani, N., Youssef, S. A., Rodier, F., Toussaint, W., Mitchell, J. R.,
      Laberge, R. M., Vijg, J., Van Steeg, H., Dolle, M. E.<i>, et al.</i> (2014).
      &quot;An Essential Role for Senescent Cells in Optimal Wound Healing through
      Secretion of PDGF-AA.&quot; <i>Dev Cell</i> <b>31</b>(6):722-733. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/25499914">PubMed</a></p>

    <p align="justify">Demontis,
      F., and Perrimon, N. (2010). &quot;FOXO/4E-BP signaling in Drosophila muscles
      regulates organism-wide proteostasis during aging.&quot; <i>Cell</i> <b>143</b>(5):813-825.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21111239">PubMed</a></p>

    <p align="justify">Deng,
      Y., Chan, S. S., and Chang, S. (2008). &quot;Telomere dysfunction and tumour
      suppression: the senescence connection.&quot; <i>Nat Rev Cancer</i> <b>8</b>(6):450-458.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18500246">PubMed</a></p>

    <p align="justify">Di
      Mitri, D., Azevedo, R. I., Henson, S. M., Libri, V., Riddell, N. E., Macaulay,
      R., Kipling, D., Soares, M. V., Battistini, L., and Akbar, A. N. (2011).
      &quot;Reversible senescence in human CD4+CD45RA+CD27- memory T cells.&quot; <i>J
        Immunol</i> <b>187</b>(5):2093-2100. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21788446">PubMed</a></p>

    <p align="justify">Dierick,
      Frippiat, Salmon, Chainiaux, and Toussaint (2003). &quot;Cells, stress and
      tissue ageing.&quot; In: <i>Biology of Aging and its Modulation</i>, Rattan, S.
      I. S. (ed.).  Kluwer, Amsterdam, 101-125.</p>

    <p align="justify">Dillin,
      A., Crawford, D. K., and Kenyon, C. (2002). &quot;Timing requirements for
      insulin/IGF-1 signaling in C. elegans.&quot; <i>Science</i> <b>298</b>(5594):830-834.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12399591">PubMed</a></p>

    <p align="justify">DiMasi,
      J. A., Feldman, L., Seckler, A., and Wilson, A. (2010). &quot;Trends in risks
      associated with new drug development: success rates for investigational
      drugs.&quot; <i>Clin Pharmacol Ther</i> <b>87</b>(3):272-277. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/20130567">PubMed</a></p>

    <p align="justify">DiMauro,
      S., Tanji, K., Bonilla, E., Pallotti, F., and Schon, E. A. (2002).
      &quot;Mitochondrial abnormalities in muscle and other aging cells:
      classification, causes, and effects.&quot; <i>Muscle Nerve</i> <b>26</b>(5):597-607.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12402281">PubMed</a></p>

    <p align="justify">DiMauro,
      S., and Schon, E. A. (2003). &quot;Mitochondrial respiratory-chain diseases.&quot;
      <i>N Engl J Med</i> <b>348</b>(26):2656-2668. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12826641">PubMed</a></p>

    <p align="justify">Dimri,
      G. P., Lee, X., Basile, G., Acosta, M., Scott, G., Roskelley, C., Medrano, E.
      E., Linskens, M., Rubelj, I., Pereira-Smith, O.<i>, et al.</i> (1995). &quot;A
      biomarker that identifies senescent human cells in culture and in aging skin in
      vivo.&quot; <i>Proc Natl Acad Sci U S A</i> <b>92</b>(20):9363-9367. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7568133">PubMed</a></p>

    <p align="justify">Dirks,
      A. J., and Leeuwenburgh, C. (2006). &quot;Caloric restriction in humans:
      potential pitfalls and health concerns.&quot; <i>Mech Ageing Dev</i> <b>127</b>(1):1-7.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16226298">PubMed</a></p>

    <p align="justify">Dolle,
      M. E., Giese, H., Hopkins, C. L., Martus, H. J., Hausdorff, J. M., and Vijg, J.
      (1997). &quot;Rapid accumulation of genome rearrangements in liver but not in
      brain of old mice.&quot; <i>Nat Genet</i> <b>17</b>(4):431-434. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9398844">PubMed</a></p>

    <p align="justify">Dolle,
      M. E., and Vijg, J. (2002). &quot;Genome dynamics in aging mice.&quot; <i>Genome
        Res</i> <b>12</b>(11):1732-1738. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12421760">PubMed</a></p>

    <p align="justify">Dominguez,
      L. J., Barbagallo, M., and Morley, J. E. (2009). &quot;Anti-aging medicine:
      pitfalls and hopes.&quot; <i>Aging Male</i> <b>12</b>(1):13-20. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19326292">PubMed</a></p>

    <p align="justify">Donehower,
      L. A., Harvey, M., Slagle, B. L., McArthur, M. J., Montgomery, C. A., Jr.,
      Butel, J. S., and Bradley, A. (1992). &quot;Mice deficient for p53 are
      developmentally normal but susceptible to spontaneous tumours.&quot; <i>Nature</i>
      <b>356</b>(6366):215-221. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1552940">PubMed</a></p>

    <p align="justify">Donehower,
      L. A. (2002). &quot;Does p53 affect organismal aging?&quot; <i>J Cell Physiol</i>
      <b>192</b>(1):23-33. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12115733">PubMed</a></p>

    <p align="justify">Drexler,
      E. K. (1986). <i>Engines of Creation</i>. Anchor Press, Garden City.</p>

    <p align="justify">Ducray,
      C., Pommier, J. P., Martins, L., Boussin, F. D., and Sabatier, L. (1999).
      &quot;Telomere dynamics, end-to-end fusions and telomerase activation during
      the human fibroblast immortalization process.&quot; <i>Oncogene</i> <b>18</b>(29):4211-4223.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10435634">PubMed</a></p>

    <p align="justify">Duffy,
      P. H., Feuers, R., Nakamura, K. D., Leakey, J., and Hart, R. W. (1990).
      &quot;Effect of chronic caloric restriction on the synchronization of various
      physiological measures in old female Fischer 344 rats.&quot; <i>Chronobiol Int</i>
      <b>7</b>(2):113-124. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2242505">PubMed</a></p>

    <p align="justify">Dulic,
      V., Beney, G. E., Frebourg, G., Drullinger, L. F., and Stein, G. H. (2000).
      &quot;Uncoupling between phenotypic senescence and cell cycle arrest in aging
      p21-deficient fibroblasts.&quot; <i>Mol Cell Biol</i> <b>20</b>(18):6741-6754.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10958672">PubMed</a></p>

    <p align="justify">Dumont,
      P., Burton, M., Chen, Q. M., Gonos, E. S., Frippiat, C., Mazarati, J. B.,
      Eliaers, F., Remacle, J., and Toussaint, O. (2000a). &quot;Induction of
      replicative senescence biomarkers by sublethal oxidative stresses in normal
      human fibroblast.&quot; <i>Free Radical Biol Med</i> <b>28</b>(3):361-373.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10699747">PubMed</a></p>

    <p align="justify">Dumont,
      P., Balbeur, L., Remacle, J., and Toussaint, O. (2000b). &quot;Appearance of
      biomarkers of in vitro ageing after successive stimulation of WI-38 fibroblasts
      with IL-1alpha and TNF-alpha: senescence associated beta-galactosidase activity
      and morphotype transition.&quot; <i>J Anat</i> <b>197 Pt 4</b>:529-537.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11197525">PubMed</a></p>

    <p align="justify">Dunham,
      M. A., Neumann, A. A., Fasching, C. L., and Reddel, R. R. (2000).
      &quot;Telomere maintenance by recombination in human cells.&quot; <i>Nat Genet</i>
      <b>26</b>(4):447-450. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11101843">PubMed</a></p>

    <p align="justify">Durieux,
      J., Wolff, S., and Dillin, A. (2011). &quot;The cell-non-autonomous nature of
      electron transport chain-mediated longevity.&quot; <i>Cell</i> <b>144</b>(1):79-91.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21215371">PubMed</a></p>

    <p align="justify">Dyson,
      N., Howley, P. M., Munger, K., and Harlow, E. (1989). &quot;The human papilloma
      virus-16 E7 oncoprotein is able to bind to the retinoblastoma gene
      product.&quot; <i>Science</i> <b>243</b>(4893):934-937. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2537532">PubMed</a>
    </p>

    <p align="justify">Ebert,
      T. A., and Southon, J. R. (2003). &quot; Red sea urchins (Strongylocentrotus
      franciscanus) can live over 100 years: confirmation with A-bomb 14carbon.&quot;
      <i>Fish Bull</i> <b>101</b>(4):915-922. </p>

    <p align="justify">Edamatsu,
      R., Mori, A., and Packer, L. (1995). &quot;The spin-trap
      N-tert-alpha-phenyl-butylnitrone prolongs the life span of the senescence
      accelerated mouse.&quot; <i>Biochem Biophys Res Commun</i> <b>211</b>(3):847-849.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7598714">PubMed</a></p>

    <p align="justify">Effros,
      R. B. (1996). &quot;Insights on immunological aging derived from the T
      lymphocyte cellular senescence model.&quot; <i>Exp Gerontol</i> <b>31</b>(1-2):21-27.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8706791">PubMed</a></p>

    <p align="justify">Effros,
      R. B. (2009). &quot;Kleemeier Award Lecture 2008--the canary in the coal mine:
      telomeres and human healthspan.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>64</b>(5):511-515.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19228779">PubMed</a></p>

    <p align="justify">Epel,
      E. S., Blackburn, E. H., Lin, J., Dhabhar, F. S., Adler, N. E., Morrow, J. D.,
      and Cawthon, R. M. (2004). &quot;Accelerated telomere shortening in response to
      life stress.&quot; <i>Proc Natl Acad Sci U S A</i> <b>101</b>(49):17312-17315.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15574496">PubMed</a></p>

    <p align="justify">Eriksson,
      P. S., Perfilieva, E., Bjork-Eriksson, T., Alborn, A. M., Nordborg, C.,
      Peterson, D. A., and Gage, F. H. (1998). &quot;Neurogenesis in the adult human
      hippocampus.&quot; <i>Nat Med</i> <b>4</b>(11):1313-1317. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9809557">PubMed</a>
    </p>

    <p align="justify">Eriksson,
      M., Brown, W. T., Gordon, L. B., Glynn, M. W., Singer, J., Scott, L., Erdos, M.
      R., Robbins, C. M., Moses, T. Y., Berglund, P.<i>, et al.</i> (2003).
      &quot;Recurrent de novo point mutations in lamin A cause Hutchinson-Gilford
      progeria syndrome.&quot; <i>Nature</i> <b>423</b>(6937):293-298. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12714972">PubMed</a></p>

    <p align="justify">Esposito,
      D., Fassina, G., Szabo, P., De Angelis, P., Rodgers, L., Weksler, M., and
      Siniscalco, M. (1989). &quot;Chromosomes of older humans are more prone to
      aminopterine-induced breakage.&quot; <i>Proc Natl Acad Sci U S A</i> <b>86</b>(4):1302-1306.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2919179">PubMed</a></p>

    <p align="justify">Failla,
      G. (1958). &quot;The aging process and cancerogenesis.&quot; <i>Ann N Y Acad
        Sci</i> <b>71</b>(6):1124-1140. <a href="http://www.ncbi.nlm.nih.gov/pubmed/13583876">PubMed</a></p>

    <p align="justify">Farrelly,
      C. (2010). &quot;Why aging research? The moral imperative to retard human
      aging.&quot; <i>Ann N Y Acad Sci</i> <b>1197</b>:1-8. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/20536826">PubMed</a></p>

    <p align="justify">Fearon,
      E. R. (1997). &quot;Human cancer syndromes: clues to the origin and nature of
      cancer.&quot; <i>Science</i> <b>278</b>(5340):1043-1050. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9353177">PubMed</a>
    </p>

    <p align="justify">Feng,
      J., Funk, W. D., Wang, S. S., Weinrich, S. L., Avilion, A. A., Chiu, C. P.,
      Adams, R. R., Chang, E., Allsopp, R. C., Yu, J.<i>, et al.</i> (1995).
      &quot;The RNA component of human telomerase.&quot; <i>Science</i> <b>269</b>(5228):1236-1241.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7544491">PubMed</a></p>

    <p align="justify">Ferguson,
      B. A., Dreisbach, T. A., Parks, C. G., Filip, G. M., and Schmitt, C. L. (2003).
      &quot;Coarse-scale population structure of pathogenic Armillaria species in a
      mixed-conifer forest in the Blue Mountains of northeast Oregon.&quot; <i>Can J
        For Res</i> <b>33</b>(4):612-623. </p>

    <p align="justify">Finch,
      C. E. (1976). &quot;The regulation of physiological changes during mammalian
      aging.&quot; <i>Q Rev Biol</i> <b>51</b>(1):49-83. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6991">PubMed</a>
    </p>

    <p align="justify">Finch,
      C. E. (1990). <i>Longevity, Senescence, and the Genome</i>. The University of
      Chicago Press, Chicago and London.</p>

    <p align="justify">Finch,
      C. E., and Pike, M. C. (1996). &quot;Maximum life span predictions from the
      Gompertz mortality model.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>51</b>(3):B183-194.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8630694">PubMed</a></p>

    <p align="justify">Fleming,
      J. E., Reveillaud, I., and Niedzwiecki, A. (1992). &quot;Role of oxidative
      stress in Drosophila aging.&quot; <i>Mutat Res</i> <b>275</b>(3-6):267-279.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1383769">PubMed</a></p>

    <p align="justify">Flurkey,
      K., Papaconstantinou, J., Miller, R. A., and Harrison, D. E. (2001).
      &quot;Lifespan extension and delayed immune and collagen aging in mutant mice
      with defects in growth hormone production.&quot; <i>Proc Natl Acad Sci U S A</i>
      <b>98</b>(12):6736-6741. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11371619">PubMed</a></p>

    <p align="justify">Font,
      E., Desfilis, E., Perez-Canellas, M. M., and Garcia-Verdugo, J. M. (2001).
      &quot;Neurogenesis and neuronal regeneration in the adult reptilian
      brain.&quot; <i>Brain Behav Evol</i> <b>58</b>(5):276-295. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11978946">PubMed</a>
    </p>

    <p align="justify">Fontana,
      L., Meyer, T. E., Klein, S., and Holloszy, J. O. (2004). &quot;Long-term
      calorie restriction is highly effective in reducing the risk for
      atherosclerosis in humans.&quot; <i>Proc Natl Acad Sci U S A</i> <b>101</b>(17):6659-6663.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15096581">PubMed</a></p>

    <p align="justify">Fontana,
      L., and Klein, S. (2007). &quot;Aging, adiposity, and calorie restriction.&quot;
      <i>Jama</i> <b>297</b>(9):986-994. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17341713">PubMed</a></p>

    <p align="justify">Fontana,
      L., Partridge, L., and Longo, V. D. (2010). &quot;Extending healthy life
      span--from yeast to humans.&quot; <i>Science</i> <b>328</b>(5976):321-326.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20395504">PubMed</a></p>

    <p align="justify">Forster,
      M. J., Morris, P., and Sohal, R. S. (2003). &quot;Genotype and age influence
      the effect of caloric intake on mortality in mice.&quot; <i>Faseb J</i> <b>17</b>(6):690-692.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12586746">PubMed</a></p>

    <p align="justify">Fossel,
      M. (1996). <i>Reversing Human Aging</i>. William Morrow and Company, New York.</p>

    <p align="justify">Franceschi,
      C., Mondello, C., Bonafe, M., Valensin, S., Sansoni, P., and Sorbi, S. (1999).
      &quot;Long telomeres and well preserved proliferative vigor in cells from
      centenarians: a contribution to longevity?&quot; <i>Aging (Milano)</i> <b>11</b>(2):69-72.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10386165">PubMed</a></p>

    <p align="justify">Franceschi,
      C., Bonafe, M., Valensin, S., Olivieri, F., De Luca, M., Ottaviani, E., and De
      Benedictis, G. (2000). &quot;Inflamm-aging. An evolutionary perspective on
      immunosenescence.&quot; <i>Ann N Y Acad Sci</i> <b>908</b>:244-254. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10911963">PubMed</a></p>

    <p align="justify">Freitas,
      R. (1999). <i>Nanomedicine, Volume I: Basic Capabilities</i>. Landes
      Bioscience, Georgetown.</p>

    <p align="justify">Freitas,
      R. (2003). <i>Nanomedicine, Volume IIA: Biocompatibility</i>. Landes
      Bioscience, Georgetown.</p>

    <p align="justify">Freitas,
      A. A., and de Magalhaes, J. P. (2011). &quot;A review and appraisal of the DNA
      damage theory of ageing.&quot; <i>Mutat Res</i> <b>728</b>(1-2):12-22.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21600302">PubMed</a></p>

    <p align="justify">Freitas,
      A. A., Vasieva, O., and de Magalhaes, J. P. (2011). &quot;A data mining
      approach for classifying DNA repair genes into ageing-related or
      non-ageing-related.&quot; <i>BMC Genomics</i> <b>12</b>:27. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21226956">PubMed</a>
    </p>

    <p align="justify">Freund,
      A., Orjalo, A. V., Desprez, P. Y., and Campisi, J. (2010). &quot;Inflammatory
      networks during cellular senescence: causes and consequences.&quot; <i>Trends
        Mol Med</i> <b>16</b>(5):238-246. <a href="http://www.ncbi.nlm.nih.gov/pubmed/20444648">PubMed</a></p>

    <p align="justify">Friguet,
      B., Bulteau, A. L., Chondrogianni, N., Conconi, M., and Petropoulos, I. (2000).
      &quot;Protein degradation by the proteasome and its implications in
      aging.&quot; <i>Ann N Y Acad Sci</i> <b>908</b>:143-154. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911955">PubMed</a>
    </p>

    <p align="justify">Froy,
      O., and Miskin, R. (2007). &quot;The interrelations among feeding, circadian
      rhythms and ageing.&quot; <i>Prog Neurobiol</i> <b>82</b>(3):142-150.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17482337">PubMed</a></p>

    <p align="justify">Fry,
      M. (2002). &quot;The Werner syndrome helicase-nuclease--one protein, many
      mysteries.&quot; <i>Sci Aging Knowledge Environ</i> <b>2002</b>(13):re2.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14602980">PubMed</a></p>

    <p align="justify">Fujioka,
      K., Greenway, F., Sheard, J., and Ying, Y. (2006). &quot;The effects of
      grapefruit on weight and insulin resistance: relationship to the metabolic
      syndrome.&quot; <i>J Med Food</i> <b>9</b>(1):49-54. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16579728">PubMed</a></p>

    <p align="justify">Fukuchi,
      K., Martin, G. M., and Monnat, R. J., Jr. (1989). &quot;Mutator phenotype of
      Werner syndrome is characterized by extensive deletions.&quot; <i>Proc Natl
        Acad Sci U S A</i> <b>86</b>(15):5893-5897. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2762303">PubMed</a></p>

    <p align="justify">Funk,
      W. D., Wang, C. K., Shelton, D. N., Harley, C. B., Pagon, G. D., and Hoeffler,
      W. K. (2000). &quot;Telomerase expression restores dermal integrity to in
      vitro-aged fibroblasts in a reconstituted skin model.&quot; <i>Exp Cell Res</i>
      <b>258</b>(2):270-278. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10896778">PubMed</a></p>

    <p align="justify">Gallo,
      R. C., and Montagnier, L. (2003). &quot;The discovery of HIV as the cause of
      AIDS.&quot; <i>N Engl J Med</i> <b>349</b>(24):2283-2285. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14668451">PubMed</a>
    </p>

    <p align="justify">Garcia,
      A. M., Busuttil, R. A., Calder, R. B., Dolle, M. E., Diaz, V., McMahan, C. A.,
      Bartke, A., Nelson, J., Reddick, R., and Vijg, J. (2008). &quot;Effect of Ames
      dwarfism and caloric restriction on spontaneous DNA mutation frequency in
      different mouse tissues.&quot; <i>Mech Ageing Dev</i> <b>129</b>(9):528-533.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18565572">PubMed</a></p>

    <p align="justify">Gardner,
      E. M. (2005). &quot;Caloric restriction decreases survival of aged mice in
      response to primary influenza infection.&quot; <i>J Gerontol A Biol Sci Med Sci</i>
      <b>60</b>(6):688-694. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15983169">PubMed</a></p>

    <p align="justify">Gardner,
      M. P., Gems, D., and Viney, M. E. (2006). &quot;Extraordinary plasticity in
      aging in Strongyloides ratti implies a gene-regulatory mechanism of lifespan
      evolution.&quot; <i>Aging Cell</i> <b>5</b>(4):315-323. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16913877">PubMed</a>
    </p>

    <p align="justify">Gavrilov,
      L. A., and Gavrilova, N. S. (2001). &quot;The reliability theory of aging and
      longevity.&quot; <i>J Theor Biol</i> <b>213</b>(4):527-545. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11742523">PubMed</a>
    </p>

    <p align="justify">Gavrilov,
      L. A., and Gavrilova, N. S. (2002). &quot;Evolutionary theories of aging and
      longevity.&quot; <i>ScientificWorldJournal</i> <b>2</b>:339-356. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12806021">PubMed</a></p>

    <p align="justify">Gebhart,
      E., Bauer, R., Raub, U., Schinzel, M., Ruprecht, K. W., and Jonas, J. B.
      (1988). &quot;Spontaneous and induced chromosomal instability in Werner
      syndrome.&quot; <i>Hum Genet</i> <b>80</b>(2):135-139. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2459043">PubMed</a></p>

    <p align="justify">Geiger,
      H., and Van Zant, G. (2002). &quot;The aging of lympho-hematopoietic stem
      cells.&quot; <i>Nat Immunol</i> <b>3</b>(4):329-333. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11919569">PubMed</a></p>

    <p align="justify">Gems,
      D. (2011). &quot;Tragedy and delight: the ethics of decelerated ageing.&quot; <i>Philos
        Trans R Soc Lond B Biol Sci</i> <b>366</b>(1561):108-112. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21115537">PubMed</a>
    </p>

    <p align="justify">Gensler,
      H. L., and Bernstein, H. (1981). &quot;DNA damage as the primary cause of
      aging.&quot; <i>Q Rev Biol</i> <b>56</b>(3):279-303. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7031747">PubMed</a></p>

    <p align="justify">George,
      J. C., Bada, J., WZeh, J., Brown, S. E., O'Hara, T., and Suydam, R. (1999).
      &quot;Age and growth estimates of bowhead whales (Balaena mysticetus) via
      aspartic acid racemization.&quot; <i>Can J Zool</i> <b>77</b>:571-580. </p>

    <p align="justify">Gerland,
      L. M., Peyrol, S., Lallemand, C., Branche, R., Magaud, J. P., and Ffrench, M.
      (2003). &quot;Association of increased autophagic inclusions labeled for beta-galactosidase
      with fibroblastic aging.&quot; <i>Exp Gerontol</i> <b>38</b>(8):887-895.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12915210">PubMed</a></p>

    <p align="justify">Gerschman,
      R., Gilbert, D. L., Nye, S. W., Dwyer, P., and Fenn, W. O. (1954). &quot;Oxygen
      poisoning and x-irradiation: a mechanism in common.&quot; <i>Science</i> <b>119</b>(3097):623-626.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/13156638">PubMed</a></p>

    <p align="justify">Gershon,
      H., and Gershon, D. (1970). &quot;Detection of inactive enzyme molecules in
      ageing organisms.&quot; <i>Nature</i> <b>227</b>(5264):1214-1217. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/5212575">PubMed</a></p>

    <p align="justify">Gershon,
      H., and Gershon, D. (2000a). &quot;Paradigms in aging research: a critical
      review and assessment.&quot; <i>Mech Ageing Dev</i> <b>117</b>(1-3):21-28.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10958920">PubMed</a></p>

    <p align="justify">Gershon,
      H., and Gershon, D. (2000b). &quot;The budding yeast, Saccharomyces cerevisiae,
      as a model for aging research: a critical review.&quot; <i>Mech Ageing Dev</i> <b>120</b>(1-3):1-22.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11087900">PubMed</a></p>

    <p align="justify">Giacomoni,
      P. U., Declercq, L., Hellemans, L., and Maes, D. (2000). &quot;Aging of human
      skin: review of a mechanistic model and first experimental data.&quot; <i>IUBMB
        Life</i> <b>49</b>(4):259-263. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10995026">PubMed</a></p>

    <p align="justify">Going,
      J. J., Stuart, R. C., Downie, M., Fletcher-Monaghan, A. J., and Keith, W. N.
      (2002). &quot;'Senescence-associated' beta-galactosidase activity in the upper
      gastrointestinal tract.&quot; <i>J Pathol</i> <b>196</b>(4):394-400. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11920734">PubMed</a></p>

    <p align="justify">Goldsmith,
      T. C. (2004). &quot;Aging as an evolved characteristic - Weismann's theory
      reconsidered.&quot; <i>Med Hypotheses</i> <b>62</b>(2):304-308. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/14962645">PubMed</a></p>

    <p align="justify">Goldstein,
      S. (1974). &quot;Aging in vitro. Growth of cultured cells from the Galapagos
      tortoise.&quot; <i>Exp Cell Res</i> <b>83</b>(2):297-302. <a href="http://www.ncbi.nlm.nih.gov/pubmed/4816902">PubMed</a>
    </p>

    <p align="justify">Goldstein,
      S. (1990). &quot;Replicative senescence: the human fibroblast comes of
      age.&quot; <i>Science</i> <b>249</b>(4973):1129-1133. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2204114">PubMed</a></p>

    <p align="justify">Gomes,
      N. M., Ryder, O. A., Houck, M. L., Charter, S. J., Walker, W., Forsyth, N. R.,
      Austad, S. N., Venditti, C., Pagel, M., Shay, J. W.<i>, et al.</i> (2011).
      &quot;Comparative biology of mammalian telomeres: hypotheses on ancestral
      states and the roles of telomeres in longevity determination.&quot; <i>Aging
        Cell</i> <b>10</b>(5):761-768. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21518243">PubMed</a></p>

    <p align="justify">Gonzalez-Suarez,
      E., Samper, E., Flores, J. M., and Blasco, M. A. (2000).
      &quot;Telomerase-deficient mice with short telomeres are resistant to skin
      tumorigenesis.&quot; <i>Nat Genet</i> <b>26</b>(1):114-117. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10973262">PubMed</a>
    </p>

    <p align="justify">Gonzalez-Suarez,
      E., Samper, E., Ramirez, A., Flores, J. M., Martin-Caballero, J., Jorcano, J.
      L., and Blasco, M. A. (2001). &quot;Increased epidermal tumors and increased
      skin wound healing in transgenic mice overexpressing the catalytic subunit of
      telomerase, mTERT, in basal keratinocytes.&quot; <i>Embo J</i> <b>20</b>(11):2619-2630.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11387197">PubMed</a></p>

    <p align="justify">Goodrich,
      D. W., and Lee, W. H. (1993). &quot;Molecular characterization of the
      retinoblastoma susceptibility gene.&quot; <i>Biochim Biophys Acta</i> <b>1155</b>(1):43-61.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8504130">PubMed</a></p>

    <p align="justify">Gopnik,
      A., Meltzoff, A., and Kuhl, P. (2000). <i>The Scientist in the Crib: What Early
        Learning Tells Us About the Mind</i>. HarperCollins, New York.</p>

    <p align="justify">Gosden,
      R. (1996). <i>Cheating Time</i>. W. H. Freeman &amp; Company, New York.</p>

    <p align="justify">Goto,
      M. (1997). &quot;Hierarchical deterioration of body systems in Werner's
      syndrome: implications for normal ageing.&quot; <i>Mech Ageing Dev</i> <b>98</b>(3):239-254.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9352493">PubMed</a></p>

    <p align="justify">Gould,
      E., Reeves, A. J., Graziano, M. S., and Gross, C. G. (1999). &quot;Neurogenesis
      in the neocortex of adult primates.&quot; <i>Science</i> <b>286</b>(5439):548-552.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10521353">PubMed</a></p>

    <p align="justify">Gray,
      M. D., Shen, J. C., Kamath-Loeb, A. S., Blank, A., Sopher, B. L., Martin, G.
      M., Oshima, J., and Loeb, L. A. (1997). &quot;The Werner syndrome protein is a
      DNA helicase.&quot; <i>Nat Genet</i> <b>17</b>(1):100-103. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9288107">PubMed</a>
    </p>

    <p align="justify">Greenberg,
      J. A., and Boozer, C. N. (2000). &quot;Metabolic mass, metabolic rate, caloric
      restriction, and aging in male Fischer 344 rats.&quot; <i>Mech Ageing Dev</i> <b>113</b>(1):37-48.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10708248">PubMed</a></p>

    <p align="justify">Greider,
      C. W., and Blackburn, E. H. (1985). &quot;Identification of a specific telomere
      terminal transferase activity in Tetrahymena extracts.&quot; <i>Cell</i> <b>43</b>(2
      Pt 1):405-413. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3907856">PubMed</a></p>

    <p align="justify">Griffith,
      J. D., Comeau, L., Rosenfield, S., Stansel, R. M., Bianchi, A., Moss, H., and
      de Lange, T. (1999). &quot;Mammalian telomeres end in a large duplex
      loop.&quot; <i>Cell</i> <b>97</b>(4):503-514. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10338214">PubMed</a></p>

    <p align="justify">Grossman,
      C. J. (1984). &quot;Regulation of the immune system by sex steroids.&quot; <i>Endocr
        Rev</i> <b>5</b>(3):435-455. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6381037">PubMed</a></p>

    <p align="justify">Grossman,
      T. (2005). &quot;Latest advances in antiaging medicine.&quot; <i>Keio J Med</i>
      <b>54</b>(2):85-94. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16077258">PubMed</a></p>

    <p align="justify">Grube,
      K., and Burkle, A. (1992). &quot;Poly(ADP-ribose) polymerase activity in
      mononuclear leukocytes of 13 mammalian species correlates with species-specific
      life span.&quot; <i>Proc Natl Acad Sci U S A</i> <b>89</b>(24):11759-11763.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1465394">PubMed</a></p>

    <p align="justify">Guevara-Aguirre,
      J., Balasubramanian, P., Guevara-Aguirre, M., Wei, M., Madia, F., Cheng, C. W.,
      Hwang, D., Martin-Montalvo, A., Saavedra, J., Ingles, S.<i>, et al.</i> (2011).
      &quot;Growth hormone receptor deficiency is associated with a major reduction
      in pro-aging signaling, cancer, and diabetes in humans.&quot; <i>Sci Transl Med</i>
      <b>3</b>(70):70ra13. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21325617">PubMed</a></p>

    <p align="justify">Gurdon,
      J. B., Laskey, R. A., and Reeves, O. R. (1975). &quot;The developmental
      capacity of nuclei transplanted from keratinized skin cells of adult
      frogs.&quot; <i>J Embryol Exp Morphol</i> <b>34</b>(1):93-112. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/1102625">PubMed</a></p>

    <p align="justify">Hahn,
      W. C. (2003). &quot;Role of telomeres and telomerase in the pathogenesis of
      human cancer.&quot; <i>J Clin Oncol</i> <b>21</b>(10):2034-2043. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12743159">PubMed</a></p>

    <p align="justify">Haigh,
      R., and Bagaric, M. (2002). &quot;Immortality and sentencing law.&quot; <i>The
        Journal of Philosophy, Science &amp; Law</i> <b>2</b></p>

    <p align="justify">Hamad,
      N. M., Elconin, J. H., Karnoub, A. E., Bai, W., Rich, J. N., Abraham, R. T.,
      Der, C. J., and Counter, C. M. (2002). &quot;Distinct requirements for Ras
      oncogenesis in human versus mouse cells.&quot; <i>Genes Dev</i> <b>16</b>(16):2045-2057.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12183360">PubMed</a></p>

    <p align="justify">Hamilton,
      W. D. (1966). &quot;The moulding of senescence by natural selection.&quot; <i>J
        Theor Biol</i> <b>12</b>(1):12-45. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6015424">PubMed</a></p>

    <p align="justify">Hamilton,
      J. B., and Mestler, G. E. (1969). &quot;Mortality and survival: comparison of
      eunuchs with intact men and women in a mentally retarded population.&quot; <i>J
        Gerontol</i> <b>24</b>(4):395-411. <a href="http://www.ncbi.nlm.nih.gov/pubmed/5362349">PubMed</a></p>

    <p align="justify">Hamilton,
      M. L., Van Remmen, H., Drake, J. A., Yang, H., Guo, Z. M., Kewitt, K., Walter,
      C. A., and Richardson, A. (2001). &quot;Does oxidative damage to DNA increase
      with age?&quot; <i>Proc Natl Acad Sci U S A</i> <b>98</b>(18):10469-10474.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11517304">PubMed</a></p>

    <p align="justify">Hamilton,
      B., Dong, Y., Shindo, M., Liu, W., Odell, I., Ruvkun, G., and Lee, S. S.
      (2005). &quot;A systematic RNAi screen for longevity genes in C. elegans.&quot;
      <i>Genes Dev</i> <b>19</b>(13):1544-1555. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15998808">PubMed</a></p>

    <p align="justify">Hammerman,
      M. R. (1987). &quot;Insulin-like growth factors and aging.&quot; <i>Endocrinol
        Metab Clin North Am</i> <b>16</b>(4):995-1011. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3322823">PubMed</a>
    </p>

    <p align="justify">Han,
      J., Mistriotis, P., Lei, P., Wang, D., Liu, S., and Andreadis, S. T. (2012).
      &quot;Nanog reverses the effects of organismal aging on mesenchymal stem cell
      proliferation and myogenic differentiation potential.&quot; <i>Stem Cells</i> <b>30</b>(12):2746-2759.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22949105">PubMed</a></p>

    <p align="justify">Hande,
      M. P., Samper, E., Lansdorp, P., and Blasco, M. A. (1999). &quot;Telomere
      length dynamics and chromosomal instability in cells derived from telomerase
      null mice.&quot; <i>J Cell Biol</i> <b>144</b>(4):589-601. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10037783">PubMed</a>
    </p>

    <p align="justify">Hara,
      E., Tsurui, H., Shinozaki, A., Nakada, S., and Oda, K. (1991).
      &quot;Cooperative effect of antisense-Rb and antisense-p53 oligomers on the
      extension of life span in human diploid fibroblasts, TIG-1.&quot; <i>Biochem
        Biophys Res Commun</i> <b>179</b>(1):528-534. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1909121">PubMed</a>
    </p>

    <p align="justify">Hara,
      E., Smith, R., Parry, D., Tahara, H., Stone, S., and Peters, G. (1996).
      &quot;Regulation of p16CDKN2 expression and its implications for cell
      immortalization and senescence.&quot; <i>Mol Cell Biol</i> <b>16</b>(3):859-867.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8622687">PubMed</a></p>

    <p align="justify">Harada,
      T., Tokuriki, M., and Tanioka, Y. (1999). &quot;Age-related changes in the
      brainstem auditory evoked potentials of the marmoset.&quot; <i>Hear Res</i> <b>128</b>(1-2):119-124.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10082292">PubMed</a></p>

    <p align="justify">Hari,
      R., Burde, V., and Arking, R. (1998). &quot;Immunological confirmation of
      elevated levels of CuZn superoxide dismutase protein in an artificially
      selected long-lived strain of Drosophila melanogaster.&quot; <i>Exp Gerontol</i>
      <b>33</b>(3):227-237. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9615921">PubMed</a></p>

    <p align="justify">Harle-Bachor,
      C., and Boukamp, P. (1996). &quot;Telomerase activity in the regenerative basal
      layer of the epidermis inhuman skin and in immortal and carcinoma-derived skin
      keratinocytes.&quot; <i>Proc Natl Acad Sci U S A</i> <b>93</b>(13):6476-6481.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8692840">PubMed</a></p>

    <p align="justify">Harley,
      C. B., Pollard, J. W., Chamberlain, J. W., Stanners, C. P., and Goldstein, S.
      (1980). &quot;Protein synthetic errors do not increase during aging of cultured
      human fibroblasts.&quot; <i>Proc Natl Acad Sci U S A</i> <b>77</b>(4):1885-1889.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6246512">PubMed</a></p>

    <p align="justify">Harley,
      C. B., Futcher, A. B., and Greider, C. W. (1990). &quot;Telomeres shorten
      during ageing of human fibroblasts.&quot; <i>Nature</i> <b>345</b>(6274):458-460.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2342578">PubMed</a></p>

    <p align="justify">Harley,
      C. B., Liu, W., Blasco, M., Vera, E., Andrews, W. H., Briggs, L. A., and
      Raffaele, J. M. (2011). &quot;A natural product telomerase activator as part of
      a health maintenance program.&quot; <i>Rejuvenation Res</i> <b>14</b>(1):45-56.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20822369">PubMed</a></p>

    <p align="justify">Harman,
      D. (1956). &quot;Aging: a theory based on free radical and radiation
      chemistry.&quot; <i>J Gerontol</i> <b>11</b>(3):298-300. <a href="http://www.ncbi.nlm.nih.gov/pubmed/13332224">PubMed</a>
    </p>

    <p align="justify">Harman,
      D. (1968). &quot;Free radical theory of aging: effect of free radical reaction
      inhibitors on the mortality rate of male LAF mice.&quot; <i>J Gerontol</i> <b>23</b>(4):476-482.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5723482">PubMed</a></p>

    <p align="justify">Harman,
      D. (1972). &quot;The biologic clock: the mitochondria?&quot; <i>J Am Geriatr
        Soc</i> <b>20</b>(4):145-147. <a href="http://www.ncbi.nlm.nih.gov/pubmed/5016631">PubMed</a></p>

    <p align="justify">Harman,
      D. (1981). &quot;The aging process.&quot; <i>Proc Natl Acad Sci U S A</i> <b>78</b>(11):7124-7128.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6947277">PubMed</a></p>

    <p align="justify">Harper,
      J. W., Adami, G. R., Wei, N., Keyomarsi, K., and Elledge, S. J. (1993).
      &quot;The p21 Cdk-interacting protein Cip1 is a potent inhibitor of G1
      cyclin-dependent kinases.&quot; <i>Cell</i> <b>75</b>(4):805-816. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/8242751">PubMed</a></p>

    <p align="justify">Harper,
      J. W., Elledge, S. J., Keyomarsi, K., Dynlacht, B., Tsai, L. H., Zhang, P.,
      Dobrowolski, S., Bai, C., Connell-Crowley, L., Swindell, E.<i>, et al.</i>
      (1995). &quot;Inhibition of cyclin-dependent kinases by p21.&quot; <i>Mol Biol
        Cell</i> <b>6</b>(4):387-400. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7626805">PubMed</a></p>

    <p align="justify">Harper,
      J. M., Leathers, C. W., and Austad, S. N. (2006). &quot;Does caloric
      restriction extend life in wild mice?&quot; <i>Aging Cell</i> <b>5</b>(6):441-449.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17054664">PubMed</a></p>

    <p align="justify">Harper,
      J. M., Salmon, A. B., Leiser, S. F., Galecki, A. T., and Miller, R. A. (2007).
      &quot;Skin-derived fibroblasts from long-lived species are resistant to some,
      but not all, lethal stresses and to the mitochondrial inhibitor rotenone.&quot;
      <i>Aging Cell</i> <b>6</b>(1):1-13. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17156084">PubMed</a></p>

    <p align="justify">Harper,
      J. M., Wang, M., Galecki, A. T., Ro, J., Williams, J. B., and Miller, R. A.
      (2011). &quot;Fibroblasts from long-lived bird species are resistant to
      multiple forms of stress.&quot; <i>J Exp Biol</i> <b>214</b>(Pt 11):1902-1910.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21562178">PubMed</a></p>

    <p align="justify">Harris,
      J. (2004). &quot;Immortal ethics.&quot; <i>Ann N Y Acad Sci</i> <b>1019</b>:527-534.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15247080">PubMed</a></p>

    <p align="justify">Harrison,
      D. E., Strong, R., Sharp, Z. D., Nelson, J. F., Astle, C. M., Flurkey, K.,
      Nadon, N. L., Wilkinson, J. E., Frenkel, K., Carter, C. S.<i>, et al.</i>
      (2009). &quot;Rapamycin fed late in life extends lifespan in genetically
      heterogeneous mice.&quot; <i>Nature</i> <b>460</b>(7253):392-395. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19587680">PubMed</a></p>

    <p align="justify">Hart,
      R. W., and Setlow, R. B. (1974). &quot;Correlation between deoxyribonucleic
      acid excision-repair and life-span in a number of mammalian species.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>71</b>(6):2169-2173. <a href="http://www.ncbi.nlm.nih.gov/pubmed/4526202">PubMed</a>
    </p>

    <p align="justify">Harvey,
      P. H., and Zammuto, R. M. (1985). &quot;Patterns of mortality and age at first
      reproduction in natural populations of mammals.&quot; <i>Nature</i> <b>315</b>(6017):319-320.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4000262">PubMed</a></p>

    <p align="justify">Haseltine,
      W. A. (2004). &quot;An interview with William A. Haseltine.&quot; <i>Rejuvenation
        Res</i> <b>7</b>(3):229-231. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15588523">PubMed</a></p>

    <p align="justify">Hastie,
      N. D., Dempster, M., Dunlop, M. G., Thompson, A. M., Green, D. K., and
      Allshire, R. C. (1990). &quot;Telomere reduction in human colorectal carcinoma
      and with ageing.&quot; <i>Nature</i> <b>346</b>(6287):866-868. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2392154">PubMed</a></p>

    <p align="justify">Hasty,
      J., McMillen, D., and Collins, J. J. (2002). &quot;Engineered gene
      circuits.&quot; <i>Nature</i> <b>420</b>(6912):224-230. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12432407">PubMed</a>
    </p>

    <p align="justify">Hasty,
      P., Campisi, J., Hoeijmakers, J., van Steeg, H., and Vijg, J. (2003).
      &quot;Aging and genome maintenance: lessons from the mouse?&quot; <i>Science</i>
      <b>299</b>(5611):1355-1359. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12610296">PubMed</a></p>

    <p align="justify">Hasty,
      P., and Vijg, J. (2004). &quot;Accelerating aging by mouse reverse genetics: a
      rational approach to understanding longevity.&quot; <i>Aging Cell</i> <b>3</b>(2):55-65.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15038819">PubMed</a></p>

    <p align="justify">Haussmann,
      M. F., Winkler, D. W., O'Reilly, K. M., Huntington, C. E., Nisbet, I. C., and
      Vleck, C. M. (2003). &quot;Telomeres shorten more slowly in long-lived birds
      and mammals than in short-lived ones.&quot; <i>Proc R Soc Lond B Biol Sci</i> <b>270</b>(1522):1387-1392.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12965030">PubMed</a></p>

    <p align="justify">Hayashi,
      J., Ohta, S., Kagawa, Y., Kondo, H., Kaneda, H., Yonekawa, H., Takai, D., and
      Miyabayashi, S. (1994). &quot;Nuclear but not mitochondrial genome involvement
      in human age-related mitochondrial dysfunction. Functional integrity of
      mitochondrial DNA from aged subjects.&quot; <i>J Biol Chem</i> <b>269</b>(9):6878-6883.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8120050">PubMed</a></p>

    <p align="justify">Hayflick,
      L., and Moorhead, P. S. (1961). &quot;The serial cultivation of human diploid
      cell strains.&quot; <i>Exp Cell Res</i> <b>25</b>:585-621. <a href="http://www.ncbi.nlm.nih.gov/pubmed/13905658">PubMed</a>
    </p>

    <p align="justify">Hayflick,
      L. (1985). &quot;The cell biology of aging.&quot; <i>Clin Geriatr Med</i> <b>1</b>(1):15-27.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/3913498">PubMed</a></p>

    <p align="justify">Hayflick,
      L. (1994). <i>How and Why We Age</i>. Ballantine Books, New York.</p>

    <p align="justify">Hayflick,
      L. (2000). &quot;The future of ageing.&quot; <i>Nature</i> <b>408</b>(6809):267-269.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11089985">PubMed</a></p>

    <p align="justify">Hayflick,
      L. (2004). &quot;&quot;Anti-aging&quot; is an oxymoron.&quot; <i>J Gerontol A
        Biol Sci Med Sci</i> <b>59</b>(6):B573-578. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15215267">PubMed</a></p>

    <p align="justify">Hedges,
      S. B. (2002). &quot;The origin and evolution of model organisms.&quot; <i>Nat
        Rev Genet</i> <b>3</b>(11):838-849. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12415314">PubMed</a></p>

    <p align="justify">Heidinger,
      B. J., Blount, J. D., Boner, W., Griffiths, K., Metcalfe, N. B., and Monaghan,
      P. (2012). &quot;Telomere length in early life predicts lifespan.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>109</b>(5):1743-1748. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/22232671">PubMed</a></p>

    <p align="justify">Heidrick,
      M. L., Hendricks, L. C., and Cook, D. E. (1984). &quot;Effect of dietary
      2-mercaptoethanol on the life span, immune system, tumor incidence and lipid
      peroxidation damage in spleen lymphocytes of aging BC3F1 mice.&quot; <i>Mech
        Ageing Dev</i> <b>27</b>(3):341-358. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6334792">PubMed</a></p>

    <p align="justify">Heilbronn,
      L. K., de Jonge, L., Frisard, M. I., DeLany, J. P., Larson-Meyer, D. E., Rood,
      J., Nguyen, T., Martin, C. K., Volaufova, J., Most, M. M.<i>, et al.</i>
      (2006). &quot;Effect of 6-month calorie restriction on biomarkers of longevity,
      metabolic adaptation, and oxidative stress in overweight individuals: a
      randomized controlled trial.&quot; <i>Jama</i> <b>295</b>(13):1539-1548.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16595757">PubMed</a></p>

    <p align="justify">Heinemann,
      M., and Panke, S. (2006). &quot;Synthetic biology--putting engineering into
      biology.&quot; <i>Bioinformatics</i> <b>22</b>(22):2790-2799. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16954140">PubMed</a></p>

    <p align="justify">Helman,
      A., Klochendler, A., Azazmeh, N., Gabai, Y., Horwitz, E., Anzi, S., Swisa, A.,
      Condiotti, R., Granit, R. Z., Nevo, Y.<i>, et al.</i> (2016).
      &quot;p16(Ink4a)-induced senescence of pancreatic beta cells enhances insulin
      secretion.&quot; <i>Nat Med</i> <b>22</b>(4):412-420. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/26950362">PubMed</a></p>

    <p align="justify">Hemann,
      M. T., and Greider, C. W. (2000). &quot;Wild-derived inbred mouse strains have
      short telomeres.&quot; <i>Nucleic Acids Res</i> <b>28</b>(22):4474-4478.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11071935">PubMed</a></p>

    <p align="justify">Hemann,
      M. T., Strong, M. A., Hao, L. Y., and Greider, C. W. (2001). &quot;The shortest
      telomere, not average telomere length, is critical for cell viability and
      chromosome stability.&quot; <i>Cell</i> <b>107</b>(1):67-77. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11595186">PubMed</a></p>

    <p align="justify">Henning,
      K. A., Li, L., Iyer, N., McDaniel, L. D., Reagan, M. S., Legerski, R., Schultz,
      R. A., Stefanini, M., Lehmann, A. R., Mayne, L. V.<i>, et al.</i> (1995).
      &quot;The Cockayne syndrome group A gene encodes a WD repeat protein that
      interacts with CSB protein and a subunit of RNA polymerase II TFIIH.&quot; <i>Cell</i>
      <b>82</b>(4):555-564. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7664335">PubMed</a></p>

    <p align="justify">Herbig,
      U., Ferreira, M., Condel, L., Carey, D., and Sedivy, J. M. (2006).
      &quot;Cellular senescence in aging primates.&quot; <i>Science</i> <b>311</b>(5765):1257.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16456035">PubMed</a></p>

    <p align="justify">Herranz,
      D., Muñoz-Martin, M., Cañamero, M., Mulero, F., Martinez-Pastor, B.,
      Fernandez-Capetillo, O., and Serrano, M. (2010). &quot;Sirt1 improves healthy
      ageing and protects from metabolic syndrome-associated cancer.&quot; <i>Nat
        Commun</i> <b>1</b>:3. </p>

    <p align="justify">Herwig,
      S., and Strauss, M. (1997). &quot;The retinoblastoma protein: a master
      regulator of cell cycle, differentiation and apoptosis.&quot; <i>Eur J Biochem</i>
      <b>246</b>(3):581-601. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9219514">PubMed</a></p>

    <p align="justify">Hill,
      K., Boesch, C., Goodall, J., Pusey, A., Williams, J., and Wrangham, R. (2001).
      &quot;Mortality rates among wild chimpanzees.&quot; <i>J Hum Evol</i> <b>40</b>(5):437-450.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11322804">PubMed</a></p>

    <p align="justify">Hjelmborg,
      v. J., Iachine, I., Skytthe, A., Vaupel, J. W., McGue, M., Koskenvuo, M.,
      Kaprio, J., Pedersen, N. L., and Christensen, K. (2006). &quot;Genetic
      influence on human lifespan and longevity.&quot; <i>Hum Genet</i> <b>119</b>(3):312-321.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16463022">PubMed</a></p>

    <p align="justify">Ho,
      K. Y., Evans, W. S., Blizzard, R. M., Veldhuis, J. D., Merriam, G. R.,
      Samojlik, E., Furlanetto, R., Rogol, A. D., Kaiser, D. L., and Thorner, M. O.
      (1987). &quot;Effects of sex and age on the 24-hour profile of growth hormone
      secretion in man: importance of endogenous estradiol concentrations.&quot; <i>J
        Clin Endocrinol Metab</i> <b>64</b>(1):51-58. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3782436">PubMed</a>
    </p>

    <p align="justify">Ho,
      Y. S., Magnenat, J. L., Bronson, R. T., Cao, J., Gargano, M., Sugawara, M., and
      Funk, C. D. (1997). &quot;Mice deficient in cellular glutathione peroxidase
      develop normally and show no increased sensitivity to hyperoxia.&quot; <i>J
        Biol Chem</i> <b>272</b>(26):16644-16651. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9195979">PubMed</a></p>

    <p align="justify">Hoeijmakers,
      J. H. (2009). &quot;DNA damage, aging, and cancer.&quot; <i>N Engl J Med</i> <b>361</b>(15):1475-1485.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19812404">PubMed</a></p>

    <p align="justify">Holliday,
      R. (1984). &quot;The biological significance of meiosis.&quot; <i>Symp Soc Exp
        Biol</i> <b>38</b>:381-394. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6400219">PubMed</a></p>

    <p align="justify">Holliday,
      R. (1995). <i>Understanding Ageing</i>. Cambridge University Press, Cambridge.</p>

    <p align="justify">Holliday,
      R. (2004). &quot;The multiple and irreversible causes of aging.&quot; <i>J
        Gerontol A Biol Sci Med Sci</i> <b>59</b>(6):B568-572. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15215266">PubMed</a>
    </p>

    <p align="justify">Holliday,
      R. (2009). &quot;The extreme arrogance of anti-aging medicine.&quot; <i>Biogerontology</i>
      <b>10</b>(2):223-228. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18726707">PubMed</a></p>

    <p align="justify">Holloszy,
      J. O., and Smith, E. K. (1986). &quot;Longevity of cold-exposed rats: a
      reevaluation of the &quot;rate-of-living theory&quot;.&quot; <i>J Appl Physiol</i>
      <b>61</b>(5):1656-1660. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3781978">PubMed</a></p>

    <p align="justify">Holloszy,
      J. O., and Schechtman, K. B. (1991). &quot;Interaction between exercise and
      food restriction: effects on longevity of male rats.&quot; <i>J Appl Physiol</i>
      <b>70</b>(4):1529-1535. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2055832">PubMed</a></p>

    <p align="justify">Holloszy,
      J. O. (1997). &quot;Mortality rate and longevity of food-restricted exercising
      male rats: a reevaluation.&quot; <i>J Appl Physiol</i> <b>82</b>(2):399-403.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9049716">PubMed</a></p>

    <p align="justify">Holloszy,
      J. O. (1998). &quot;Longevity of exercising male rats: effect of an antioxidant
      supplemented diet.&quot; <i>Mech Ageing Dev</i> <b>100</b>(3):211-219.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9578110">PubMed</a></p>

    <p align="justify">Holloszy,
      J. O. (2000). &quot;The biology of aging.&quot; <i>Mayo Clin Proc</i> <b>75
        Suppl</b>:S3-8; discussion S8-9. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10959208">PubMed</a></p>

    <p align="justify">Holmes,
      D. J., and Austad, S. N. (1995). &quot;Birds as animal models for the
      comparative biology of aging: a prospectus.&quot; <i>J Gerontol A Biol Sci Med
        Sci</i> <b>50</b>(2):B59-66. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7874580">PubMed</a></p>

    <p align="justify">Holmes,
      D. J., Fluckiger, R., and Austad, S. N. (2001). &quot;Comparative biology of
      aging in birds: an update.&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):869-883.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295520">PubMed</a></p>

    <p align="justify">Holmes,
      D. J., and Ottinger, M. A. (2003). &quot;Birds as long-lived animal models for
      the study of aging.&quot; <i>Exp Gerontol</i> <b>38</b>(11-12):1365-1375.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14698817">PubMed</a></p>

    <p align="justify">Hopkin,
      K. (2003). &quot;Dietary drawbacks.&quot; <i>Sci Aging Knowledge Environ</i> <b>2003</b>(8):NS4.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12844545">PubMed</a></p>

    <p align="justify">Horikoshi,
      T., Balin, A. K., and Carter, D. M. (1986). &quot;Effect of oxygen on the
      growth of human epidermal keratinocytes.&quot; <i>J Invest Dermatol</i> <b>86</b>(4):424-427.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2427617">PubMed</a></p>

    <p align="justify">Horikoshi,
      T., Balin, A. K., and Carter, D. M. (1991). &quot;Effects of oxygen tension on
      the growth and pigmentation of normal human melanocytes.&quot; <i>J Invest
        Dermatol</i> <b>96</b>(6):841-844. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1904467">PubMed</a></p>

    <p align="justify">Horwitz,
      E. M., Prockop, D. J., Fitzpatrick, L. A., Koo, W. W., Gordon, P. L., Neel, M.,
      Sussman, M., Orchard, P., Marx, J. C., Pyeritz, R. E.<i>, et al.</i> (1999).
      &quot;Transplantability and therapeutic effects of bone marrow-derived
      mesenchymal cells in children with osteogenesis imperfecta.&quot; <i>Nat Med</i>
      <b>5</b>(3):309-313. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10086387">PubMed</a></p>

    <p align="justify">Howitz,
      K. T., Bitterman, K. J., Cohen, H. Y., Lamming, D. W., Lavu, S., Wood, J. G.,
      Zipkin, R. E., Chung, P., Kisielewski, A., Zhang, L. L.<i>, et al.</i> (2003).
      &quot;Small molecule activators of sirtuins extend Saccharomyces cerevisiae
      lifespan.&quot; <i>Nature</i> <b>425</b>(6954):191-196. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12939617">PubMed</a>
    </p>

    <p align="justify">Hsin,
      H., and Kenyon, C. (1999). &quot;Signals from the reproductive system regulate
      the lifespan of C. elegans.&quot; <i>Nature</i> <b>399</b>(6734):362-366.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10360574">PubMed</a></p>

    <p align="justify">Huang,
      S., Li, B., Gray, M. D., Oshima, J., Mian, I. S., and Campisi, J. (1998).
      &quot;The premature ageing syndrome protein, WRN, is a 3'--&gt;5'
      exonuclease.&quot; <i>Nat Genet</i> <b>20</b>(2):114-116. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9771700">PubMed</a>
    </p>

    <p align="justify">Huang,
      T. T., Carlson, E. J., Gillespie, A. M., Shi, Y., and Epstein, C. J. (2000).
      &quot;Ubiquitous overexpression of CuZn superoxide dismutase does not extend
      life span in mice.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>55</b>(1):B5-9.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10719757">PubMed</a></p>

    <p align="justify">Hughes,
      K. A., Alipaz, J. A., Drnevich, J. M., and Reynolds, R. M. (2002). &quot;A test
      of evolutionary theories of aging.&quot; <i>Proc Natl Acad Sci U S A</i> <b>99</b>(22):14286-14291.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12386342">PubMed</a></p>

    <p align="justify">Hunt,
      J., Jennions, M. D., Spyrou, N., and Brooks, R. (2006). &quot;Artificial
      selection on male longevity influences age-dependent reproductive effort in the
      black field cricket Teleogryllus commodus.&quot; <i>Am Nat</i> <b>168</b>(3):E72-86.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16947102">PubMed</a></p>

    <p align="justify">Hussussian,
      C. J., Struewing, J. P., Goldstein, A. M., Higgins, P. A., Ally, D. S.,
      Sheahan, M. D., Clark, W. H., Jr., Tucker, M. A., and Dracopoli, N. C. (1994).
      &quot;Germline p16 mutations in familial melanoma.&quot; <i>Nat Genet</i> <b>8</b>(1):15-21.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7987387">PubMed</a></p>

    <p align="justify">Hwang,
      S. J., Lozano, G., Amos, C. I., and Strong, L. C. (2003). &quot;Germline p53
      mutations in a cohort with childhood sarcoma: sex differences in cancer
      risk.&quot; <i>Am J Hum Genet</i> <b>72</b>(4):975-983. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12610779">PubMed</a>
    </p>

    <p align="justify">Hwang,
      W. S., Ryu, Y. J., Park, J. H., Park, E. S., Lee, E. G., Koo, J. M., Jeon, H.
      Y., Lee, B. C., Kang, S. K., Kim, S. J.<i>, et al.</i> (2004). &quot;Evidence
      of a pluripotent human embryonic stem cell line derived from a cloned
      blastocyst.&quot; <i>Science</i> <b>303</b>(5664):1669-1674. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/14963337">PubMed</a></p>

    <p align="justify">Hwang,
      W. S., Roh, S. I., Lee, B. C., Kang, S. K., Kwon, D. K., Kim, S., Kim, S. J.,
      Park, S. W., Kwon, H. S., Lee, C. K.<i>, et al.</i> (2005).
      &quot;Patient-specific embryonic stem cells derived from human SCNT
      blastocysts.&quot; <i>Science</i> <b>308</b>(5729):1777-1783. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15905366">PubMed</a></p>

    <p align="justify">Hyun,
      M., Lee, J., Lee, K., May, A., Bohr, V. A., and Ahn, B. (2008). &quot;Longevity
      and resistance to stress correlate with DNA repair capacity in Caenorhabditis
      elegans.&quot; <i>Nucleic Acids Res</i> <b>36</b>(4):1380-1389. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/18203746">PubMed</a></p>

    <p align="justify">Ingram,
      D. K., Roth, G. S., Lane, M. A., Ottinger, M. A., Zou, S., de Cabo, R., and
      Mattison, J. A. (2006). &quot;The potential for dietary restriction to increase
      longevity in humans: extrapolation from monkey studies.&quot; <i>Biogerontology</i>
      <b>7</b>(3):143-148. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16732404">PubMed</a></p>

    <p align="justify">Inoue,
      S., Koya-Miyata, S., Ushio, S., Iwaki, K., Ikeda, M., and Kurimoto, M. (2003).
      &quot;Royal Jelly prolongs the life span of C3H/HeJ mice: correlation with
      reduced DNA damage.&quot; <i>Exp Gerontol</i> <b>38</b>(9):965-969. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12954483">PubMed</a></p>

    <p align="justify">Iwama,
      H., Ohyashiki, K., Ohyashiki, J. H., Hayashi, S., Yahata, N., Ando, K., Toyama,
      K., Hoshika, A., Takasaki, M., Mori, M.<i>, et al.</i> (1998). &quot;Telomeric
      length and telomerase activity vary with age in peripheral blood cells obtained
      from normal individuals.&quot; <i>Hum Genet</i> <b>102</b>(4):397-402.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9600234">PubMed</a></p>

    <p align="justify">Ja,
      W. W., West, A. P., Jr., Delker, S. L., Bjorkman, P. J., Benzer, S., and
      Roberts, R. W. (2007). &quot;Extension of Drosophila melanogaster life span
      with a GPCR peptide inhibitor.&quot; <i>Nat Chem Biol</i> <b>3</b>(7):415-419.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17546039">PubMed</a></p>

    <p align="justify">Jahng,
      J. W., Kim, J. G., Kim, H. J., Kim, B. T., Kang, D. W., and Lee, J. H. (2007).
      &quot;Chronic food restriction in young rats results in depression- and
      anxiety-like behaviors with decreased expression of serotonin reuptake
      transporter.&quot; <i>Brain Res</i> <b>1150</b>:100-107. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17383614">PubMed</a>
    </p>

    <p align="justify">Jang,
      M., Cai, L., Udeani, G. O., Slowing, K. V., Thomas, C. F., Beecher, C. W.,
      Fong, H. H., Farnsworth, N. R., Kinghorn, A. D., Mehta, R. G.<i>, et al.</i>
      (1997). &quot;Cancer chemopreventive activity of resveratrol, a natural product
      derived from grapes.&quot; <i>Science</i> <b>275</b>(5297):218-220. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/8985016">PubMed</a></p>

    <p align="justify">Janzen,
      D. H. (1976). &quot;Why bamboos wait so long to flower.&quot; <i>Ann Rev Ecol
        Sys</i> <b>7</b>:347-391. </p>

    <p align="justify">Jaskelioff,
      M., Muller, F. L., Paik, J. H., Thomas, E., Jiang, S., Adams, A. C., Sahin, E.,
      Kost-Alimova, M., Protopopov, A., Cadinanos, J.<i>, et al.</i> (2011).
      &quot;Telomerase reactivation reverses tissue degeneration in aged
      telomerase-deficient mice.&quot; <i>Nature</i> <b>469</b>(7328):102-106.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21113150">PubMed</a></p>

    <p align="justify">Jazwinski,
      S. M. (1990). &quot;Aging and senescence of the budding yeast Saccharomyces
      cerevisiae.&quot; <i>Mol Microbiol</i> <b>4</b>(3):337-343. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2192228">PubMed</a>
    </p>

    <p align="justify">Jazwinski,
      S. M. (2001). &quot;New clues to old yeast.&quot; <i>Mech Ageing Dev</i> <b>122</b>(9):865-882.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11348655">PubMed</a></p>

    <p align="justify">Jazwinski,
      S. M. (2005). &quot;Yeast longevity and aging--the mitochondrial
      connection.&quot; <i>Mech Ageing Dev</i> <b>126</b>(2):243-248. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15621203">PubMed</a></p>

    <p align="justify">Jena,
      B. S., Nayak, S. B., and Patnaik, B. K. (2002). &quot;Age-related effect of
      aluminium on the catalase activities of the brains of two species of
      poikilothermic vertebrates.&quot; <i>Gerontology</i> <b>48</b>(1):34-38.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11844928">PubMed</a></p>

    <p align="justify">Jeyapalan,
      J. C., and Sedivy, J. M. (2008). &quot;Cellular senescence and organismal
      aging.&quot; <i>Mech Ageing Dev</i> <b>129</b>(7-8):467-474. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/18502472">PubMed</a></p>

    <p align="justify">Jiang,
      X. R., Jimenez, G., Chang, E., Frolkis, M., Kusler, B., Sage, M., Beeche, M.,
      Bodnar, A. G., Wahl, G. M., Tlsty, T. D.<i>, et al.</i> (1999).
      &quot;Telomerase expression in human somatic cells does not induce changes
      associated with a transformed phenotype.&quot; <i>Nat Genet</i> <b>21</b>(1):111-114.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9916802">PubMed</a></p>

    <p align="justify">Johnson,
      T. E., Mitchell, D. H., Kline, S., Kemal, R., and Foy, J. (1984).
      &quot;Arresting development arrests aging in the nematode Caenorhabditis
      elegans.&quot; <i>Mech Ageing Dev</i> <b>28</b>(1):23-40. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6542614">PubMed</a>
    </p>

    <p align="justify">Johnson,
      T. E. (1990). &quot;Increased life-span of age-1 mutants in Caenorhabditis
      elegans and lower Gompertz rate of aging.&quot; <i>Science</i> <b>249</b>(4971):908-912.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2392681">PubMed</a></p>

    <p align="justify">Johnson,
      F. B., Sinclair, D. A., and Guarente, L. (1999). &quot;Molecular biology of
      aging.&quot; <i>Cell</i> <b>96</b>(2):291-302. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9988222">PubMed</a></p>

    <p align="justify">Johnson,
      T. E. (2002). &quot;A personal retrospective on the genetics of aging.&quot; <i>Biogerontology</i>
      <b>3</b>(1-2):7-12. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12014846">PubMed</a></p>

    <p align="justify">Jolly,
      C. A., Muthukumar, A., Avula, C. P., Troyer, D., and Fernandes, G. (2001).
      &quot;Life span is prolonged in food-restricted autoimmune-prone (NZB x
      NZW)F(1) mice fed a diet enriched with (n-3) fatty acids.&quot; <i>J Nutr</i> <b>131</b>(10):2753-2760.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11584100">PubMed</a></p>

    <p align="justify">Juhasz,
      G., Erdi, B., Sass, M., and Neufeld, T. P. (2007). &quot;Atg7-dependent
      autophagy promotes neuronal health, stress tolerance, and longevity but is
      dispensable for metamorphosis in Drosophila.&quot; <i>Genes Dev</i> <b>21</b>(23):3061-3066.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18056421">PubMed</a></p>

    <p align="justify">Jurgens,
      K. D., and Prothero, J. (1987). &quot;Scaling of maximal lifespan in
      bats.&quot; <i>Comp Biochem Physiol A</i> <b>88</b>(2):361-367. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2890474">PubMed</a></p>

    <p align="justify">Jurk,
      D., Wang, C., Miwa, S., Maddick, M., Korolchuk, V., Tsolou, A., Gonos, E. S.,
      Thrasivoulou, C., Saffrey, M. J., Cameron, K.<i>, et al.</i> (2012).
      &quot;Postmitotic neurons develop a p21-dependent senescence-like phenotype
      driven by a DNA damage response.&quot; <i>Aging Cell</i> <b>11</b>(6):996-1004.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22882466">PubMed</a></p>

    <p align="justify">Kakuo,
      S., Asaoka, K., and Ide, T. (1999). &quot;Human is a unique species among
      primates in terms of telomere length.&quot; <i>Biochem Biophys Res Commun</i> <b>263</b>(2):308-314.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10491289">PubMed</a></p>

    <p align="justify">Kamada,
      Y., Yoshino, K., Kondo, C., Kawamata, T., Oshiro, N., Yonezawa, K., and Ohsumi,
      Y. (2010). &quot;Tor directly controls the Atg1 kinase complex to regulate
      autophagy.&quot; <i>Mol Cell Biol</i> <b>30</b>(4):1049-1058. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19995911">PubMed</a></p>

    <p align="justify">Kammori,
      M., Nakamura, K. I., Kawahara, M., Mimura, Y., Kaminishi, M., and Takubo, K.
      (2002). &quot;Telomere shortening with aging in human thyroid and parathyroid
      tissue.&quot; <i>Exp Gerontol</i> <b>37</b>(4):513-521. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11830354">PubMed</a>
    </p>

    <p align="justify">Kanungo,
      M. S. (1994). <i>Genes and Aging</i>. Cambridge University Press, Cambridge,
      UK.</p>

    <p align="justify">Kapahi,
      P., Boulton, M. E., and Kirkwood, T. B. (1999). &quot;Positive correlation
      between mammalian life span and cellular resistance to stress.&quot; <i>Free
        Radic Biol Med</i> <b>26</b>(5-6):495-500. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10218637">PubMed</a></p>

    <p align="justify">Kaplan,
      H. S., and Robson, A. J. (2002). &quot;The emergence of humans: the coevolution
      of intelligence and longevity with intergenerational transfers.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>99</b>(15):10221-10226. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12122210">PubMed</a></p>

    <p align="justify">Kara,
      T. C. (1994). &quot;Ageing in amphibians.&quot; <i>Gerontology</i> <b>40</b>(2-4):161-173.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7926854">PubMed</a></p>

    <p align="justify">Karakashian,
      S. J., Lanners, H. N., and Rudzinska, M. A. (1984). &quot;Cellular and clonal
      aging in the suctorian protozoan Tokophrya infusionum.&quot; <i>Mech Ageing Dev</i>
      <b>26</b>(2-3):217-229. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6482520">PubMed</a></p>

    <p align="justify">Karasek,
      M. (2004). &quot;Melatonin, human aging, and age-related diseases.&quot; <i>Exp
        Gerontol</i> <b>39</b>(11-12):1723-1729. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15582288">PubMed</a></p>

    <p align="justify">Karatza,
      C., Stein, W. D., and Shall, S. (1984). &quot;Kinetics of in vitro ageing of
      mouse embryo fibroblasts.&quot; <i>J Cell Sci</i> <b>65</b>:163-175. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/6715422">PubMed</a></p>

    <p align="justify">Karlseder,
      J., Broccoli, D., Dai, Y., Hardy, S., and de Lange, T. (1999). &quot;p53- and
      ATM-dependent apoptosis induced by telomeres lacking TRF2.&quot; <i>Science</i>
      <b>283</b>(5406):1321-1325. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10037601">PubMed</a></p>

    <p align="justify">Karlseder,
      J., Smogorzewska, A., and de Lange, T. (2002). &quot;Senescence induced by
      altered telomere state, not telomere loss.&quot; <i>Science</i> <b>295</b>(5564):2446-2449.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11923537">PubMed</a></p>

    <p align="justify">Katsimpardi,
      L., Litterman, N. K., Schein, P. A., Miller, C. M., Loffredo, F. S.,
      Wojtkiewicz, G. R., Chen, J. W., Lee, R. T., Wagers, A. J., and Rubin, L. L.
      (2014). &quot;Vascular and neurogenic rejuvenation of the aging mouse brain by
      young systemic factors.&quot; <i>Science</i> <b>344</b>(6184):630-634.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/24797482">PubMed</a></p>

    <p align="justify">Kay,
      M. A. (2011). &quot;State-of-the-art gene-based therapies: the road
      ahead.&quot; <i>Nat Rev Genet</i> <b>12</b>(5):316-328. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21468099">PubMed</a>
    </p>

    <p align="justify">Kealy,
      R. D., Lawler, D. F., Ballam, J. M., Mantz, S. L., Biery, D. N., Greeley, E.
      H., Lust, G., Segre, M., Smith, G. K., and Stowe, H. D. (2002). &quot;Effects
      of diet restriction on life span and age-related changes in dogs.&quot; <i>J Am
        Vet Med Assoc</i> <b>220</b>(9):1315-1320. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11991408">PubMed</a></p>

    <p align="justify">Keaney,
      M., and Gems, D. (2003). &quot;No increase in lifespan in Caenorhabditis
      elegans upon treatment with the superoxide dismutase mimetic EUK-8.&quot; <i>Free
        Radic Biol Med</i> <b>34</b>(2):277-282. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12521609">PubMed</a></p>

    <p align="justify">Keaney,
      M., Matthijssens, F., Sharpe, M., Vanfleteren, J., and Gems, D. (2004).
      &quot;Superoxide dismutase mimetics elevate superoxide dismutase activity in
      vivo but do not retard aging in the nematode Caenorhabditis elegans.&quot; <i>Free
        Radic Biol Med</i> <b>37</b>(2):239-250. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15203195">PubMed</a></p>

    <p align="justify">Kennedy,
      S. R., Salk, J. J., Schmitt, M. W., and Loeb, L. A. (2013).
      &quot;Ultra-sensitive sequencing reveals an age-related increase in somatic
      mitochondrial mutations that are inconsistent with oxidative damage.&quot; <i>PLoS
        Genet</i> <b>9</b>(9):e1003794. <a href="http://www.ncbi.nlm.nih.gov/pubmed/24086148">PubMed</a></p>

    <p align="justify">Kenyon,
      C. J. (2010). &quot;The genetics of ageing.&quot; <i>Nature</i> <b>464</b>(7288):504-512.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20336132">PubMed</a></p>

    <p align="justify">Keyes,
      W. M., Wu, Y., Vogel, H., Guo, X., Lowe, S. W., and Mills, A. A. (2005).
      &quot;p63 deficiency activates a program of cellular senescence and leads to
      accelerated aging.&quot; <i>Genes Dev</i> <b>19</b>(17):1986-1999. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16107615">PubMed</a></p>

    <p align="justify">Khaidakov,
      M., Heflich, R. H., Manjanatha, M. G., Myers, M. B., and Aidoo, A. (2003).
      &quot;Accumulation of point mutations in mitochondrial DNA of aging mice.&quot;
      <i>Mutat Res</i> <b>526</b>(1-2):1-7. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12714177">PubMed</a></p>

    <p align="justify">Khansari,
      D. N., and Gustad, T. (1991). &quot;Effects of long-term, low-dose growth
      hormone therapy on immune function and life expectancy of mice.&quot; <i>Mech
        Ageing Dev</i> <b>57</b>(1):87-100. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2002700">PubMed</a></p>

    <p align="justify">Khaw,
      K. T., Wareham, N., Bingham, S., Welch, A., Luben, R., and Day, N. (2008).
      &quot;Combined impact of health behaviours and mortality in men and women: the
      EPIC-Norfolk prospective population study.&quot; <i>PLoS Med</i> <b>5</b>(1):e12.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18184033">PubMed</a></p>

    <p align="justify">Kiecolt-Glaser,
      J. K., Preacher, K. J., MacCallum, R. C., Atkinson, C., Malarkey, W. B., and
      Glaser, R. (2003). &quot;Chronic stress and age-related increases in the
      proinflammatory cytokine IL-6.&quot; <i>Proc Natl Acad Sci U S A</i> <b>100</b>(15):9090-9095.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12840146">PubMed</a></p>

    <p align="justify">Kilian,
      A., Bowtell, D. D., Abud, H. E., Hime, G. R., Venter, D. J., Keese, P. K.,
      Duncan, E. L., Reddel, R. R., and Jefferson, R. A. (1997). &quot;Isolation of a
      candidate human telomerase catalytic subunit gene, which reveals complex
      splicing patterns in different cell types.&quot; <i>Hum Mol Genet</i> <b>6</b>(12):2011-2019.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9328464">PubMed</a></p>

    <p align="justify">Kill,
      I. R., Faragher, R. G., Lawrence, K., and Shall, S. (1994). &quot;The
      expression of proliferation-dependent antigens during the lifespan of normal
      and progeroid human fibroblasts in culture.&quot; <i>J Cell Sci</i> <b>107 ( Pt
        2)</b>:571-579. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7911472">PubMed</a></p>

    <p align="justify">Kim,
      H., You, S., Farris, J., Kong, B. W., Christman, S. A., Foster, L. K., and
      Foster, D. N. (2002). &quot;Expression profiles of p53-, p16(INK4a)-, and
      telomere-regulating genes in replicative senescent primary human, mouse, and
      chicken fibroblast cells.&quot; <i>Exp Cell Res</i> <b>272</b>(2):199-208.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11777345">PubMed</a></p>

    <p align="justify">Kim,
      E. B., Fang, X., Fushan, A. A., Huang, Z., Lobanov, A. V., Han, L., Marino, S.
      M., Sun, X., Turanov, A. A., Yang, P.<i>, et al.</i> (2011). &quot;Genome
      sequencing reveals insights into physiology and longevity of the naked mole
      rat.&quot; <i>Nature</i> <b>479</b>(7372):223-227. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/21993625">PubMed</a></p>

    <p align="justify">King,
      D. E., Mainous, A. G., 3rd, and Geesey, M. E. (2007). &quot;Turning back the
      clock: adopting a healthy lifestyle in middle age.&quot; <i>Am J Med</i> <b>120</b>(7):598-603.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17602933">PubMed</a></p>

    <p align="justify">Kipling,
      D., and Cooke, H. J. (1990). &quot;Hypervariable ultra-long telomeres in
      mice.&quot; <i>Nature</i> <b>347</b>(6291):400-402. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2170845">PubMed</a></p>

    <p align="justify">Kirkwood,
      T. B. (1977). &quot;Evolution of ageing.&quot; <i>Nature</i> <b>270</b>(5635):301-304.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/593350">PubMed</a></p>

    <p align="justify">Kirkwood,
      T. B., and Austad, S. N. (2000). &quot;Why do we age?&quot; <i>Nature</i> <b>408</b>(6809):233-238.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11089980">PubMed</a></p>

    <p align="justify">Kirkwood,
      T. B. (2005). &quot;Understanding the odd science of aging.&quot; <i>Cell</i> <b>120</b>(4):437-447.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15734677">PubMed</a></p>

    <p align="justify">Kitano,
      H. (2002). &quot;Computational systems biology.&quot; <i>Nature</i> <b>420</b>(6912):206-210.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12432404">PubMed</a></p>

    <p align="justify">Kiviniemi,
      T. O., Saraste, A., Toikka, J. O., Saraste, M., Raitakari, O. T., Parkka, J.
      P., Lehtimaki, T., Hartiala, J. J., Viikari, J., and Koskenvuo, J. W. (2007).
      &quot;A moderate dose of red wine, but not de-alcoholized red wine increases
      coronary flow reserve.&quot; <i>Atherosclerosis</i>  <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/17662293">PubMed</a></p>

    <p align="justify">Kiyono,
      T., Foster, S. A., Koop, J. I., McDougall, J. K., Galloway, D. A., and
      Klingelhutz, A. J. (1998). &quot;Both Rb/p16INK4a inactivation and telomerase
      activity are required to immortalize human epithelial cells.&quot; <i>Nature</i>
      <b>396</b>(6706):84-88. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9817205">PubMed</a></p>

    <p align="justify">Klapper,
      W., Kuhne, K., Singh, K. K., Heidorn, K., Parwaresch, R., and Krupp, G. (1998).
      &quot;Longevity of lobsters is linked to ubiquitous telomerase
      expression.&quot; <i>FEBS Lett</i> <b>439</b>(1-2):143-146. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9849895">PubMed</a>
    </p>

    <p align="justify">Klass,
      M., and Hirsh, D. (1976). &quot;Non-ageing developmental variant of
      Caenorhabditis elegans.&quot; <i>Nature</i> <b>260</b>(5551):523-525. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/1264206">PubMed</a></p>

    <p align="justify">Kleiber,
      M. (1975). &quot;Metabolic turnover rate: a physiological meaning of the
      metabolic rate per unit body weight.&quot; <i>J Theor Biol</i> <b>53</b>(1):199-204.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1195755">PubMed</a></p>

    <p align="justify">Knight,
      S., Vulliamy, T., Copplestone, A., Gluckman, E., Mason, P., and Dokal, I.
      (1998). &quot;Dyskeratosis Congenita (DC) Registry: identification of new
      features of DC.&quot; <i>Br J Haematol</i> <b>103</b>(4):990-996. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9886310">PubMed</a></p>

    <p align="justify">Knowler,
      W. C., Barrett-Connor, E., Fowler, S. E., Hamman, R. F., Lachin, J. M., Walker,
      E. A., and Nathan, D. M. (2002). &quot;Reduction in the incidence of type 2
      diabetes with lifestyle intervention or metformin.&quot; <i>N Engl J Med</i> <b>346</b>(6):393-403.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11832527">PubMed</a></p>

    <p align="justify">Ko,
      L. J., and Prives, C. (1996). &quot;p53: puzzle and paradigm.&quot; <i>Genes
        Dev</i> <b>10</b>(9):1054-1072. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8654922">PubMed</a></p>

    <p align="justify">Kohn,
      D. B., Bauer, G., Rice, C. R., Rothschild, J. C., Carbonaro, D. A., Valdez, P.,
      Hao, Q., Zhou, C., Bahner, I., Kearns, K.<i>, et al.</i> (1999). &quot;A
      clinical trial of retroviral-mediated transfer of a rev-responsive element decoy
      gene into CD34(+) cells from the bone marrow of human immunodeficiency
      virus-1-infected children.&quot; <i>Blood</i> <b>94</b>(1):368-371. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10381536">PubMed</a></p>

    <p align="justify">Kondratov,
      R. V. (2007). &quot;A role of the circadian system and circadian proteins in
      aging.&quot; <i>Ageing Res Rev</i> <b>6</b>(1):12-27. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/17369106">PubMed</a></p>

    <p align="justify">Kopp,
      P. (1998). &quot;Resveratrol, a phytoestrogen found in red wine. A possible
      explanation for the conundrum of the 'French paradox'?&quot; <i>Eur J
        Endocrinol</i> <b>138</b>(6):619-620. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9678525">PubMed</a></p>

    <p align="justify">Kowald,
      A., and Kirkwood, T. B. (1994). &quot;Towards a network theory of ageing: a
      model combining the free radical theory and the protein error theory.&quot; <i>J
        Theor Biol</i> <b>168</b>(1):75-94. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8022192">PubMed</a></p>

    <p align="justify">Krause,
      D. S., Theise, N. D., Collector, M. I., Henegariu, O., Hwang, S., Gardner, R.,
      Neutzel, S., and Sharkis, S. J. (2001). &quot;Multi-organ, multi-lineage
      engraftment by a single bone marrow-derived stem cell.&quot; <i>Cell</i> <b>105</b>(3):369-377.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11348593">PubMed</a></p>

    <p align="justify">Krishnamurthy,
      J., Ramsey, M. R., Ligon, K. L., Torrice, C., Koh, A., Bonner-Weir, S., and
      Sharpless, N. E. (2006). &quot;p16INK4a induces an age-dependent decline in
      islet regenerative potential.&quot; <i>Nature</i> <b>443</b>(7110):453-457.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16957737">PubMed</a></p>

    <p align="justify">Krzisnik,
      C., Kolacio, Z., and Battelino, T. (1999). &quot;The &quot;Little People&quot;
      of the island of Krk - Revisited. Etiology of hypopituitarism revealed.&quot; <i>J
        Endocr Genet</i> <b>1</b>(1):9-19. </p>

    <p align="justify">Ku,
      H. H., Brunk, U. T., and Sohal, R. S. (1993). &quot;Relationship between
      mitochondrial superoxide and hydrogen peroxide production and longevity of
      mammalian species.&quot; <i>Free Radic Biol Med</i> <b>15</b>(6):621-627.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8138188">PubMed</a></p>

    <p align="justify">Kubota,
      C., Yamakuchi, H., Todoroki, J., Mizoshita, K., Tabara, N., Barber, M., and
      Yang, X. (2000). &quot;Six cloned calves produced from adult fibroblast cells
      after long-term culture.&quot; <i>Proc Natl Acad Sci U S A</i> <b>97</b>(3):990-995.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10655472">PubMed</a></p>

    <p align="justify">Kuilman,
      T., Michaloglou, C., Vredeveld, L. C., Douma, S., van Doorn, R., Desmet, C. J.,
      Aarden, L. A., Mooi, W. J., and Peeper, D. S. (2008). &quot;Oncogene-induced
      senescence relayed by an interleukin-dependent inflammatory network.&quot; <i>Cell</i>
      <b>133</b>(6):1019-1031. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18555778">PubMed</a></p>

    <p align="justify">Kujoth,
      G. C., Hiona, A., Pugh, T. D., Someya, S., Panzer, K., Wohlgemuth, S. E.,
      Hofer, T., Seo, A. Y., Sullivan, R., Jobling, W. A.<i>, et al.</i> (2005).
      &quot;Mitochondrial DNA mutations, oxidative stress, and apoptosis in mammalian
      aging.&quot; <i>Science</i> <b>309</b>(5733):481-484. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16020738">PubMed</a></p>

    <p align="justify">Kulju,
      K. S., and Lehman, J. M. (1995). &quot;Increased p53 protein associated with
      aging in human diploid fibroblasts.&quot; <i>Exp Cell Res</i> <b>217</b>(2):336-345.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7698234">PubMed</a></p>

    <p align="justify">Kuro-o,
      M., Matsumura, Y., Aizawa, H., Kawaguchi, H., Suga, T., Utsugi, T., Ohyama, Y.,
      Kurabayashi, M., Kaname, T., Kume, E.<i>, et al.</i> (1997). &quot;Mutation of
      the mouse klotho gene leads to a syndrome resembling ageing.&quot; <i>Nature</i>
      <b>390</b>(6655):45-51. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9363890">PubMed</a></p>

    <p align="justify">Kurosu,
      H., Yamamoto, M., Clark, J. D., Pastor, J. V., Nandi, A., Gurnani, P., McGuinness,
      O. P., Chikuda, H., Yamaguchi, M., Kawaguchi, H.<i>, et al.</i> (2005).
      &quot;Suppression of aging in mice by the hormone Klotho.&quot; <i>Science</i> <b>309</b>(5742):1829-1833.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16123266">PubMed</a></p>

    <p align="justify">Kuruvilla,
      F. G., Shamji, A. F., Sternson, S. M., Hergenrother, P. J., and Schreiber, S.
      L. (2002). &quot;Dissecting glucose signalling with diversity-oriented
      synthesis and small-molecule microarrays.&quot; <i>Nature</i> <b>416</b>(6881):653-657.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11948353">PubMed</a></p>

    <p align="justify">Kurz,
      D. J., Decary, S., Hong, Y., and Erusalimsky, J. D. (2000).
      &quot;Senescence-associated (beta)-galactosidase reflects an increase in
      lysosomal mass during replicative ageing of human endothelial cells.&quot; <i>J
        Cell Sci</i> <b>113 ( Pt 20)</b>:3613-3622. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11017877">PubMed</a></p>

    <p align="justify">Kurzweil,
      R., and Grossman, T. (2004). <i>Fantastic Voyage: Live Long Enough to Live
        Forever</i>. Rodale, Emmaus, PA.</p>

    <p align="justify">Kwon,
      Y. W., Masutani, H., Nakamura, H., Ishii, Y., and Yodoi, J. (2003). &quot;Redox
      regulation of cell growth and cell death.&quot; <i>Biol Chem</i> <b>384</b>(7):991-996.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12956415">PubMed</a></p>

    <p align="justify">Lagouge,
      M., Argmann, C., Gerhart-Hines, Z., Meziane, H., Lerin, C., Daussin, F.,
      Messadeq, N., Milne, J., Lambert, P., Elliott, P.<i>, et al.</i> (2006).
      &quot;Resveratrol improves mitochondrial function and protects against
      metabolic disease by activating SIRT1 and PGC-1alpha.&quot; <i>Cell</i> <b>127</b>(6):1109-1122.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17112576">PubMed</a></p>

    <p align="justify">Lakdawalla,
      D. N., Sun, E. C., Jena, A. B., Reyes, C. M., Goldman, D. P., and Philipson, T.
      J. (2010). &quot;An economic evaluation of the war on cancer.&quot; <i>J Health
        Econ</i> <b>29</b>(3):333-346. <a href="http://www.ncbi.nlm.nih.gov/pubmed/20363520">PubMed</a></p>

    <p align="justify">Lakowski,
      B., and Hekimi, S. (1996). &quot;Determination of life-span in Caenorhabditis
      elegans by four clock genes.&quot; <i>Science</i> <b>272</b>(5264):1010-1013.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8638122">PubMed</a></p>

    <p align="justify">Lambert,
      A. J., Boysen, H. M., Buckingham, J. A., Yang, T., Podlutsky, A., Austad, S.
      N., Kunz, T. H., Buffenstein, R., and Brand, M. D. (2007). &quot;Low rates of
      hydrogen peroxide production by isolated heart mitochondria associate with long
      maximum lifespan in vertebrate homeotherms.&quot; <i>Aging Cell</i> <b>6</b>(5):607-618.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17596208">PubMed</a></p>

    <p align="justify">Lane,
      I. W., and Comac, L. (1992). <i>Sharks Don't Get Cancer</i>. Avery Penguin
      Putnam, New York.</p>

    <p align="justify">Lanna,
      A., Henson, S. M., Escors, D., and Akbar, A. N. (2014). &quot;The kinase p38
      activated by the metabolic regulator AMPK and scaffold TAB1 drives the
      senescence of human T cells.&quot; <i>Nat Immunol</i> <b>15</b>(10):965-972.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/25151490">PubMed</a></p>

    <p align="justify">Lanner,
      R. M., and Connor, K. F. (2001). &quot;Does bristlecone pine senesce?&quot; <i>Exp
        Gerontol</i> <b>36</b>(4-6):675-685. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295507">PubMed</a></p>

    <p align="justify">Lanza,
      R. P., Cibelli, J. B., and West, M. D. (1999). &quot;Human therapeutic
      cloning.&quot; <i>Nat Med</i> <b>5</b>(9):975-977. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10470061">PubMed</a></p>

    <p align="justify">Lanza,
      R. P., Cibelli, J. B., Blackwell, C., Cristofalo, V. J., Francis, M. K.,
      Baerlocher, G. M., Mak, J., Schertzer, M., Chavez, E. A., Sawyer, N.<i>, et al.</i>
      (2000). &quot;Extension of cell life-span and telomere length in animals cloned
      from senescent somatic cells.&quot; <i>Science</i> <b>288</b>(5466):665-669.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10784448">PubMed</a></p>

    <p align="justify">Lapasset,
      L., Milhavet, O., Prieur, A., Besnard, E., Babled, A., Ait-Hamou, N., Leschik,
      J., Pellestor, F., Ramirez, J. M., De Vos, J.<i>, et al.</i> (2011).
      &quot;Rejuvenating senescent and centenarian human cells by reprogramming
      through the pluripotent state.&quot; <i>Genes Dev</i> <b>25</b>(21):2248-2253.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22056670">PubMed</a></p>

    <p align="justify">Lapointe,
      J., and Hekimi, S. (2010). &quot;When a theory of aging ages badly.&quot; <i>Cell
        Mol Life Sci</i> <b>67</b>(1):1-8. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19730800">PubMed</a></p>

    <p align="justify">Laron,
      Z. (2005). &quot;Do deficiencies in growth hormone and insulin-like growth
      factor-1 (IGF-1) shorten or prolong longevity?&quot; <i>Mech Ageing Dev</i> <b>126</b>(2):305-307.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15621211">PubMed</a></p>

    <p align="justify">Larsen,
      P. L. (1993). &quot;Aging and resistance to oxidative damage in Caenorhabditis
      elegans.&quot; <i>Proc Natl Acad Sci U S A</i> <b>90</b>(19):8905-8909.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8415630">PubMed</a></p>

    <p align="justify">Larsson,
      N. G. (2010). &quot;Somatic mitochondrial DNA mutations in mammalian
      aging.&quot; <i>Annu Rev Biochem</i> <b>79</b>:683-706. <a href="http://www.ncbi.nlm.nih.gov/pubmed/20350166">PubMed</a>
    </p>

    <p align="justify">Le
      Bourg, E. (2001). &quot;A mini-review of the evolutionary theories of aging. Is
      it the time to accept them?&quot; <i>Demographic Research</i> <b>4</b>(1) </p>

    <p align="justify">Leduc,
      P. R., Wong, M. S., Ferreira, P. M., Groff, R. E., Haslinger, K., Koonce, M.
      P., Lee, W. Y., Love, J. C., McCammon, J. A., Monteiro-Riviere, N. A.<i>, et
        al.</i> (2007). &quot;Towards an in vivo biologically inspired
      nanofactory.&quot; <i>Nat Nanotechnol</i> <b>2</b>(1):3-7. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18654192">PubMed</a>
    </p>

    <p align="justify">Lee,
      H. W., Blasco, M. A., Gottlieb, G. J., Horner, J. W., 2nd, Greider, C. W., and
      DePinho, R. A. (1998). &quot;Essential role of mouse telomerase in highly
      proliferative organs.&quot; <i>Nature</i> <b>392</b>(6676):569-574. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9560153">PubMed</a></p>

    <p align="justify">Lee,
      C. K., Klopp, R. G., Weindruch, R., and Prolla, T. A. (1999). &quot;Gene
      expression profile of aging and its retardation by caloric restriction.&quot; <i>Science</i>
      <b>285</b>(5432):1390-1393. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10464095">PubMed</a></p>

    <p align="justify">Lee,
      R. D. (2003). &quot;Rethinking the evolutionary theory of aging: transfers, not
      births, shape senescence in social species.&quot; <i>Proc Natl Acad Sci U S A</i>
      <b>100</b>(16):9637-9642. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12878733">PubMed</a></p>

    <p align="justify">Lee,
      S., and Schmitt, C. A. (2003). &quot;Chemotherapy response and
      resistance.&quot; <i>Curr Opin Genet Dev</i> <b>13</b>(1):90-96. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12573441">PubMed</a></p>

    <p align="justify">Lee,
      J. W., Beebe, K., Nangle, L. A., Jang, J., Longo-Guess, C. M., Cook, S. A.,
      Davisson, M. T., Sundberg, J. P., Schimmel, P., and Ackerman, S. L. (2006).
      &quot;Editing-defective tRNA synthetase causes protein misfolding and
      neurodegeneration.&quot; <i>Nature</i> <b>443</b>(7107):50-55. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16906134">PubMed</a></p>

    <p align="justify">Lees,
      E. (1995). &quot;Cyclin dependent kinase regulation.&quot; <i>Curr Opin Cell
        Biol</i> <b>7</b>(6):773-780. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8608007">PubMed</a></p>

    <p align="justify">Li,
      Y., and de Magalhaes, J. P. (2013). &quot;Accelerated protein evolution
      analysis reveals genes and pathways associated with the evolution of mammalian
      longevity.&quot; <i>Age (Dordr)</i> <b>35</b>(2):301-314. <a href="http://www.ncbi.nlm.nih.gov/pubmed/22205409">PubMed</a>
    </p>

    <p align="justify">Liang,
      H., Masoro, E. J., Nelson, J. F., Strong, R., McMahan, C. A., and Richardson,
      A. (2003). &quot;Genetic mouse models of extended lifespan.&quot; <i>Exp
        Gerontol</i> <b>38</b>(11-12):1353-1364. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14698816">PubMed</a></p>

    <p align="justify">Liang,
      S., Mele, J., Wu, Y., Buffenstein, R., and Hornsby, P. J. (2010).
      &quot;Resistance to experimental tumorigenesis in cells of a long-lived mammal,
      the naked mole-rat (Heterocephalus glaber).&quot; <i>Aging Cell</i> <b>9</b>(4):626-635.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20550519">PubMed</a></p>

    <p align="justify">Lightowlers,
      R. N., Jacobs, H. T., and Kajander, O. A. (1999). &quot;Mitochondrial DNA--all
      things bad?&quot; <i>Trends Genet</i> <b>15</b>(3):91-93. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10203801">PubMed</a>
    </p>

    <p align="justify">Lin,
      Y. J., Seroude, L., and Benzer, S. (1998). &quot;Extended life-span and stress
      resistance in the Drosophila mutant methuselah.&quot; <i>Science</i> <b>282</b>(5390):943-946.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9794765">PubMed</a></p>

    <p align="justify">Lin,
      S. J., Defossez, P. A., and Guarente, L. (2000). &quot;Requirement of NAD and
      SIR2 for life-span extension by calorie restriction in Saccharomyces
      cerevisiae.&quot; <i>Science</i> <b>289</b>(5487):2126-2128. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11000115">PubMed</a></p>

    <p align="justify">Lin,
      K., Hsin, H., Libina, N., and Kenyon, C. (2001). &quot;Regulation of the
      Caenorhabditis elegans longevity protein DAF-16 by insulin/IGF-1 and germline
      signaling.&quot; <i>Nat Genet</i> <b>28</b>(2):139-145. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11381260">PubMed</a>
    </p>

    <p align="justify">Lindsey,
      J., McGill, N. I., Lindsey, L. A., Green, D. K., and Cooke, H. J. (1991).
      &quot;In vivo loss of telomeric repeats with age in humans.&quot; <i>Mutat Res</i>
      <b>256</b>(1):45-48. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1944386">PubMed</a></p>

    <p align="justify">Lindstedt,
      S. L., and Calder, W. A. (1981). &quot;Body size, physiological time, and
      longevity of homeothermic animals.&quot; <i>Q Rev Biol</i> <b>56</b>(1):1-16. </p>

    <p align="justify">Linke,
      S. P., Sengupta, S., Khabie, N., Jeffries, B. A., Buchhop, S., Miska, S.,
      Henning, W., Pedeux, R., Wang, X. W., Hofseth, L. J.<i>, et al.</i> (2003).
      &quot;p53 interacts with hRAD51 and hRAD54, and directly modulates homologous
      recombination.&quot; <i>Cancer Res</i> <b>63</b>(10):2596-2605. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12750285">PubMed</a></p>

    <p align="justify">Linnane,
      A. W., Marzuki, S., Ozawa, T., and Tanaka, M. (1989). &quot;Mitochondrial DNA
      mutations as an important contributor to ageing and degenerative
      diseases.&quot; <i>Lancet</i> <b>1</b>(8639):642-645. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2564461">PubMed</a></p>

    <p align="justify">Lio,
      D., Scola, L., Crivello, A., Colonna-Romano, G., Candore, G., Bonafe, M.,
      Cavallone, L., Marchegiani, F., Olivieri, F., Franceschi, C.<i>, et al.</i>
      (2003). &quot;Inflammation, genetics, and longevity: further studies on the
      protective effects in men of IL-10 -1082 promoter SNP and its interaction with
      TNF-alpha -308 promoter SNP.&quot; <i>J Med Genet</i> <b>40</b>(4):296-299.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12676903">PubMed</a></p>

    <p align="justify">Lipman,
      R. D., Smith, D. E., Bronson, R. T., and Blumberg, J. (1995). &quot;Is
      late-life caloric restriction beneficial?&quot; <i>Aging (Milano)</i> <b>7</b>(2):136-139.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7548264">PubMed</a></p>

    <p align="justify">Lipman,
      R. D., Bronson, R. T., Wu, D., Smith, D. E., Prior, R., Cao, G., Han, S. N.,
      Martin, K. R., Meydani, S. N., and Meydani, M. (1998). &quot;Disease incidence
      and longevity are unaltered by dietary antioxidant supplementation initiated
      during middle age in C57BL/6 mice.&quot; <i>Mech Ageing Dev</i> <b>103</b>(3):269-284.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9723903">PubMed</a></p>

    <p align="justify">Liu,
      V. W., Zhang, C., and Nagley, P. (1998). &quot;Mutations in mitochondrial DNA
      accumulate differentially in three different human tissues during ageing.&quot;
      <i>Nucleic Acids Res</i> <b>26</b>(5):1268-1275. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9469836">PubMed</a>
    </p>

    <p align="justify">Liu,
      S., Qu, Y., Stewart, T. J., Howard, M. J., Chakrabortty, S., Holekamp, T. F.,
      and McDonald, J. W. (2000). &quot;Embryonic stem cells differentiate into
      oligodendrocytes and myelinate in culture and after spinal cord
      transplantation.&quot; <i>Proc Natl Acad Sci U S A</i> <b>97</b>(11):6126-6131.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10823956">PubMed</a></p>

    <p align="justify">Liu,
      X., Jiang, N., Hughes, B., Bigras, E., Shoubridge, E., and Hekimi, S. (2005).
      &quot;Evolutionary conservation of the clk-1-dependent mechanism of longevity:
      loss of mclk1 increases cellular fitness and lifespan in mice.&quot; <i>Genes
        Dev</i> <b>19</b>(20):2424-2434. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16195414">PubMed</a></p>

    <p align="justify">Liu,
      H., Bravata, D. M., Olkin, I., Nayak, S., Roberts, B., Garber, A. M., and
      Hoffman, A. R. (2007). &quot;Systematic review: the safety and efficacy of
      growth hormone in the healthy elderly.&quot; <i>Ann Intern Med</i> <b>146</b>(2):104-115.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17227934">PubMed</a></p>

    <p align="justify">Loffredo,
      F. S., Steinhauser, M. L., Jay, S. M., Gannon, J., Pancoast, J. R., Yalamanchi,
      P., Sinha, M., Dall'osso, C., Khong, D., Shadrach, J. L.<i>, et al.</i> (2013).
      &quot;Growth Differentiation Factor 11 Is a Circulating Factor that Reverses
      Age-Related Cardiac Hypertrophy.&quot; <i>Cell</i> <b>153</b>(4):828-839.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23663781">PubMed</a></p>

    <p align="justify">Longo,
      V. D. (1999). &quot;Mutations in signal transduction proteins increase stress
      resistance and longevity in yeast, nematodes, fruit flies, and mammalian
      neuronal cells.&quot; <i>Neurobiol Aging</i> <b>20</b>(5):479-486. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10638521">PubMed</a></p>

    <p align="justify">Longo,
      V. D., and Fabrizio, P. (2002). &quot;Regulation of longevity and stress
      resistance: a molecular strategy conserved from yeast to humans?&quot; <i>Cell
        Mol Life Sci</i> <b>59</b>(6):903-908. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12169020">PubMed</a></p>

    <p align="justify">Longo,
      V. D., Mitteldorf, J., and Skulachev, V. P. (2005). &quot;Programmed and
      altruistic ageing.&quot; <i>Nat Rev Genet</i> <b>6</b>(11):866-872. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16304601">PubMed</a></p>

    <p align="justify">Lopez-Otin,
      C., Blasco, M. A., Partridge, L., Serrano, M., and Kroemer, G. (2013).
      &quot;The hallmarks of aging.&quot; <i>Cell</i> <b>153</b>(6):1194-1217.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23746838">PubMed</a></p>

    <p align="justify">Lorenzini,
      A., Tresini, M., Austad, S. N., and Cristofalo, V. J. (2005). &quot;Cellular
      replicative capacity correlates primarily with species body mass not
      longevity.&quot; <i>Mech Ageing Dev</i> <b>126</b>(10):1130-1133. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15993927">PubMed</a></p>

    <p align="justify">Lowell,
      J. E., and Pillus, L. (1998). &quot;Telomere tales: chromatin, telomerase and
      telomere function in Saccharomyces cerevisiae.&quot; <i>Cell Mol Life Sci</i> <b>54</b>(1):32-49.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9487385">PubMed</a></p>

    <p align="justify">Lu,
      T., Pan, Y., Kao, S. Y., Li, C., Kohane, I., Chan, J., and Yankner, B. A.
      (2004). &quot;Gene regulation and DNA damage in the ageing human brain.&quot; <i>Nature</i>
      <b>429</b>(6994):883-891. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15190254">PubMed</a></p>

    <p align="justify">Lucke,
      J. C., Diedrichs, P. C., Partridge, B., and Hall, W. D. (2009).
      &quot;Anticipating the anti-ageing pill. Lessons from the history of the oral
      contraceptive pill and hormone replacement therapy.&quot; <i>EMBO Rep</i> <b>10</b>(2):108-113.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19148223">PubMed</a></p>

    <p align="justify">Luckinbill,
      L. S., and Clare, M. J. (1985). &quot;Selection for life span in Drosophila
      melanogaster.&quot; <i>Heredity</i> <b>55 ( Pt 1)</b>:9-18. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3930429">PubMed</a>
    </p>

    <p align="justify">Lundblad,
      V., and Szostak, J. W. (1989). &quot;A mutant with a defect in telomere
      elongation leads to senescence in yeast.&quot; <i>Cell</i> <b>57</b>(4):633-643.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2655926">PubMed</a></p>

    <p align="justify">Lutz,
      P. L., Prentice, H. M., and Milton, S. L. (2003). &quot;Is turtle longevity
      linked to enhanced mechanisms for surviving brain anoxia and
      reoxygenation?&quot; <i>Exp Gerontol</i> <b>38</b>(7):797-800. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12855290">PubMed</a></p>

    <p align="justify">Ly,
      D. H., Lockhart, D. J., Lerner, R. A., and Schultz, P. G. (2000). &quot;Mitotic
      misregulation and human aging.&quot; <i>Science</i> <b>287</b>(5462):2486-2492.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10741968">PubMed</a></p>

    <p align="justify">Lyman,
      C. P., O'Brien, R. C., Greene, G. C., and Papafrangos, E. D. (1981).
      &quot;Hibernation and longevity in the Turkish hamster Mesocricetus
      brandti.&quot; <i>Science</i> <b>212</b>(4495):668-670. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7221552">PubMed</a>
    </p>

    <p align="justify">Lyon,
      J., and Gorner, P. (1995). <i>Altered Fates: Gene Therapy and the Retooling of
        Human Life</i>. W. W. Norton, New York.</p>

    <p align="justify">MacArthur,
      R. H., and Wilson, E. O. (1967). <i>The Theory of Island Biogeography</i>.
      Princeton University Press, Princeton.</p>

    <p align="justify">Macera-Bloch,
      L., Houghton, J., Lenahan, M., Jha, K. K., and Ozer, H. L. (2002).
      &quot;Termination of lifespan of SV40-transformed human fibroblasts in crisis
      is due to apoptosis.&quot; <i>J Cell Physiol</i> <b>190</b>(3):332-344.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11857449">PubMed</a></p>

    <p align="justify">Maddison,
      D. R., and Schulz, K. S. (2004). &quot;The Tree of Life Web Project.&quot; http://tolweb.org</p>

    <p align="justify">Magalhaes,
      J. P. (1999). &quot;Post-mortem lethal genes.&quot; <i>Longevity Report</i>
      (69):6. </p>

    <p align="justify">Majhi,
      S., Jena, B. S., and Patnaik, B. K. (2000). &quot;Effect of age on lipid
      peroxides, lipofuscin and ascorbic acid contents of the lungs of male garden
      lizard.&quot; <i>Comp Biochem Physiol C Toxicol Pharmacol</i> <b>126</b>(3):293-298.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11048679">PubMed</a></p>

    <p align="justify">Makarov,
      V. L., Hirose, Y., and Langmore, J. P. (1997). &quot;Long G tails at both ends
      of human chromosomes suggest a C strand degradation mechanism for telomere
      shortening.&quot; <i>Cell</i> <b>88</b>(5):657-666. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9054505">PubMed</a></p>

    <p align="justify">Mangel,
      M., and Abrahams, M. V. (2001). &quot;Age and longevity in fish, with
      consideration of the ferox trout.&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):765-790.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295513">PubMed</a></p>

    <p align="justify">Mannick,
      J. B., Del Giudice, G., Lattanzi, M., Valiante, N. M., Praestgaard, J., Huang,
      B., Lonetto, M. A., Maecker, H. T., Kovarik, J., Carson, S.<i>, et al.</i>
      (2014). &quot;mTOR inhibition improves immune function in the elderly.&quot; <i>Sci
        Transl Med</i> <b>6</b>(268):268ra179. <a href="http://www.ncbi.nlm.nih.gov/pubmed/25540326">PubMed</a></p>

    <p align="justify">Mantell,
      L. L., and Greider, C. W. (1994). &quot;Telomerase activity in germline and
      embryonic cells of Xenopus.&quot; <i>Embo J</i> <b>13</b>(13):3211-3217.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8039513">PubMed</a></p>

    <p align="justify">Marden,
      J. H., Rogina, B., Montooth, K. L., and Helfand, S. L. (2003).
      &quot;Conditional tradeoffs between aging and organismal performance of Indy
      long-lived mutant flies.&quot; <i>Proc Natl Acad Sci U S A</i> <b>100</b>(6):3369-3373.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12626742">PubMed</a></p>

    <p align="justify">Marrone,
      A., and Mason, P. J. (2003). &quot;Dyskeratosis congenita.&quot; <i>Cell Mol
        Life Sci</i> <b>60</b>(3):507-517. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12737310">PubMed</a></p>

    <p align="justify">Marsili,
      S., Salganik, R. I., Albright, C. D., Freel, C. D., Johnsen, S., Peiffer, R.
      L., and Costello, M. J. (2004). &quot;Cataract formation in a strain of rats
      selected for high oxidative stress.&quot; <i>Exp Eye Res</i> <b>79</b>(5):595-612.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15500819">PubMed</a></p>

    <p align="justify">Martens,
      U. M., Chavez, E. A., Poon, S. S., Schmoor, C., and Lansdorp, P. M. (2000).
      &quot;Accumulation of short telomeres in human fibroblasts prior to replicative
      senescence.&quot; <i>Exp Cell Res</i> <b>256</b>(1):291-299. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10739676">PubMed</a></p>

    <p align="justify">Martin,
      G. M. (1978). &quot;Genetic syndromes in man with potential relevance to the
      pathobiology of aging.&quot; <i>Birth Defects Orig Artic Ser</i> <b>14</b>(1):5-39.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/147113">PubMed</a></p>

    <p align="justify">Martin,
      G. M. (1982). &quot;Syndromes of accelerated aging.&quot; <i>Natl Cancer Inst
        Monogr</i> <b>60</b>:241-247. <a href="http://www.ncbi.nlm.nih.gov/pubmed/6214719">PubMed</a></p>

    <p align="justify">Martin,
      G. M., Smith, A. C., Ketterer, D. J., Ogburn, C. E., and Disteche, C. M.
      (1985). &quot;Increased chromosomal aberrations in first metaphases of cells
      isolated from the kidneys of aged mice.&quot; <i>Isr J Med Sci</i> <b>21</b>(3):296-301.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/3997491">PubMed</a></p>

    <p align="justify">Martin,
      G. M., Austad, S. N., and Johnson, T. E. (1996). &quot;Genetic analysis of
      ageing: role of oxidative damage and environmental stresses.&quot; <i>Nat Genet</i>
      <b>13</b>(1):25-34. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8673100">PubMed</a></p>

    <p align="justify">Martin,
      G. M., and Oshima, J. (2000). &quot;Lessons from human progeroid
      syndromes.&quot; <i>Nature</i> <b>408</b>(6809):263-266. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11089984">PubMed</a>
    </p>

    <p align="justify">Martin,
      K., Potten, C. S., and Kirkwood, T. B. (2000). &quot;Age-related changes in
      irradiation-induced apoptosis and expression of p21 and p53 in crypt stem cells
      of murine intestine.&quot; <i>Ann N Y Acad Sci</i> <b>908</b>:315-318.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911974">PubMed</a></p>

    <p align="justify">Martin,
      J. A., and Buckwalter, J. A. (2002). &quot;Human chondrocyte senescence and
      osteoarthritis.&quot; <i>Biorheology</i> <b>39</b>(1-2):145-152. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12082277">PubMed</a></p>

    <p align="justify">Martinez,
      D. E. (1998). &quot;Mortality patterns suggest lack of senescence in
      hydra.&quot; <i>Exp Gerontol</i> <b>33</b>(3):217-225. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9615920">PubMed</a></p>

    <p align="justify">Martin-Montalvo,
      A., Mercken, E. M., Mitchell, S. J., Palacios, H. H., Mote, P. L.,
      Scheibye-Knudsen, M., Gomes, A. P., Ward, T. M., Minor, R. K., Blouin, M. J.<i>,
        et al.</i> (2013). &quot;Metformin improves healthspan and lifespan in
      mice.&quot; <i>Nat Commun</i> <b>4</b>:2192. <a href="http://www.ncbi.nlm.nih.gov/pubmed/23900241">PubMed</a></p>

    <p align="justify">Mason,
      P. J. (2003). &quot;Stem cells, telomerase and dyskeratosis congenita.&quot; <i>Bioessays</i>
      <b>25</b>(2):126-133. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12539238">PubMed</a></p>

    <p align="justify">Mason,
      J. B., Cargill, S. L., Anderson, G. B., and Carey, J. R. (2009).
      &quot;Transplantation of young ovaries to old mice increased life span in
      transplant recipients.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>64</b>(12):1207-1211.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19776215">PubMed</a></p>

    <p align="justify">Masoro,
      E. J., McCarter, R. J., Katz, M. S., and McMahan, C. A. (1992). &quot;Dietary
      restriction alters characteristics of glucose fuel use.&quot; <i>J Gerontol</i>
      <b>47</b>(6):B202-208. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1430849">PubMed</a></p>

    <p align="justify">Masoro,
      E. J. (2005). &quot;Overview of caloric restriction and ageing.&quot; <i>Mech
        Ageing Dev</i> <b>126</b>(9):913-922. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15885745">PubMed</a></p>

    <p align="justify">Masutomi,
      K., Yu, E. Y., Khurts, S., Ben-Porath, I., Currier, J. L., Metz, G. B., Brooks,
      M. W., Kaneko, S., Murakami, S., DeCaprio, J. A.<i>, et al.</i> (2003).
      &quot;Telomerase maintains telomere structure in normal human cells.&quot; <i>Cell</i>
      <b>114</b>(2):241-253. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12887925">PubMed</a></p>

    <p align="justify">Matheu,
      A., Pantoja, C., Efeyan, A., Criado, L. M., Martin-Caballero, J., Flores, J.
      M., Klatt, P., and Serrano, M. (2004). &quot;Increased gene dosage of Ink4a/Arf
      results in cancer resistance and normal aging.&quot; <i>Genes Dev</i> <b>18</b>(22):2736-2746.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15520276">PubMed</a></p>

    <p align="justify">Matheu,
      A., Maraver, A., Klatt, P., Flores, I., Garcia-Cao, I., Borras, C., Flores, J.
      M., Vina, J., Blasco, M. A., and Serrano, M. (2007). &quot;Delayed ageing
      through damage protection by the Arf/p53 pathway.&quot; <i>Nature</i> <b>448</b>(7151):375-379.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17637672">PubMed</a></p>

    <p align="justify">Mathon,
      N. F., and Lloyd, A. C. (2001). &quot;Cell senescence and cancer.&quot; <i>Nat
        Rev Cancer</i> <b>1</b>(3):203-213. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11902575">PubMed</a></p>

    <p align="justify">Mathon,
      N. F., Malcolm, D. S., Harrisingh, M. C., Cheng, L., and Lloyd, A. C. (2001).
      &quot;Lack of replicative senescence in normal rodent glia.&quot; <i>Science</i>
      <b>291</b>(5505):872-875. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11157166">PubMed</a></p>

    <p align="justify">Matsumura,
      T. (1980). &quot;Multinucleation and polyploidization of aging human cells in
      culture.&quot; <i>Adv Exp Med Biol</i> <b>129</b>:31-38. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7395634">PubMed</a>
    </p>

    <p align="justify">Matsuura,
      S., Tauchi, H., Nakamura, A., Kondo, N., Sakamoto, S., Endo, S., Smeets, D.,
      Solder, B., Belohradsky, B. H., Der Kaloustian, V. M.<i>, et al.</i> (1998).
      &quot;Positional cloning of the gene for Nijmegen breakage syndrome.&quot; <i>Nat
        Genet</i> <b>19</b>(2):179-181. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9620777">PubMed</a></p>

    <p align="justify">Mattison,
      J. A., Roth, G. S., Beasley, T. M., Tilmont, E. M., Handy, A. M., Herbert, R.
      L., Longo, D. L., Allison, D. B., Young, J. E., Bryant, M.<i>, et al.</i>
      (2012). &quot;Impact of caloric restriction on health and survival in rhesus
      monkeys from the NIA study.&quot; <i>Nature</i> <b>489</b>(7415):318-321.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22932268">PubMed</a></p>

    <p align="justify">Mattson,
      M. P., Duan, W., and Maswood, N. (2002). &quot;How does the brain control
      lifespan?&quot; <i>Ageing Res Rev</i> <b>1</b>(2):155-165. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12039436">PubMed</a>
    </p>

    <p align="justify">Mattson,
      M. P., Cutler, R. G., and Camandola, S. (2007). &quot;Energy intake and
      amyotrophic lateral sclerosis.&quot; <i>Neuromolecular Med</i> <b>9</b>(1):17-20.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17114821">PubMed</a></p>

    <p align="justify">McCarter,
      R. J., and Palmer, J. (1992). &quot;Energy metabolism and aging: a lifelong
      study of Fischer 344 rats.&quot; <i>Am J Physiol</i> <b>263</b>(3 Pt
      1):E448-452. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1415524">PubMed</a></p>

    <p align="justify">McCay,
      C. M., Crowell, M. F., and Maynard, L. A. (1935). &quot;The effect of retarded
      growth upon length of the life span and upon the ultimate body size.&quot; <i>J
        Nutrit</i> <b>10</b>(1):63-75. </p>

    <p align="justify">McClintock,
      B. (1941). &quot;The stability of broken ends of chromosomes in Zea mays.&quot;
      <i>Genetics</i> <b>26</b>:234-282. </p>

    <p align="justify">McConnell,
      B. B., Starborg, M., Brookes, S., and Peters, G. (1998). &quot;Inhibitors of
      cyclin-dependent kinases induce features of replicative senescence in early
      passage human diploid fibroblasts.&quot; <i>Curr Biol</i> <b>8</b>(6):351-354.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9512419">PubMed</a></p>

    <p align="justify">McCormick,
      A., and Campisi, J. (1991). &quot;Cellular aging and senescence.&quot; <i>Curr
        Opin Cell Biol</i> <b>3</b>(2):230-234. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1883615">PubMed</a></p>

    <p align="justify">McEachern,
      M. J., and Blackburn, E. H. (1996). &quot;Cap-prevented recombination between
      terminal telomeric repeat arrays (telomere CPR) maintains telomeres in
      Kluyveromyces lactis lacking telomerase.&quot; <i>Genes Dev</i> <b>10</b>(14):1822-1834.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8698241">PubMed</a></p>

    <p align="justify">McElwee,
      J. J., Schuster, E., Blanc, E., Piper, M. D., Thomas, J. H., Patel, D. S.,
      Selman, C., Withers, D. J., Thornton, J. M., Partridge, L.<i>, et al.</i>
      (2007). &quot;Evolutionary conservation of regulated longevity assurance
      mechanisms.&quot; <i>Genome Biol</i> <b>8</b>(7):R132. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17612391">PubMed</a>
    </p>

    <p align="justify">McKinney,
      M. L., and McNamara, K. J. (1991). <i>Heterochrony: The Evolution of Ontogeny</i>.
      Plenum, New York.</p>

    <p align="justify">Medawar,
      P. B. (1952). <i>An Unsolved Problem of Biology</i>. H. K. Lewis, London.</p>

    <p align="justify">Medawar,
      P. (1955). &quot;The definition and measurement of senescence.&quot; In: <i>Ciba
        Foundation Colloquia on Ageing, Volume I</i>, Wolstenholme, G. E. W. (ed.).  J.
      &amp; A. Churchill, London, 4-15.</p>

    <p align="justify">Medema,
      R. H., Herrera, R. E., Lam, F., and Weinberg, R. A. (1995). &quot;Growth
      suppression by p16ink4 requires functional retinoblastoma protein.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>92</b>(14):6289-6293. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7603984">PubMed</a>
    </p>

    <p align="justify">Medvedev,
      Z. A. (1981). &quot;On the immortality of the germ line: genetic and
      biochemical mechanism. A review.&quot; <i>Mech Ageing Dev</i> <b>17</b>(4):331-359.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6173551">PubMed</a></p>

    <p align="justify">Medvedev,
      Z. A. (1990). &quot;An attempt at a rational classification of theories of
      ageing.&quot; <i>Biol Rev Camb Philos Soc</i> <b>65</b>(3):375-398. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/2205304">PubMed</a></p>

    <p align="justify">Melk,
      A., Kittikowit, W., Sandhu, I., Halloran, K. M., Grimm, P., Schmidt, B. M., and
      Halloran, P. F. (2003). &quot;Cell senescence in rat kidneys in vivo increases
      with growth and age despite lack of telomere shortening.&quot; <i>Kidney Int</i>
      <b>63</b>(6):2134-2143. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12753300">PubMed</a></p>

    <p align="justify">Melov,
      S., Ravenscroft, J., Malik, S., Gill, M. S., Walker, D. W., Clayton, P. E.,
      Wallace, D. C., Malfroy, B., Doctrow, S. R., and Lithgow, G. J. (2000).
      &quot;Extension of life-span with superoxide dismutase/catalase mimetics.&quot;
      <i>Science</i> <b>289</b>(5484):1567-1569. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10968795">PubMed</a></p>

    <p align="justify">Mendez,
      M. V., Stanley, A., Phillips, T., Murphy, M., Menzoian, J. O., and Park, H. Y.
      (1998). &quot;Fibroblasts cultured from distal lower extremities in patients
      with venous reflux display cellular characteristics of senescence.&quot; <i>J
        Vasc Surg</i> <b>28</b>(6):1040-1050. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9845655">PubMed</a></p>

    <p align="justify">Mercken,
      E. M., Mitchell, S. J., Martin-Montalvo, A., Minor, R. K., Almeida, M., Gomes,
      A. P., Scheibye-Knudsen, M., Palacios, H. H., Licata, J. J., Zhang, Y.<i>, et
        al.</i> (2014). &quot;SRT2104 extends survival of male mice on a standard diet
      and preserves bone and muscle mass.&quot; <i>Aging Cell</i> <b>13</b>(5):787-796.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/24931715">PubMed</a></p>

    <p align="justify">Meyer,
      T. E., Kovacs, S. J., Ehsani, A. A., Klein, S., Holloszy, J. O., and Fontana,
      L. (2006). &quot;Long-term caloric restriction ameliorates the decline in
      diastolic function in humans.&quot; <i>J Am Coll Cardiol</i> <b>47</b>(2):398-402.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16412867">PubMed</a></p>

    <p align="justify">Meyne,
      J., Ratliff, R. L., and Moyzis, R. K. (1989). &quot;Conservation of the human
      telomere sequence (TTAGGG)n among vertebrates.&quot; <i>Proc Natl Acad Sci U S
        A</i> <b>86</b>(18):7049-7053. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2780561">PubMed</a></p>

    <p align="justify">Migliaccio,
      E., Giorgio, M., Mele, S., Pelicci, G., Reboldi, P., Pandolfi, P. P.,
      Lanfrancone, L., and Pelicci, P. G. (1999). &quot;The p66shc adaptor protein
      controls oxidative stress response and life span in mammals.&quot; <i>Nature</i>
      <b>402</b>(6759):309-313. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10580504">PubMed</a></p>

    <p align="justify">Miller,
      S. E., and Hadfield, M. G. (1990). &quot;Developmental arrest during larval
      life and life-span extension in a marine mollusc.&quot; <i>Science</i> <b>248</b>(4953):356-358.
    </p>

    <p align="justify">Miller,
      R. A. (1999). &quot;Kleemeier award lecture: are there genes for aging?&quot; <i>J
        Gerontol A Biol Sci Med Sci</i> <b>54</b>(7):B297-307. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10462163">PubMed</a>
    </p>

    <p align="justify">Miller,
      J. K. (2001). &quot;Escaping senescence: demographic data from the three-toed
      box turtle (Terrapene carolina triunguis).&quot; <i>Exp Gerontol</i> <b>36</b>(4-6):829-832.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295516">PubMed</a></p>

    <p align="justify">Miller,
      R. A., Harper, J. M., Galecki, A., and Burke, D. T. (2002a). &quot;Big mice die
      young: early life body weight predicts longevity in genetically heterogeneous
      mice.&quot; <i>Aging Cell</i> <b>1</b>(1):22-29. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12882350">PubMed</a>
    </p>

    <p align="justify">Miller,
      R. A., Harper, J. M., Dysko, R. C., Durkee, S. J., and Austad, S. N. (2002b).
      &quot;Longer life spans and delayed maturation in wild-derived mice.&quot; <i>Exp
        Biol Med (Maywood)</i> <b>227</b>(7):500-508. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12094015">PubMed</a>
    </p>

    <p align="justify">Miller,
      R. A. (2005). &quot;Genetic approaches to the study of aging.&quot; <i>J Am
        Geriatr Soc</i> <b>53</b>(9 Suppl):S284-286. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16131353">PubMed</a>
    </p>

    <p align="justify">Miller,
      R. A., Harrison, D. E., Astle, C. M., Baur, J. A., Boyd, A. R., de Cabo, R.,
      Fernandez, E., Flurkey, K., Javors, M. A., Nelson, J. F.<i>, et al.</i> (2011).
      &quot;Rapamycin, but not resveratrol or simvastatin, extends life span of
      genetically heterogeneous mice.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>66</b>(2):191-201.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20974732">PubMed</a></p>

    <p align="justify">Milne,
      J. C., Lambert, P. D., Schenk, S., Carney, D. P., Smith, J. J., Gagne, D. J.,
      Jin, L., Boss, O., Perni, R. B., Vu, C. B.<i>, et al.</i> (2007). &quot;Small
      molecule activators of SIRT1 as therapeutics for the treatment of type 2
      diabetes.&quot; <i>Nature</i> <b>450</b>(7170):712-716. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18046409">PubMed</a>
    </p>

    <p align="justify">Minamino,
      T., Miyauchi, H., Yoshida, T., Ishida, Y., Yoshida, H., and Komuro, I. (2002).
      &quot;Endothelial cell senescence in human atherosclerosis: role of telomere in
      endothelial dysfunction.&quot; <i>Circulation</i> <b>105</b>(13):1541-1544.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11927518">PubMed</a></p>

    <p align="justify">Minamino,
      T., Yoshida, T., Tateno, K., Miyauchi, H., Zou, Y., Toko, H., and Komuro, I.
      (2003). &quot;Ras induces vascular smooth muscle cell senescence and
      inflammation in human atherosclerosis.&quot; <i>Circulation</i> <b>108</b>(18):2264-2269.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14557365">PubMed</a></p>

    <p align="justify">Miskin,
      R., and Masos, T. (1997). &quot;Transgenic mice overexpressing urokinase-type
      plasminogen activator in the brain exhibit reduced food consumption, body
      weight and size, and increased longevity.&quot; <i>J Gerontol A Biol Sci Med
        Sci</i> <b>52</b>(2):B118-124. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9060969">PubMed</a></p>

    <p align="justify">Mitchell,
      S. J., Martin-Montalvo, A., Mercken, E. M., Palacios, H. H., Ward, T. M.,
      Abulwerdi, G., Minor, R. K., Vlasuk, G. P., Ellis, J. L., Sinclair, D. A.<i>,
        et al.</i> (2014). &quot;The SIRT1 activator SRT1720 extends lifespan and
      improves health of mice fed a standard diet.&quot; <i>Cell Rep</i> <b>6</b>(5):836-843.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/24582957">PubMed</a></p>

    <p align="justify">Mitsui,
      A., Hamuro, J., Nakamura, H., Kondo, N., Hirabayashi, Y., Ishizaki-Koizumi, S.,
      Hirakawa, T., Inoue, T., and Yodoi, J. (2002). &quot;Overexpression of human
      thioredoxin in transgenic mice controls oxidative stress and life span.&quot; <i>Antioxid
        Redox Signal</i> <b>4</b>(4):693-696. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12230882">PubMed</a></p>

    <p align="justify">Miwa,
      S., Riyahi, K., Partridge, L., and Brand, M. D. (2004). &quot;Lack of
      correlation between mitochondrial reactive oxygen species production and life
      span in Drosophila.&quot; <i>Ann N Y Acad Sci</i> <b>1019</b>:388-391.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15247051">PubMed</a></p>

    <p align="justify">Mizutani,
      K., Ikeda, K., and Yamori, Y. (2000). &quot;Resveratrol inhibits AGEs-induced
      proliferation and collagen synthesis activity in vascular smooth muscle cells
      from stroke-prone spontaneously hypertensive rats.&quot; <i>Biochem Biophys Res
        Commun</i> <b>274</b>(1):61-67. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10903896">PubMed</a></p>

    <p align="justify">Mobbs,
      C. V. (2004). &quot;Not wisely but too well: aging as a cost of neuroendocrine
      activity.&quot; <i>Sci Aging Knowledge Environ</i> <b>2004</b>(35):pe33.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15342923">PubMed</a></p>

    <p align="justify">Mokni,
      M., Elkahoui, S., Limam, F., Amri, M., and Aouani, E. (2007). &quot;Effect of
      resveratrol on antioxidant enzyme activities in the brain of healthy rat.&quot;
      <i>Neurochem Res</i> <b>32</b>(6):981-987. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17401679">PubMed</a></p>

    <p align="justify">Molofsky,
      A. V., Slutsky, S. G., Joseph, N. M., He, S., Pardal, R., Krishnamurthy, J.,
      Sharpless, N. E., and Morrison, S. J. (2006). &quot;Increasing p16INK4a
      expression decreases forebrain progenitors and neurogenesis during
      ageing.&quot; <i>Nature</i> <b>443</b>(7110):448-452. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16957738">PubMed</a></p>

    <p align="justify">Mondello,
      C., Petropoulou, C., Monti, D., Gonos, E. S., Franceschi, C., and Nuzzo, F.
      (1999). &quot;Telomere length in fibroblasts and blood cells from healthy centenarians.&quot;
      <i>Exp Cell Res</i> <b>248</b>(1):234-242. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10094830">PubMed</a></p>

    <p align="justify">Monroe,
      K. R., Murphy, S. P., Kolonel, L. N., and Pike, M. C. (2007). &quot;Prospective
      study of grapefruit intake and risk of breast cancer in postmenopausal women:
      the Multiethnic Cohort Study.&quot; <i>Br J Cancer</i> <b>97</b>(3):440-445.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17622247">PubMed</a></p>

    <p align="justify">Moore,
      J. (1981). &quot;Asexual reproduction and environmental predictability in
      cestodes (Cyclopdyllidea: Taeniidae).&quot; <i>Evolution</i> <b>35</b>:723-741.
    </p>

    <p align="justify">Moosmann,
      B., and Behl, C. (2008). &quot;Mitochondrially encoded cysteine predicts animal
      lifespan.&quot; <i>Aging Cell</i> <b>7</b>(1):32-46. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/18028257">PubMed</a></p>

    <p align="justify">Morales,
      C. P., Holt, S. E., Ouellette, M., Kaur, K. J., Yan, Y., Wilson, K. S., White,
      M. A., Wright, W. E., and Shay, J. W. (1999). &quot;Absence of
      cancer-associated changes in human fibroblasts immortalized with telomerase.&quot;
      <i>Nat Genet</i> <b>21</b>(1):115-118. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9916803">PubMed</a></p>

    <p align="justify">Morgan,
      H., Beck, T., Blake, A., Gates, H., Adams, N., Debouzy, G., Leblanc, S.,
      Lengger, C., Maier, H., Melvin, D.<i>, et al.</i> (2010). &quot;EuroPhenome: a
      repository for high-throughput mouse phenotyping data.&quot; <i>Nucleic Acids
        Res</i> <b>38</b>(Database issue):D577-585. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19933761">PubMed</a></p>

    <p align="justify">Mori,
      A., Utsumi, K., Liu, J., and Hosokawa, M. (1998). &quot;Oxidative damage in the
      senescence-accelerated mouse.&quot; <i>Ann N Y Acad Sci</i> <b>854</b>:239-250.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9928434">PubMed</a></p>

    <p align="justify">Morimoto,
      R. I. (2006). &quot;Stress, aging, and neurodegenerative disease.&quot; <i>N
        Engl J Med</i> <b>355</b>(21):2254-2255. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17124027">PubMed</a></p>

    <p align="justify">Morimoto,
      R. I., and Cuervo, A. M. (2009). &quot;Protein homeostasis and aging: taking
      care of proteins from the cradle to the grave.&quot; <i>J Gerontol A Biol Sci
        Med Sci</i> <b>64</b>(2):167-170. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19228787">PubMed</a></p>

    <p align="justify">Morley,
      J. F., Brignull, H. R., Weyers, J. J., and Morimoto, R. I. (2002). &quot;The
      threshold for polyglutamine-expansion protein aggregation and cellular toxicity
      is dynamic and influenced by aging in Caenorhabditis elegans.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>99</b>(16):10417-10422. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12122205">PubMed</a></p>

    <p align="justify">Morrison,
      S. J., Prowse, K. R., Ho, P., and Weissman, I. L. (1996). &quot;Telomerase
      activity in hematopoietic cells is associated with self-renewal
      potential.&quot; <i>Immunity</i> <b>5</b>(3):207-216. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/8808676">PubMed</a></p>

    <p align="justify">Morrison,
      J. H., and Hof, P. R. (1997). &quot;Life and death of neurons in the aging
      brain.&quot; <i>Science</i> <b>278</b>(5337):412-419. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9334292">PubMed</a></p>

    <p align="justify">Moskovitz,
      J., Bar-Noy, S., Williams, W. M., Requena, J., Berlett, B. S., and Stadtman, E.
      R. (2001). &quot;Methionine sulfoxide reductase (MsrA) is a regulator of
      antioxidant defense and lifespan in mammals.&quot; <i>Proc Natl Acad Sci U S A</i>
      <b>98</b>(23):12920-12925. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11606777">PubMed</a></p>

    <p align="justify">Motonaga,
      K., Itoh, M., Hachiya, Y., Endo, A., Kato, K., Ishikura, H., Saito, Y., Mori,
      S., Takashima, S., and Goto, Y. (2002). &quot;Age related expression of
      Werner's syndrome protein in selected tissues and coexpression of transcription
      factors.&quot; <i>J Clin Pathol</i> <b>55</b>(3):195-199. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11896071">PubMed</a>
    </p>

    <p align="justify">Motta,
      M., Bennati, E., Vacante, M., Stanta, G., Cardillo, E., Malaguarnera, M., and
      Giarelli, L. (2010). &quot;Autopsy reports in extreme longevity.&quot; <i>Arch
        Gerontol Geriatr</i> <b>50</b>(1):48-50. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19285737">PubMed</a></p>

    <p align="justify">Moyzis,
      R. K., Buckingham, J. M., Cram, L. S., Dani, M., Deaven, L. L., Jones, M. D.,
      Meyne, J., Ratliff, R. L., and Wu, J. R. (1988). &quot;A highly conserved
      repetitive DNA sequence, (TTAGGG)n, present at the telomeres of human
      chromosomes.&quot; <i>Proc Natl Acad Sci U S A</i> <b>85</b>(18):6622-6626.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/3413114">PubMed</a></p>

    <p align="justify">Muller,
      H. J. (1938). &quot;The remaking of chromosomes.&quot; <i>Collecting Net</i> <b>13</b>(8):182-198.
    </p>

    <p align="justify">Munne-Bosch,
      S. (2008). &quot;Do perennials really senesce?&quot; <i>Trends Plant Sci</i> <b>13</b>(5):216-220.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18328774">PubMed</a></p>

    <p align="justify">Munoz-Espin,
      D., Canamero, M., Maraver, A., Gomez-Lopez, G., Contreras, J., Murillo-Cuesta,
      S., Rodriguez-Baeza, A., Varela-Nieto, I., Ruberte, J., Collado, M.<i>, et al.</i>
      (2013). &quot;Programmed cell senescence during mammalian embryonic
      development.&quot; <i>Cell</i> <b>155</b>(5):1104-1118. <a href="http://www.ncbi.nlm.nih.gov/pubmed/24238962">PubMed</a>
    </p>

    <p align="justify">Munro,
      J., Steeghs, K., Morrison, V., Ireland, H., and Parkinson, E. K. (2001).
      &quot;Human fibroblast replicative senescence can occur in the absence of
      extensive cell division and short telomeres.&quot; <i>Oncogene</i> <b>20</b>(27):3541-3552.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11429701">PubMed</a></p>

    <p align="justify">Murakami,
      S., Tedesco, P. M., Cypser, J. R., and Johnson, T. E. (2000). &quot;Molecular
      genetic mechanisms of life span manipulation in Caenorhabditis elegans.&quot; <i>Ann
        N Y Acad Sci</i> <b>908</b>:40-49. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911946">PubMed</a></p>

    <p align="justify">Murphree,
      A. L., and Benedict, W. F. (1984). &quot;Retinoblastoma: clues to human
      oncogenesis.&quot; <i>Science</i> <b>223</b>(4640):1028-1033. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/6320372">PubMed</a></p>

    <p align="justify">Murphy,
      K. M., and Topel, R. H. (2006). &quot;The value of health and longevity.&quot; <i>Journal
        of Political Economy</i> <b>114</b>(5):871-904. </p>

    <p align="justify">Nabholz,
      B., Glemin, S., and Galtier, N. (2008). &quot;Strong variations of
      mitochondrial mutation rate across mammals--the longevity hypothesis.&quot; <i>Mol
        Biol Evol</i> <b>25</b>(1):120-130. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17998254">PubMed</a></p>

    <p align="justify">Nair,
      K. S., Rizza, R. A., O'Brien, P., Dhatariya, K., Short, K. R., Nehra, A.,
      Vittone, J. L., Klee, G. G., Basu, A., Basu, R.<i>, et al.</i> (2006).
      &quot;DHEA in elderly women and DHEA or testosterone in elderly men.&quot; <i>N
        Engl J Med</i> <b>355</b>(16):1647-1659. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17050889">PubMed</a></p>

    <p align="justify">Nakamura,
      T. M., Morin, G. B., Chapman, K. B., Weinrich, S. L., Andrews, W. H., Lingner,
      J., Harley, C. B., and Cech, T. R. (1997). &quot;Telomerase catalytic subunit
      homologs from fission yeast and human.&quot; <i>Science</i> <b>277</b>(5328):955-959.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9252327">PubMed</a></p>

    <p align="justify">Nanney,
      D. L. (1974). &quot;Aging and long-term temporal regulation in ciliated
      protozoa. A critical review.&quot; <i>Mech Ageing Dev</i> <b>3</b>(2):81-105.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4215928">PubMed</a></p>

    <p align="justify">Napoli,
      C., Martin-Padura, I., de Nigris, F., Giorgio, M., Mansueto, G., Somma, P., Condorelli,
      M., Sica, G., De Rosa, G., and Pelicci, P. (2003). &quot;Deletion of the p66Shc
      longevity gene reduces systemic and tissue oxidative stress, vascular cell
      apoptosis, and early atherogenesis in mice fed a high-fat diet.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>100</b>(4):2112-2116. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12571362">PubMed</a></p>

    <p align="justify">Narayanan,
      L., Fritzell, J. A., Baker, S. M., Liskay, R. M., and Glazer, P. M. (1997).
      &quot;Elevated levels of mutation in multiple tissues of mice deficient in the
      DNA mismatch repair gene Pms2.&quot; <i>Proc Natl Acad Sci U S A</i> <b>94</b>(7):3122-3127.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9096356">PubMed</a></p>

    <p align="justify">Nathwani,
      A. C., Tuddenham, E. G., Rangarajan, S., Rosales, C., McIntosh, J., Linch, D.
      C., Chowdary, P., Riddell, A., Pie, A. J., Harrington, C.<i>, et al.</i>
      (2011). &quot;Adenovirus-associated virus vector-mediated gene transfer in
      hemophilia B.&quot; <i>N Engl J Med</i> <b>365</b>(25):2357-2365. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/22149959">PubMed</a></p>

    <p align="justify">Nemoto,
      S., and Finkel, T. (2002). &quot;Redox regulation of forkhead proteins through
      a p66shc-dependent signaling pathway.&quot; <i>Science</i> <b>295</b>(5564):2450-2452.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11884717">PubMed</a></p>

    <p align="justify">Nesse,
      R. M. (1988). &quot;Life table tests of evolutionary theories of
      senescence.&quot; <i>Exp Gerontol</i> <b>23</b>(6):445-453. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3250881">PubMed</a>
    </p>

    <p align="justify">Neufeld,
      T. P. (2010). &quot;TOR-dependent control of autophagy: biting the hand that
      feeds.&quot; <i>Curr Opin Cell Biol</i> <b>22</b>(2):157-168. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/20006481">PubMed</a></p>

    <p align="justify">Njajou,
      O. T., Cawthon, R. M., Damcott, C. M., Wu, S. H., Ott, S., Garant, M. J.,
      Blackburn, E. H., Mitchell, B. D., Shuldiner, A. R., and Hsueh, W. C. (2007).
      &quot;Telomere length is paternally inherited and is associated with parental
      lifespan.&quot; <i>Proc Natl Acad Sci U S A</i> <b>104</b>(29):12135-12139.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17623782">PubMed</a></p>

    <p align="justify">Norddahl,
      G. L., Pronk, C. J., Wahlestedt, M., Sten, G., Nygren, J. M., Ugale, A.,
      Sigvardsson, M., and Bryder, D. (2011). &quot;Accumulating mitochondrial DNA
      mutations drive premature hematopoietic aging phenotypes distinct from
      physiological stem cell aging.&quot; <i>Cell Stem Cell</i> <b>8</b>(5):499-510.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21549326">PubMed</a></p>

    <p align="justify">Nottebohm,
      F. (1989). &quot;From bird song to neurogenesis.&quot; <i>Sci Am</i> <b>260</b>(2):74-79.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2643827">PubMed</a></p>

    <p align="justify">Nussey,
      D. H., Froy, H., Lemaitre, J. F., Gaillard, J. M., and Austad, S. N. (2013).
      &quot;Senescence in natural populations of animals: widespread evidence and its
      implications for bio-gerontology.&quot; <i>Ageing Res Rev</i> <b>12</b>(1):214-225.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22884974">PubMed</a></p>

    <p align="justify">Nwosu,
      B. U., Nilsson, O., Mitchum, R. D., Jr., Coco, M., Barnes, K. M., and Baron, J.
      (2005). &quot;Lack of telomere shortening with age in mouse resting zone
      chondrocytes.&quot; <i>Horm Res</i> <b>63</b>(3):125-128. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15795509">PubMed</a>
    </p>

    <p align="justify">Nystrom,
      T. (2002). &quot;Translational fidelity, protein oxidation, and senescence:
      lessons from bacteria.&quot; <i>Ageing Res Rev</i> <b>1</b>(4):693-703.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12208238">PubMed</a></p>

    <p align="justify">Oklejewicz,
      M., and Daan, S. (2002). &quot;Enhanced longevity in tau mutant Syrian
      hamsters, Mesocricetus auratus.&quot; <i>J Biol Rhythms</i> <b>17</b>(3):210-216.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12054192">PubMed</a></p>

    <p align="justify">Olovnikov,
      A. M. (1971). &quot;[Principle of marginotomy in template synthesis of
      polynucleotides].&quot; <i>Dokl Akad Nauk SSSR</i> <b>201</b>(6):1496-1499.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5158754">PubMed</a></p>

    <p align="justify">Olovnikov,
      A. M. (1973). &quot;A theory of marginotomy. The incomplete copying of template
      margin in enzymic synthesis of polynucleotides and biological significance of
      the phenomenon.&quot; <i>J Theor Biol</i> <b>41</b>(1):181-190. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/4754905">PubMed</a></p>

    <p align="justify">Olshansky,
      S. J., Carnes, B. A., and Cassel, C. (1990). &quot;In search of Methuselah:
      estimating the upper limits to human longevity.&quot; <i>Science</i> <b>250</b>(4981):634-640.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2237414">PubMed</a></p>

    <p align="justify">Olshansky,
      S. J., Hayflick, L., and Carnes, B. A. (2002). &quot;No truth to the fountain
      of youth.&quot; <i>Sci Am</i> <b>286</b>(6):92-95. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12030096">PubMed</a></p>

    <p align="justify">Olshansky,
      S. J., Perry, D., Miller, R. A., and Butler, R. N. (2006). &quot;In pursuit of
      the longevity dividend.&quot; <i>The Scientist</i> <b>20</b>:28-36. </p>

    <p align="justify">Olson,
      C. B. (1987). &quot;A review of why and how we age: a defense of multifactorial
      aging.&quot; <i>Mech Ageing Dev</i> <b>41</b>(1-2):1-28. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3323678">PubMed</a>
    </p>

    <p align="justify">Olson,
      M. V., and Varki, A. (2003). &quot;Sequencing the chimpanzee genome: insights
      into human evolution and disease.&quot; <i>Nat Rev Genet</i> <b>4</b>(1):20-28.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12509750">PubMed</a></p>

    <p align="justify">Olsson,
      M., and Shine, R. (2002). &quot;Growth to death in lizards.&quot; <i>Evolution
        Int J Org Evolution</i> <b>56</b>(9):1867-1870. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12389733">PubMed</a>
    </p>

    <p align="justify">Orgel,
      L. E. (1963). &quot;The maintenance of the accuracy of protein synthesis and
      its relevance to ageing.&quot; <i>Proc Natl Acad Sci U S A</i> <b>49</b>:517-521.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/13940312">PubMed</a></p>

    <p align="justify">Orgel,
      L. E. (1973). &quot;Ageing of clones of mammalian cells.&quot; <i>Nature</i> <b>243</b>(5408):441-445.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4591306">PubMed</a></p>

    <p align="justify">Orlic,
      D., Kajstura, J., Chimenti, S., Jakoniuk, I., Anderson, S. M., Li, B., Pickel,
      J., McKay, R., Nadal-Ginard, B., Bodine, D. M.<i>, et al.</i> (2001).
      &quot;Bone marrow cells regenerate infarcted myocardium.&quot; <i>Nature</i> <b>410</b>(6829):701-705.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11287958">PubMed</a></p>

    <p align="justify">Orr,
      W. C., and Sohal, R. S. (1994). &quot;Extension of life-span by overexpression
      of superoxide dismutase and catalase in Drosophila melanogaster.&quot; <i>Science</i>
      <b>263</b>(5150):1128-1130. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8108730">PubMed</a></p>

    <p align="justify">Orr,
      W. C., Mockett, R. J., Benes, J. J., and Sohal, R. S. (2003). &quot;Effects of
      overexpression of copper-zinc and manganese superoxide dismutases, catalase,
      and thioredoxin reductase genes on longevity in Drosophila melanogaster.&quot; <i>J
        Biol Chem</i> <b>278</b>(29):26418-26422. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12743125">PubMed</a></p>

    <p align="justify">Osborne,
      T. B., Mendel, L. B., and Fey, E. L. (1917). &quot;The effect of retardation of
      growth upon the breeding period and duration of life in rats.&quot; <i>Science</i>
      <b>45</b>:294-295. </p>

    <p align="justify">Oshima,
      J., Campisi, J., Tannock, T. C., and Martin, G. M. (1995). &quot;Regulation of
      c-fos expression in senescing Werner syndrome fibroblasts differs from that
      observed in senescing fibroblasts from normal donors.&quot; <i>J Cell Physiol</i>
      <b>162</b>(2):277-283. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7822435">PubMed</a></p>

    <p align="justify">Osiewacz,
      H. D., and Borghouts, C. (2000). &quot;Mitochondrial oxidative stress and aging
      in the filamentous fungus Podospora anserina.&quot; <i>Ann N Y Acad Sci</i> <b>908</b>:31-39.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911945">PubMed</a></p>

    <p align="justify">Ottinger,
      M. A. (2001). &quot;Quail and other short-lived birds.&quot; <i>Exp Gerontol</i>
      <b>36</b>(4-6):859-868. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295519">PubMed</a></p>

    <p align="justify">Outeiro,
      T. F., Kontopoulos, E., Altmann, S. M., Kufareva, I., Strathearn, K. E., Amore,
      A. M., Volk, C. B., Maxwell, M. M., Rochet, J. C., McLean, P. J.<i>, et al.</i>
      (2007). &quot;Sirtuin 2 inhibitors rescue alpha-synuclein-mediated toxicity in
      models of Parkinson's disease.&quot; <i>Science</i> <b>317</b>(5837):516-519.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17588900">PubMed</a></p>

    <p align="justify">Overington,
      J. P., Al-Lazikani, B., and Hopkins, A. L. (2006). &quot;How many drug targets
      are there?&quot; <i>Nat Rev Drug Discov</i> <b>5</b>(12):993-996. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/17139284">PubMed</a></p>

    <p align="justify">Pan,
      C., Xue, B. H., Ellis, T. M., Peace, D. J., and Diaz, M. O. (1997).
      &quot;Changes in telomerase activity and telomere length during human T
      lymphocyte senescence.&quot; <i>Exp Cell Res</i> <b>231</b>(2):346-353.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9087176">PubMed</a></p>

    <p align="justify">Panda,
      S., Isbatan, A., and Adami, G. R. (2008). &quot;Modification of the ATM/ATR
      directed DNA damage response state with aging and long after hepatocyte
      senescence induction in vivo.&quot; <i>Mech Ageing Dev</i> <b>129</b>(6):332-340.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18440596">PubMed</a></p>

    <p align="justify">Paradis,
      V., Youssef, N., Dargere, D., Ba, N., Bonvoust, F., Deschatrette, J., and
      Bedossa, P. (2001). &quot;Replicative senescence in normal liver, chronic
      hepatitis C, and hepatocellular carcinomas.&quot; <i>Hum Pathol</i> <b>32</b>(3):327-332.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11274643">PubMed</a></p>

    <p align="justify">Park,
      W. Y., Hwang, C. I., Kang, M. J., Seo, J. Y., Chung, J. H., Kim, Y. S., Lee, J.
      H., Kim, H., Kim, K. A., Yoo, H. J.<i>, et al.</i> (2001). &quot;Gene profile
      of replicative senescence is different from progeria or elderly donor.&quot; <i>Biochem
        Biophys Res Commun</i> <b>282</b>(4):934-939. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11352641">PubMed</a>
    </p>

    <p align="justify">Park,
      S. Y., Murphy, S. P., Wilkens, L. R., Henderson, B. E., and Kolonel, L. N.
      (2011). &quot;Multivitamin use and the risk of mortality and cancer incidence:
      the multiethnic cohort study.&quot; <i>Am J Epidemiol</i> <b>173</b>(8):906-914.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21343248">PubMed</a></p>

    <p align="justify">Parker,
      S. T. (1990). &quot;Why big brains are so rare: energy costs of intelligence
      and brain size in anthropoid primates.&quot; In: <i>Language and Intelligence
        in Monkeys and Apes</i>, Parker, S. T. and Gibson, K. R. (ed.).  Cambridge
      University Press, Cambridge, 129-156.</p>

    <p align="justify">Parker,
      J. D., Parker, K. M., Sohal, B. H., Sohal, R. S., and Keller, L. (2004).
      &quot;Decreased expression of Cu-Zn superoxide dismutase 1 in ants with extreme
      lifespan.&quot; <i>Proc Natl Acad Sci U S A</i> <b>101</b>(10):3486-3489.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14993600">PubMed</a></p>

    <p align="justify">Parkes,
      T. L., Elia, A. J., Dickinson, D., Hilliker, A. J., Phillips, J. P., and
      Boulianne, G. L. (1998). &quot;Extension of Drosophila lifespan by
      overexpression of human SOD1 in motorneurons.&quot; <i>Nat Genet</i> <b>19</b>(2):171-174.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9620775">PubMed</a></p>

    <p align="justify">Parrinello,
      S., Samper, E., Krtolica, A., Goldstein, J., Melov, S., and Campisi, J. (2003).
      &quot;Oxygen sensitivity severely limits the replicative lifespan of murine
      fibroblasts.&quot; <i>Nat Cell Biol</i> <b>5</b>(8):741-747. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12855956">PubMed</a></p>

    <p align="justify">Partridge,
      L., and Mangel, M. (1999). &quot;Messages from mortality: the evolution of
      death rates in the old.&quot; <i>Trends in Ecology and Evolution</i> <b>14</b>(11):438-442.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10511720">PubMed</a></p>

    <p align="justify">Partridge,
      L., and Gems, D. (2007). &quot;Benchmarks for ageing studies.&quot; <i>Nature</i>
      <b>450</b>(7167):165-167. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17994065">PubMed</a></p>

    <p align="justify">Patel,
      M. N., Knight, C. G., Karageorgi, C., and Leroi, A. M. (2002). &quot;Evolution
      of germ-line signals that regulate growth and aging in nematodes.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>99</b>(2):769-774. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11805331">PubMed</a>
    </p>

    <p align="justify">Patnaik,
      B. K. (1994). &quot;Ageing in reptiles.&quot; <i>Gerontology</i> <b>40</b>(2-4):200-220.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7926857">PubMed</a></p>

    <p align="justify">Pearl,
      R. (1928). <i>The Rate of Living</i>. Knopf, New York.</p>

    <p align="justify">Pearson,
      K. J., Baur, J. A., Lewis, K. N., Peshkin, L., Price, N. L., Labinskyy, N.,
      Swindell, W. R., Kamara, D., Minor, R. K., Perez, E.<i>, et al.</i> (2008).
      &quot;Resveratrol delays age-related deterioration and mimics transcriptional
      aspects of dietary restriction without extending life span.&quot; <i>Cell Metab</i>
      <b>8</b>(2):157-168. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18599363">PubMed</a></p>

    <p align="justify">Pera,
      M. F., Reubinoff, B., and Trounson, A. (2000). &quot;Human embryonic stem cells.&quot;
      <i>J Cell Sci</i> <b>113 ( Pt 1)</b>:5-10. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10591620">PubMed</a></p>

    <p align="justify">Perez,
      V. I., Buffenstein, R., Masamsetti, V., Leonard, S., Salmon, A. B., Mele, J.,
      Andziak, B., Yang, T., Edrey, Y., Friguet, B.<i>, et al.</i> (2009).
      &quot;Protein stability and resistance to oxidative stress are determinants of
      longevity in the longest-living rodent, the naked mole-rat.&quot; <i>Proc Natl
        Acad Sci U S A</i> <b>106</b>(9):3059-3064. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19223593">PubMed</a></p>

    <p align="justify">Perez,
      V. I., Cortez, L. A., Lew, C. M., Rodriguez, M., Webb, C. R., Van Remmen, H.,
      Chaudhuri, A., Qi, W., Lee, S., Bokov, A.<i>, et al.</i> (2011).
      &quot;Thioredoxin 1 overexpression extends mainly the earlier part of life span
      in mice.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>66</b>(12):1286-1299.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21873593">PubMed</a></p>

    <p align="justify">Perez-Campo,
      R., Lopez-Torres, M., Rojas, C., Cadenas, S., and Barja de Quiroga, G. (1993).
      &quot;Lung glutathione reductase induction in aging catalase-depleted frogs
      correlates with early survival throughout the life span.&quot; <i>Mech Ageing
        Dev</i> <b>67</b>(1-2):115-127. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8469024">PubMed</a></p>

    <p align="justify">Perls,
      T., Kunkel, L. M., and Puca, A. A. (2002). &quot;The genetics of exceptional
      human longevity.&quot; <i>J Am Geriatr Soc</i> <b>50</b>(2):359-368. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12028221">PubMed</a></p>

    <p align="justify">Perls,
      T. T. (2006). &quot;The different paths to 100.&quot; <i>Am J Clin Nutr</i> <b>83</b>(2):484S-487S.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16470017">PubMed</a></p>

    <p align="justify">Pervaiz,
      S. (2003). &quot;Resveratrol: from grapevines to mammalian biology.&quot; <i>Faseb
        J</i> <b>17</b>(14):1975-1985. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14597667">PubMed</a></p>

    <p align="justify">Peterson,
      R. T., Link, B. A., Dowling, J. E., and Schreiber, S. L. (2000). &quot;Small
      molecule developmental screens reveal the logic and timing of vertebrate
      development.&quot; <i>Proc Natl Acad Sci U S A</i> <b>97</b>(24):12965-12969.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11087852">PubMed</a></p>

    <p align="justify">Peto,
      R., and Doll, R. (1997). &quot;There is no such thing as aging.&quot; <i>Bmj</i>
      <b>315</b>(7115):1030-1032. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9366723">PubMed</a></p>

    <p align="justify">Petrascheck,
      M., Ye, X., and Buck, L. B. (2007). &quot;An antidepressant that extends
      lifespan in adult Caenorhabditis elegans.&quot; <i>Nature</i> <b>450</b>(7169):553-556.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18033297">PubMed</a></p>

    <p align="justify">Phelan,
      J. P., and Rose, M. R. (2005). &quot;Why dietary restriction substantially
      increases longevity in animal models but won't in humans.&quot; <i>Ageing Res
        Rev</i> <b>4</b>(3):339-350. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16046282">PubMed</a></p>

    <p align="justify">Pianka,
      E. R. (1970). &quot;On r and K selection.&quot; <i>Am Nat</i> <b>104</b>(940):592-597.
    </p>

    <p align="justify">Pichierri,
      P., Franchitto, A., Mosesso, P., Proietti de Santis, L., Balajee, A. S., and
      Palitti, F. (2000). &quot;Werner's syndrome lymphoblastoid cells are
      hypersensitive to topoisomerase II inhibitors in the G2 phase of the cell
      cycle.&quot; <i>Mutat Res</i> <b>459</b>(2):123-133. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10725663">PubMed</a></p>

    <p align="justify">Pijnenburg,
      M. A., and Leget, C. (2007). &quot;Who wants to live forever? Three arguments
      against extending the human lifespan.&quot; <i>J Med Ethics</i> <b>33</b>(10):585-587.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17906056">PubMed</a></p>

    <p align="justify">Pitkanen,
      S., and Robinson, B. H. (1996). &quot;Mitochondrial complex I deficiency leads
      to increased production of superoxide radicals and induction of superoxide
      dismutase.&quot; <i>J Clin Invest</i> <b>98</b>(2):345-351. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8755643">PubMed</a>
    </p>

    <p align="justify">Pletcher,
      S. D., Khazaeli, A. A., and Curtsinger, J. W. (2000). &quot;Why do life spans
      differ? Partitioning mean longevity differences in terms of age-specific
      mortality parameters.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>55</b>(8):B381-389.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10952359">PubMed</a></p>

    <p align="justify">Plytycz,
      B., and Bigaj, J. (1993). &quot;Studies on the growth and longevity of the
      yellow-bellied toad, Bombina variegata, in natural environments.&quot; <i>Amphibia-Reptilia</i>
      <b>14</b>:35-44. </p>

    <p align="justify">Plytycz,
      B., Mika, J., and Bigaj, J. (1995a). &quot;Age-dependent changes in thymuses in
      the European common frog, Rana temporaria.&quot; <i>J Exp Zool</i> <b>273</b>(6):451-460.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8568500">PubMed</a></p>

    <p align="justify">Plytycz,
      B., Jozkowicz, A., Chadzinska, M., and Bigaj, J. (1995b). &quot;Longevity of
      yellow-bellied toads (Bombina variegata) and the efficiency of their immune
      system.&quot; <i>Naturschutzreport</i> <b>11</b>:77-84. </p>

    <p align="justify">Poeggeler,
      B. (2005). &quot;Melatonin, aging, and age-related diseases: perspectives for
      prevention, intervention, and therapy.&quot; <i>Endocrine</i> <b>27</b>(2):201-212.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16217133">PubMed</a></p>

    <p align="justify">Polyukhov,
      A. M., Kobsar, I. V., Grebelnik, V. I., and Voitenko, V. P. (2000). &quot;The
      accelerated occurrence of age-related changes of organism in Chernobyl workers:
      a radiation-induced progeroid syndrome?&quot; <i>Exp Gerontol</i> <b>35</b>(1):105-115.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10705044">PubMed</a></p>

    <p align="justify">Ponten,
      J., Stein, W. D., and Shall, S. (1983). &quot;A quantitative analysis of the
      aging of human glial cells in culture.&quot; <i>J Cell Physiol</i> <b>117</b>(3):342-352.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6361044">PubMed</a></p>

    <p align="justify">Post,
      S. G. (2004). &quot;Anti-aging medicine: the history: establishing an
      appropriate ethical framework: the moral conversation around the goal of
      prolongevity.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>59</b>(6):B534-539.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15215258">PubMed</a></p>

    <p align="justify">Pound,
      P., Ebrahim, S., Sandercock, P., Bracken, M. B., and Roberts, I. (2004).
      &quot;Where is the evidence that animal research benefits humans?&quot; <i>Bmj</i>
      <b>328</b>(7438):514-517. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14988196">PubMed</a></p>

    <p align="justify">Prescott,
      J., and Blackburn, E. H. (1997). &quot;Telomerase RNA mutations in
      Saccharomyces cerevisiae alter telomerase action and reveal nonprocessivity in
      vivo and in vitro.&quot; <i>Genes Dev</i> <b>11</b>(4):528-540. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9042865">PubMed</a></p>

    <p align="justify">Price,
      J. S., Waters, J. G., Darrah, C., Pennington, C., Edwards, D. R., Donell, S.
      T., and Clark, I. M. (2002). &quot;The role of chondrocyte senescence in
      osteoarthritis.&quot; <i>Aging Cell</i> <b>1</b>(1):57-65. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12882354">PubMed</a>
    </p>

    <p align="justify">Prinzinger,
      R. (2005). &quot;Programmed ageing: the theory of maximal metabolic scope. How
      does the biological clock tick?&quot; <i>EMBO Rep</i> <b>6 Spec No</b>:S14-19.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15995655">PubMed</a></p>

    <p align="justify">Promislow,
      D. E. (1993). &quot;On size and survival: progress and pitfalls in the
      allometry of life span.&quot; <i>J Gerontol</i> <b>48</b>(4):B115-123.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8315214">PubMed</a></p>

    <p align="justify">Promislow,
      D. E. (1994). &quot;DNA repair and the evolution of longevity: a critical
      analysis.&quot; <i>J Theor Biol</i> <b>170</b>(3):291-300. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7996857">PubMed</a>
    </p>

    <p align="justify">Prothero,
      J. (1993). &quot;Adult life span as a function of age at maturity.&quot; <i>Exp
        Gerontol</i> <b>28</b>(6):529-536. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8137889">PubMed</a></p>

    <p align="justify">Prowse,
      K. R., and Greider, C. W. (1995). &quot;Developmental and tissue-specific
      regulation of mouse telomerase and telomere length.&quot; <i>Proc Natl Acad Sci
        U S A</i> <b>92</b>(11):4818-4822. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7761406">PubMed</a></p>

    <p align="justify">Puca,
      A. A., Daly, M. J., Brewster, S. J., Matise, T. C., Barrett, J.,
      Shea-Drinkwater, M., Kang, S., Joyce, E., Nicoli, J., Benson, E.<i>, et al.</i>
      (2001). &quot;A genome-wide scan for linkage to human exceptional longevity
      identifies a locus on chromosome 4.&quot; <i>Proc Natl Acad Sci U S A</i> <b>98</b>(18):10505-10508.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11526246">PubMed</a></p>

    <p align="justify">Quick,
      K. L., Ali, S. S., Arch, R., Xiong, C., Wozniak, D., and Dugan, L. L. (2008).
      &quot;A carboxyfullerene SOD mimetic improves cognition and extends the
      lifespan of mice.&quot; <i>Neurobiol Aging</i> <b>29</b>(1):117-128. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/17079053">PubMed</a></p>

    <p align="justify">Rabinovitch,
      P. S., and Martin, G. M. (1982). &quot;Encephalomyocarditis virus as a probe of
      errors in macromolecular synthesis in aging mice.&quot; <i>Mech Ageing Dev</i> <b>20</b>(2):155-163.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6294419">PubMed</a></p>

    <p align="justify">Racette,
      S. B., Weiss, E. P., Villareal, D. T., Arif, H., Steger-May, K., Schechtman, K.
      B., Fontana, L., Klein, S., and Holloszy, J. O. (2006). &quot;One year of
      caloric restriction in humans: feasibility and effects on body composition and
      abdominal adipose tissue.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>61</b>(9):943-950.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16960025">PubMed</a></p>

    <p align="justify">Raji,
      N. S., and Rao, K. S. (1998). &quot;Trisomy 21 and accelerated aging:
      DNA-repair parameters in peripheral lymphocytes of Down's syndrome
      patients.&quot; <i>Mech Ageing Dev</i> <b>100</b>(1):85-101. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9509398">PubMed</a>
    </p>

    <p align="justify">Ramirez,
      R. D., Morales, C. P., Herbert, B. S., Rohde, J. M., Passons, C., Shay, J. W.,
      and Wright, W. E. (2001). &quot;Putative telomere-independent mechanisms of
      replicative aging reflect inadequate growth conditions.&quot; <i>Genes Dev</i> <b>15</b>(4):398-403.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11230148">PubMed</a></p>

    <p align="justify">Ramsey,
      J. J., Colman, R. J., Binkley, N. C., Christensen, J. D., Gresl, T. A.,
      Kemnitz, J. W., and Weindruch, R. (2000). &quot;Dietary restriction and aging
      in rhesus monkeys: the University of Wisconsin study.&quot; <i>Exp Gerontol</i>
      <b>35</b>(9-10):1131-1149. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11113597">PubMed</a></p>

    <p align="justify">Rando,
      T. A., and Chang, H. Y. (2012). &quot;Aging, rejuvenation, and epigenetic
      reprogramming: resetting the aging clock.&quot; <i>Cell</i> <b>148</b>(1-2):46-57.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22265401">PubMed</a></p>

    <p align="justify">Rasmussen,
      U. F., Krustrup, P., Kjaer, M., and Rasmussen, H. N. (2003). &quot;Experimental
      evidence against the mitochondrial theory of aging A study of isolated human
      skeletal muscle mitochondria.&quot; <i>Exp Gerontol</i> <b>38</b>(8):877-886.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12915209">PubMed</a></p>

    <p align="justify">Ravussin,
      E., Redman, L. M., Rochon, J., Das, S. K., Fontana, L., Kraus, W. E.,
      Romashkan, S., Williamson, D. A., Meydani, S. N., Villareal, D. T.<i>, et al.</i>
      (2015). &quot;A 2-Year Randomized Controlled Trial of Human Caloric
      Restriction: Feasibility and Effects on Predictors of Health Span and
      Longevity.&quot; <i>J Gerontol A Biol Sci Med Sci</i> <b>70</b>(9):1097-1104.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/26187233">PubMed</a></p>

    <p align="justify">Reaume,
      A. G., Elliott, J. L., Hoffman, E. K., Kowall, N. W., Ferrante, R. J., Siwek,
      D. F., Wilcox, H. M., Flood, D. G., Beal, M. F., Brown, R. H., Jr.<i>, et al.</i>
      (1996). &quot;Motor neurons in Cu/Zn superoxide dismutase-deficient mice
      develop normally but exhibit enhanced cell death after axonal injury.&quot; <i>Nat
        Genet</i> <b>13</b>(1):43-47. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8673102">PubMed</a></p>

    <p align="justify">Renault,
      V., Thornell, L. E., Eriksson, P. O., Butler-Browne, G., Mouly, V., and Thorne,
      L. E. (2002). &quot;Regenerative potential of human skeletal muscle during
      aging.&quot; <i>Aging Cell</i> <b>1</b>(2):132-139. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12882343">PubMed</a></p>

    <p align="justify">Rera,
      M., Azizi, M. J., and Walker, D. W. (2013). &quot;Organ-specific mediation of
      lifespan extension: more than a gut feeling?&quot; <i>Ageing Res Rev</i> <b>12</b>(1):436-444.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22706186">PubMed</a></p>

    <p align="justify">Reznick,
      D. N., Bryant, M. J., Roff, D., Ghalambor, C. K., and Ghalambor, D. E. (2004).
      &quot;Effect of extrinsic mortality on the evolution of senescence in
      guppies.&quot; <i>Nature</i> <b>431</b>(7012):1095-1099. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15510147">PubMed</a>
    </p>

    <p align="justify">Reznick,
      D. N. (2005). &quot;The genetic basis of aging: an evolutionary biologist's
      perspective.&quot; <i>Sci Aging Knowledge Environ</i> <b>2005</b>(11):pe7.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15772417">PubMed</a></p>

    <p align="justify">Rheinwald,
      J. G., Hahn, W. C., Ramsey, M. R., Wu, J. Y., Guo, Z., Tsao, H., De Luca, M.,
      Catricala, C., and O'Toole, K. M. (2002). &quot;A two-stage, p16(INK4A)- and
      p53-dependent keratinocyte senescence mechanism that limits replicative
      potential independent of telomere status.&quot; <i>Mol Cell Biol</i> <b>22</b>(14):5157-5172.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12077343">PubMed</a></p>

    <p align="justify">Ricklefs,
      R. E. (2010). &quot;Life-history connections to rates of aging in terrestrial
      vertebrates.&quot; <i>Proc Natl Acad Sci U S A</i> <b>107</b>(22):10314-10319.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20479246">PubMed</a></p>

    <p align="justify">Robbins,
      E., Levine, E. M., and Eagle, H. (1970). &quot;Morphologic changes accompanying
      senescence of cultured human diploid cells.&quot; <i>J Exp Med</i> <b>131</b>(6):1211-1222.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5419270">PubMed</a></p>

    <p align="justify">Robertson,
      O. H. (1961). &quot;Prolongation of the life span of kokanee salmon (<i>Oncorhynchus
        nerka kennerlyi</i>) by castration before beginning of gonad development.&quot;
      <i>Proc Natl Acad Sci U S A</i> <b>47</b>(4):609-621. </p>

    <p align="justify">Robinson,
      B. H. (1998). &quot;Human complex I deficiency: clinical spectrum and
      involvement of oxygen free radicals in the pathogenicity of the defect.&quot; <i>Biochim
        Biophys Acta</i> <b>1364</b>(2):271-286. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9593934">PubMed</a></p>

    <p align="justify">Robles,
      S. J., and Adami, G. R. (1998). &quot;Agents that cause DNA double strand
      breaks lead to p16INK4a enrichment and the premature senescence of normal
      fibroblasts.&quot; <i>Oncogene</i> <b>16</b>(9):1113-1123. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9528853">PubMed</a>
    </p>

    <p align="justify">Rodriguez,
      J. I., Perez-Alonso, P., Funes, R., and Perez-Rodriguez, J. (1999).
      &quot;Lethal neonatal Hutchinson-Gilford progeria syndrome.&quot; <i>Am J Med Genet</i>
      <b>82</b>(3):242-248. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10215548">PubMed</a></p>

    <p align="justify">Rogan,
      E. M., Bryan, T. M., Hukku, B., Maclean, K., Chang, A. C., Moy, E. L.,
      Englezou, A., Warneford, S. G., Dalla-Pozza, L., and Reddel, R. R. (1995).
      &quot;Alterations in p53 and p16INK4 expression and telomere length during spontaneous
      immortalization of Li-Fraumeni syndrome fibroblasts.&quot; <i>Mol Cell Biol</i>
      <b>15</b>(9):4745-4753. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7651392">PubMed</a></p>

    <p align="justify">Rohme,
      D. (1981). &quot;Evidence for a relationship between longevity of mammalian
      species and life spans of normal fibroblasts in vitro and erythrocytes in
      vivo.&quot; <i>Proc Natl Acad Sci U S A</i> <b>78</b>(8):5009-5013. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/6946449">PubMed</a></p>

    <p align="justify">Rollo,
      C. D. (2002). &quot;Growth negatively impacts the life span of mammals.&quot; <i>Evol
        Dev</i> <b>4</b>(1):55-61. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11868658">PubMed</a></p>

    <p align="justify">Rose,
      M. R. (1989). &quot;Genetics of increased lifespan in Drosophila.&quot; <i>Bioessays</i>
      <b>11</b>(5):132-135. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2511838">PubMed</a></p>

    <p align="justify">Rose,
      M. R. (1991). <i>Evolutionary Biology of Aging</i>. Oxford University Press,
      New York.</p>

    <p align="justify">Rose,
      M. R., Drapeau, M. D., Yazdi, P. G., Shah, K. H., Moise, D. B., Thakar, R. R.,
      Rauser, C. L., and Mueller, L. D. (2002). &quot;Evolution of late-life mortality
      in Drosophila melanogaster.&quot; <i>Evolution Int J Org Evolution</i> <b>56</b>(10):1982-1991.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12449485">PubMed</a></p>

    <p align="justify">Ross,
      J. M., Stewart, J. B., Hagstrom, E., Brene, S., Mourier, A., Coppotelli, G.,
      Freyer, C., Lagouge, M., Hoffer, B. J., Olson, L.<i>, et al.</i> (2013).
      &quot;Germline mitochondrial DNA mutations aggravate ageing and can impair
      brain development.&quot; <i>Nature</i> <b>501</b>(7467):412-415. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/23965628">PubMed</a></p>

    <p align="justify">Rossi,
      D. J., Bryder, D., Seita, J., Nussenzweig, A., Hoeijmakers, J., and Weissman,
      I. L. (2007). &quot;Deficiencies in DNA damage repair limit the function of
      haematopoietic stem cells with age.&quot; <i>Nature</i> <b>447</b>(7145):725-729.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17554309">PubMed</a></p>

    <p align="justify">Rossolini,
      G., and Piantanelli, L. (2001). &quot;Mathematical modeling of the aging
      processes and the mechanisms of mortality: paramount role of heterogeneity.&quot;
      <i>Exp Gerontol</i> <b>36</b>(8):1277-1288. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11602204">PubMed</a></p>

    <p align="justify">Rotig,
      A., de Lonlay, P., Chretien, D., Foury, F., Koenig, M., Sidi, D., Munnich, A.,
      and Rustin, P. (1997). &quot;Aconitase and mitochondrial iron-sulphur protein
      deficiency in Friedreich ataxia.&quot; <i>Nat Genet</i> <b>17</b>(2):215-217.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9326946">PubMed</a></p>

    <p align="justify">Rougier,
      G. W., and Novacek, M. J. (1998). &quot;Early mammals: teeth, jaws and finally
      a skeleton!&quot; <i>Curr Biol</i> <b>8</b>(8):R284-287. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9550690">PubMed</a>
    </p>

    <p align="justify">Rouse,
      J., and Jackson, S. P. (2002). &quot;Interfaces between the detection,
      signaling, and repair of DNA damage.&quot; <i>Science</i> <b>297</b>(5581):547-551.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12142523">PubMed</a></p>

    <p align="justify">Rovio,
      A. T., Marchington, D. R., Donat, S., Schuppe, H. C., Abel, J., Fritsche, E.,
      Elliott, D. J., Laippala, P., Ahola, A. L., McNay, D.<i>, et al.</i> (2001).
      &quot;Mutations at the mitochondrial DNA polymerase (POLG) locus associated
      with male infertility.&quot; <i>Nat Genet</i> <b>29</b>(3):261-262. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11687794">PubMed</a></p>

    <p align="justify">Rowe,
      J. W., and Kahn, R. L. (1987). &quot;Human aging: usual and successful.&quot; <i>Science</i>
      <b>237</b>(4811):143-149. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3299702">PubMed</a></p>

    <p align="justify">Roy,
      J., Fulton, T. B., and Blackburn, E. H. (1998). &quot;Specific telomerase RNA
      residues distant from the template are essential for telomerase function.&quot;
      <i>Genes Dev</i> <b>12</b>(20):3286-3300. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9784502">PubMed</a></p>

    <p align="justify">Ruan,
      H., Tang, X. D., Chen, M. L., Joiner, M. L., Sun, G., Brot, N., Weissbach, H.,
      Heinemann, S. H., Iverson, L., Wu, C. F.<i>, et al.</i> (2002).
      &quot;High-quality life extension by the enzyme peptide methionine sulfoxide
      reductase.&quot; <i>Proc Natl Acad Sci U S A</i> <b>99</b>(5):2748-2753.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11867705">PubMed</a></p>

    <p align="justify">Rubinsztein,
      D. C., Marino, G., and Kroemer, G. (2011). &quot;Autophagy and aging.&quot; <i>Cell</i>
      <b>146</b>(5):682-695. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21884931">PubMed</a></p>

    <p align="justify">Rudolph,
      K. L., Chang, S., Lee, H. W., Blasco, M., Gottlieb, G. J., Greider, C., and
      DePinho, R. A. (1999). &quot;Longevity, stress response, and cancer in aging
      telomerase-deficient mice.&quot; <i>Cell</i> <b>96</b>(5):701-712. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10089885">PubMed</a></p>

    <p align="justify">Rudolph,
      K. L., Millard, M., Bosenberg, M. W., and DePinho, R. A. (2001). &quot;Telomere
      dysfunction and evolution of intestinal carcinoma in mice and humans.&quot; <i>Nat
        Genet</i> <b>28</b>(2):155-159. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11381263">PubMed</a></p>

    <p align="justify">Saito,
      K., Yoshioka, H., and Cutler, R. G. (1998). &quot;A spin trap,
      N-tert-butyl-alpha-phenylnitrone extends the life span of mice.&quot; <i>Biosci
        Biotechnol Biochem</i> <b>62</b>(4):792-794. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9614711">PubMed</a></p>

    <p align="justify">Saitoh,
      M., Nishitoh, H., Fujii, M., Takeda, K., Tobiume, K., Sawada, Y., Kawabata, M.,
      Miyazono, K., and Ichijo, H. (1998). &quot;Mammalian thioredoxin is a direct
      inhibitor of apoptosis signal-regulating kinase (ASK) 1.&quot; <i>Embo J</i> <b>17</b>(9):2596-2606.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9564042">PubMed</a></p>

    <p align="justify">Salganik,
      R. I., Solovyova, N. A., Dikalov, S. I., Grishaeva, O. N., Semenova, L. A., and
      Popovsky, A. V. (1994). &quot;Inherited enhancement of hydroxyl radical
      generation and lipid peroxidation in the S strain rats results in DNA
      rearrangements, degenerative diseases, and premature aging.&quot; <i>Biochem
        Biophys Res Commun</i> <b>199</b>(2):726-733. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8135816">PubMed</a>
    </p>

    <p align="justify">Salk,
      D., Bryant, E., Au, K., Hoehn, H., and Martin, G. M. (1981). &quot;Systematic
      growth studies, cocultivation, and cell hybridization studies of Werner
      syndrome cultured skin fibroblasts.&quot; <i>Hum Genet</i> <b>58</b>(3):310-316.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7327553">PubMed</a></p>

    <p align="justify">Salminen,
      A., and Kaarniranta, K. (2009). &quot;SIRT1: regulation of longevity via
      autophagy.&quot; <i>Cell Signal</i> <b>21</b>(9):1356-1360. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19249351">PubMed</a>
    </p>

    <p align="justify">Salmon,
      A. B., Murakami, S., Bartke, A., Kopchick, J., Yasumura, K., and Miller, R. A.
      (2005). &quot;Fibroblast cell lines from young adult mice of long-lived mutant
      strains are resistant to multiple forms of stress.&quot; <i>Am J Physiol
        Endocrinol Metab</i> <b>289</b>(1):E23-29. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15701676">PubMed</a></p>

    <p align="justify">Salmon,
      A. B., Leonard, S., Masamsetti, V., Pierce, A., Podlutsky, A. J., Podlutskaya,
      N., Richardson, A., Austad, S. N., and Chaudhuri, A. R. (2009). &quot;The long
      lifespan of two bat species is correlated with resistance to protein oxidation
      and enhanced protein homeostasis.&quot; <i>Faseb J</i> <b>23</b>(7):2317-2326.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19244163">PubMed</a></p>

    <p align="justify">Salway,
      K. D., Page, M. M., Faure, P. A., Burness, G., and Stuart, J. A. (2011).
      &quot;Enhanced protein repair and recycling are not correlated with longevity
      in 15 vertebrate endotherm species.&quot; <i>Age (Dordr)</i> <b>33</b>(1):33-47.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20567926">PubMed</a></p>

    <p align="justify">Sams-Dodd,
      F. (2005). &quot;Target-based drug discovery: is something wrong?&quot; <i>Drug
        Discov Today</i> <b>10</b>(2):139-147. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15718163">PubMed</a></p>

    <p align="justify">Sapolsky,
      R. M., Krey, L. C., and McEwen, B. S. (1986). &quot;The neuroendocrinology of
      stress and aging: the glucocorticoid cascade hypothesis.&quot; <i>Endocr Rev</i>
      <b>7</b>(3):284-301. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3527687">PubMed</a></p>

    <p align="justify">Sayin,
      V. I., Ibrahim, M. X., Larsson, E., Nilsson, J. A., Lindahl, P., and Bergo, M.
      O. (2014). &quot;Antioxidants accelerate lung cancer progression in mice.&quot;
      <i>Sci Transl Med</i> <b>6</b>(221):221ra215. <a href="http://www.ncbi.nlm.nih.gov/pubmed/24477002">PubMed</a></p>

    <p align="justify">Schmidt-Nielsen,
      K. (1984). <i>Scaling: Why is Animal Size So Important?</i> Cambridge Univ.
      Press, Cambridge.</p>

    <p align="justify">Schneider,
      E. L. (1999). &quot;Aging in the third millennium.&quot; <i>Science</i> <b>283</b>(5403):796-797.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10049123">PubMed</a></p>

    <p align="justify">Schrempf,
      A., Heinze, J., and Cremer, S. (2005). &quot;Sexual cooperation: mating
      increases longevity in ant queens.&quot; <i>Curr Biol</i> <b>15</b>(3):267-270.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15694312">PubMed</a></p>

    <p align="justify">Schriner,
      S. E., Ogburn, C. E., Smith, A. C., Newcomb, T. G., Ladiges, W. C., Dolle, M.
      E., Vijg, J., Fukuchi, K., and Martin, G. M. (2000). &quot;Levels of dna damage
      are unaltered in mice overexpressing human catalase in nuclei.&quot; <i>Free
        Radic Biol Med</i> <b>29</b>(7):664-673. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11033419">PubMed</a></p>

    <p align="justify">Schriner,
      S. E., Linford, N. J., Martin, G. M., Treuting, P., Ogburn, C. E., Emond, M.,
      Coskun, P. E., Ladiges, W., Wolf, N., Van Remmen, H.<i>, et al.</i> (2005).
      &quot;Extension of murine life span by overexpression of catalase targeted to
      mitochondria.&quot; <i>Science</i> <b>308</b>(5730):1909-1911. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15879174">PubMed</a></p>

    <p align="justify">Schulz,
      V. P., Zakian, V. A., Ogburn, C. E., McKay, J., Jarzebowicz, A. A., Edland, S.
      D., and Martin, G. M. (1996). &quot;Accelerated loss of telomeric repeats may
      not explain accelerated replicative decline of Werner syndrome cells.&quot; <i>Hum
        Genet</i> <b>97</b>(6):750-754. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8641691">PubMed</a></p>

    <p align="justify">Selman,
      C., McLaren, J. S., Meyer, C., Duncan, J. S., Redman, P., Collins, A. R.,
      Duthie, G. G., and Speakman, J. R. (2006). &quot;Life-long vitamin C
      supplementation in combination with cold exposure does not affect oxidative
      damage or lifespan in mice, but decreases expression of antioxidant protection
      genes.&quot; <i>Mech Ageing Dev</i> <b>127</b>(12):897-904. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17092545">PubMed</a>
    </p>

    <p align="justify">Selman,
      C., McLaren, J. S., Collins, A. R., Duthie, G. G., and Speakman, J. R. (2013).
      &quot;Deleterious consequences of antioxidant supplementation on lifespan in a
      wild-derived mammal.&quot; <i>Biol Lett</i> <b>9</b>(4):20130432. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/23825087">PubMed</a></p>

    <p align="justify">Seluanov,
      A., Chen, Z., Hine, C., Sasahara, T. H., Ribeiro, A. A., Catania, K. C.,
      Presgraves, D. C., and Gorbunova, V. (2007). &quot;Telomerase activity
      coevolves with body mass not lifespan.&quot; <i>Aging Cell</i> <b>6</b>(1):45-52.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17173545">PubMed</a></p>

    <p align="justify">Seluanov,
      A., Hine, C., Bozzella, M., Hall, A., Sasahara, T. H., Ribeiro, A. A., Catania,
      K. C., Presgraves, D. C., and Gorbunova, V. (2008). &quot;Distinct tumor
      suppressor mechanisms evolve in rodent species that differ in size and
      lifespan.&quot; <i>Aging Cell</i> <b>7</b>(6):813-823. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18778411">PubMed</a>
    </p>

    <p align="justify">Seluanov,
      A., Hine, C., Azpurua, J., Feigenson, M., Bozzella, M., Mao, Z., Catania, K.
      C., and Gorbunova, V. (2009). &quot;Hypersensitivity to contact inhibition
      provides a clue to cancer resistance of naked mole-rat.&quot; <i>Proc Natl Acad
        Sci U S A</i> <b>106</b>(46):19352-19357. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19858485">PubMed</a></p>

    <p align="justify">Sequeiros,
      J., Silveira, I., Maciel, P., Coutinho, P., Manaia, A., Gaspar, C., Burlet, P.,
      Loureiro, L., Guimaraes, J., Tanaka, H.<i>, et al.</i> (1994). &quot;Genetic
      linkage studies of Machado-Joseph disease with chromosome 14q STRPs in 16
      Portuguese-Azorean kindreds.&quot; <i>Genomics</i> <b>21</b>(3):645-648.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7959745">PubMed</a></p>

    <p align="justify">Serra,
      V., and von Zglinicki, T. (2002). &quot;Human fibroblasts in vitro senesce with
      a donor-specific telomere length.&quot; <i>FEBS Lett</i> <b>516</b>(1-3):71-74.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11959106">PubMed</a></p>

    <p align="justify">Serrano,
      M., Lin, A. W., McCurrach, M. E., Beach, D., and Lowe, S. W. (1997).
      &quot;Oncogenic ras provokes premature cell senescence associated with
      accumulation of p53 and p16INK4a.&quot; <i>Cell</i> <b>88</b>(5):593-602.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9054499">PubMed</a></p>

    <p align="justify">Serrano,
      M., and Blasco, M. A. (2001). &quot;Putting the stress on senescence.&quot; <i>Curr
        Opin Cell Biol</i> <b>13</b>(6):748-753. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11698192">PubMed</a></p>

    <p align="justify">Sethe,
      S., and de Magalhaes, J. P. (2013). &quot;Ethical Perspectives in
      Biogerontology.&quot; In: <i>Ethics, Health Policy and (Anti-) Aging: Mixed
        Blessings</i>, Schermer, M. and Pinxten, W. (ed.).  Springer, Dordrecht,
      Netherlands, 173-188.</p>

    <p align="justify">Shaharabany,
      M., Gollop, N., Ravin, S., Golomb, E., DeMarco, L., Ferreira, P. C., Boson, W.
      L., and Friedman, E. (1999). &quot;Naturally occurring antibacterial activities
      of avian and crocodile tissues.&quot; <i>J Antimicrob Chemother</i> <b>44</b>(3):416-418.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10511415">PubMed</a></p>

    <p align="justify">Shammas,
      M. A., Simmons, C. G., Corey, D. R., and Shmookler Reis, R. J. (1999).
      &quot;Telomerase inhibition by peptide nucleic acids reverses 'immortality' of
      transformed human cells.&quot; <i>Oncogene</i> <b>18</b>(46):6191-6200.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10597217">PubMed</a></p>

    <p align="justify">Shapiro,
      G. I., Edwards, C. D., Ewen, M. E., and Rollins, B. J. (1998). &quot;p16INK4A
      participates in a G1 arrest checkpoint in response to DNA damage.&quot; <i>Mol
        Cell Biol</i> <b>18</b>(1):378-387. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9418885">PubMed</a></p>

    <p align="justify">Shawi,
      M., and Autexier, C. (2008). &quot;Telomerase, senescence and ageing.&quot; <i>Mech
        Ageing Dev</i> <b>129</b>(1-2):3-10. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18215413">PubMed</a></p>

    <p align="justify">Shay,
      J. W., Pereira-Smith, O. M., and Wright, W. E. (1991). &quot;A role for both RB
      and p53 in the regulation of human cellular senescence.&quot; <i>Exp Cell Res</i>
      <b>196</b>(1):33-39. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1652450">PubMed</a></p>

    <p align="justify">Shay,
      J. W. (1999). &quot;At the end of the millennium, a view of the end.&quot; <i>Nat
        Genet</i> <b>23</b>(4):382-383. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10581016">PubMed</a></p>

    <p align="justify">Shay,
      J. W., and Wright, W. E. (2000). &quot;Hayflick, his limit, and cellular
      ageing.&quot; <i>Nat Rev Mol Cell Biol</i> <b>1</b>(1):72-76. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11413492">PubMed</a></p>

    <p align="justify">Shelton,
      D. N., Chang, E., Whittier, P. S., Choi, D., and Funk, W. D. (1999).
      &quot;Microarray analysis of replicative senescence.&quot; <i>Curr Biol</i> <b>9</b>(17):939-945.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10508581">PubMed</a></p>

    <p align="justify">Shen,
      J. C., and Loeb, L. A. (2000). &quot;The Werner syndrome gene: the molecular
      basis of RecQ helicase-deficiency diseases.&quot; <i>Trends Genet</i> <b>16</b>(5):213-220.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10782115">PubMed</a></p>

    <p align="justify">Shen,
      J., Tsai, Y.-T., DiMarco, N. M., Long, M. A., Sun, X., and Tang, L. (2011).
      &quot;Transplantation of mesenchymal stem cells from young donors delays aging
      in mice.&quot; <i>Scientific Reports</i> <b>1</b>:67. </p>

    <p align="justify">Sherwood,
      S. W., Rush, D., Ellsworth, J. L., and Schimke, R. T. (1988). &quot;Defining
      cellular senescence in IMR-90 cells: a flow cytometric analysis.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>85</b>(23):9086-9090. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3194411">PubMed</a>
    </p>

    <p align="justify">Shi,
      N., and Pardridge, W. M. (2000). &quot;Noninvasive gene targeting to the
      brain.&quot; <i>Proc Natl Acad Sci U S A</i> <b>97</b>(13):7567-7572.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10840060">PubMed</a></p>

    <p align="justify">Shiels,
      P. G., Kind, A. J., Campbell, K. H., Waddington, D., Wilmut, I., Colman, A.,
      and Schnieke, A. E. (1999). &quot;Analysis of telomere lengths in cloned
      sheep.&quot; <i>Nature</i> <b>399</b>(6734):316-317. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10360570">PubMed</a></p>

    <p align="justify">Shimokawa,
      I., Higami, Y., Tsuchiya, T., Otani, H., Komatsu, T., Chiba, T., and Yamaza, H.
      (2003). &quot;Life span extension by reduction of the growth
      hormone-insulin-like growth factor-1 axis: relation to caloric
      restriction.&quot; <i>Faseb J</i> <b>17</b>(9):1108-1109. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12692087">PubMed</a>
    </p>

    <p align="justify">Simon,
      A. F., Shih, C., Mack, A., and Benzer, S. (2003). &quot;Steroid control of
      longevity in Drosophila melanogaster.&quot; <i>Science</i> <b>299</b>(5611):1407-1410.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12610309">PubMed</a></p>

    <p align="justify">Simonsen,
      A., Cumming, R. C., Brech, A., Isakson, P., Schubert, D. R., and Finley, K. D.
      (2008). &quot;Promoting basal levels of autophagy in the nervous system
      enhances longevity and oxidant resistance in adult Drosophila.&quot; <i>Autophagy</i>
      <b>4</b>(2):176-184. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18059160">PubMed</a></p>

    <p align="justify">Sinclair,
      D. A., and Guarente, L. (1997). &quot;Extrachromosomal rDNA circles--a cause of
      aging in yeast.&quot; <i>Cell</i> <b>91</b>(7):1033-1042. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9428525">PubMed</a>
    </p>

    <p align="justify">Sinclair,
      D. A. (2002). &quot;Paradigms and pitfalls of yeast longevity research.&quot; <i>Mech
        Ageing Dev</i> <b>123</b>(8):857-867. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12044934">PubMed</a></p>

    <p align="justify">Sinha,
      M., Jang, Y. C., Oh, J., Khong, D., Wu, E. Y., Manohar, R., Miller, C.,
      Regalado, S. G., Loffredo, F. S., Pancoast, J. R.<i>, et al.</i> (2014).
      &quot;Restoring systemic GDF11 levels reverses age-related dysfunction in mouse
      skeletal muscle.&quot; <i>Science</i> <b>344</b>(6184):649-652. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/24797481">PubMed</a></p>

    <p align="justify">Sitte,
      N., Saretzki, G., and von Zglinicki, T. (1998). &quot;Accelerated telomere
      shortening in fibroblasts after extended periods of confluency.&quot; <i>Free
        Radic Biol Med</i> <b>24</b>(6):885-893. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9607597">PubMed</a></p>

    <p align="justify">Smirina,
      E. M. (1994). &quot;Age determination and longevity in amphibians.&quot; <i>Gerontology</i>
      <b>40</b>(2-4):133-146. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7926852">PubMed</a></p>

    <p align="justify">Smith,
      J. R., and Whitney, R. G. (1980). &quot;Intraclonal variation in proliferative
      potential of human diploid fibroblasts: stochastic mechanism for cellular
      aging.&quot; <i>Science</i> <b>207</b>(4426):82-84. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7350644">PubMed</a></p>

    <p align="justify">Smith,
      J., and Schofield, P. N. (1994). &quot;The effects of fibroblast growth factors
      in long-term primary culture of dystrophic (mdx) mouse muscle myoblasts.&quot; <i>Exp
        Cell Res</i> <b>210</b>(1):86-93. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8270001">PubMed</a></p>

    <p align="justify">Smith,
      D. E., Roberts, J., Gage, F. H., and Tuszynski, M. H. (1999).
      &quot;Age-associated neuronal atrophy occurs in the primate brain and is
      reversible by growth factor gene therapy.&quot; <i>Proc Natl Acad Sci U S A</i>
      <b>96</b>(19):10893-10898. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10485922">PubMed</a></p>

    <p align="justify">Smith-Sonneborn,
      J. (1987). &quot;Longevity in the protozoa.&quot; <i>Basic Life Sci</i> <b>42</b>:101-109.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/3124804">PubMed</a></p>

    <p align="justify">Smogorzewska,
      A., van Steensel, B., Bianchi, A., Oelmann, S., Schaefer, M. R., Schnapp, G.,
      and de Lange, T. (2000). &quot;Control of human telomere length by TRF1 and
      TRF2.&quot; <i>Mol Cell Biol</i> <b>20</b>(5):1659-1668. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10669743">PubMed</a>
    </p>

    <p align="justify">Smogorzewska,
      A., and de Lange, T. (2002). &quot;Different telomere damage signaling pathways
      in human and mouse cells.&quot; <i>Embo J</i> <b>21</b>(16):4338-4348.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12169636">PubMed</a></p>

    <p align="justify">Snyder,
      E. Y., and Loring, J. F. (2005). &quot;A role for stem cell biology in the
      physiological and pathological aspects of aging.&quot; <i>J Am Geriatr Soc</i> <b>53</b>(9
      Suppl):S287-291. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16131354">PubMed</a></p>

    <p align="justify">Sohal,
      R. S., Sohal, B. H., and Brunk, U. T. (1990). &quot;Relationship between
      antioxidant defenses and longevity in different mammalian species.&quot; <i>Mech
        Ageing Dev</i> <b>53</b>(3):217-227. <a href="http://www.ncbi.nlm.nih.gov/pubmed/2376982">PubMed</a></p>

    <p align="justify">Sohal,
      R. S., and Brunk, U. T. (1992). &quot;Mitochondrial production of pro-oxidants
      and cellular senescence.&quot; <i>Mutat Res</i> <b>275</b>(3-6):295-304.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1383771">PubMed</a></p>

    <p align="justify">Sohal,
      R. S., and Weindruch, R. (1996). &quot;Oxidative stress, caloric restriction,
      and aging.&quot; <i>Science</i> <b>273</b>(5271):59-63. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8658196">PubMed</a>
    </p>

    <p align="justify">Sohal,
      R. S., Mockett, R. J., and Orr, W. C. (2002). &quot;Mechanisms of aging: an
      appraisal of the oxidative stress hypothesis.&quot; <i>Free Radic Biol Med</i> <b>33</b>(5):575-586.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12208343">PubMed</a></p>

    <p align="justify">Sonntag,
      W. E., Lynch, C. D., Cefalu, W. T., Ingram, R. L., Bennett, S. A., Thornton, P.
      L., and Khan, A. S. (1999). &quot;Pleiotropic effects of growth hormone and
      insulin-like growth factor (IGF)-1 on biological aging: inferences from
      moderate caloric-restricted animals.&quot; <i>J Gerontol A Biol Sci Med Sci</i>
      <b>54</b>(12):B521-538. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10647962">PubMed</a></p>

    <p align="justify">Sousa-Victor,
      P., Gutarra, S., Garcia-Prat, L., Rodriguez-Ubreva, J., Ortet, L.,
      Ruiz-Bonilla, V., Jardi, M., Ballestar, E., Gonzalez, S., Serrano, A. L.<i>, et
        al.</i> (2014). &quot;Geriatric muscle stem cells switch reversible quiescence
      into senescence.&quot; <i>Nature</i> <b>506</b>(7488):316-321. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/24522534">PubMed</a></p>

    <p align="justify">Speakman,
      J. R., Talbot, D. A., Selman, C., Snart, S., McLaren, J. S., Redman, P., Krol,
      E., Jackson, D. M., Johnson, M. S., and Brand, M. D. (2004). &quot;Uncoupled
      and surviving: individual mice with high metabolism have greater mitochondrial
      uncoupling and live longer.&quot; <i>Aging Cell</i> <b>3</b>(3):87-95.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15153176">PubMed</a></p>

    <p align="justify">Speakman,
      J. R. (2005). &quot;Correlations between physiology and lifespan--two widely
      ignored problems with comparative studies.&quot; <i>Aging Cell</i> <b>4</b>(4):167-175.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16026331">PubMed</a></p>

    <p align="justify">Spence,
      A. P. (1995). <i>Biology of Human Aging</i>. Prentice Hall, Englewood Cliffs,
      NJ.</p>

    <p align="justify">Spencer,
      C. C., and Promislow, D. E. (2002a). &quot;Biologists finally horn in on
      senescence in the wild.&quot; <i>Sci Aging Knowledge Environ</i> <b>2002</b>(47):pe19.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14603017">PubMed</a></p>

    <p align="justify">Spencer,
      C. C., and Promislow, D. E. (2002b). &quot;Genes, culture, and aging flies--what
      the lab can and cannot tell us about natural genetic variation for
      senescence.&quot; <i>Sci Aging Knowledge Environ</i> <b>2002</b>(20):pe6.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14602988">PubMed</a></p>

    <p align="justify">Spindler,
      S. R. (2005). &quot;Rapid and reversible induction of the longevity, anticancer
      and genomic effects of caloric restriction.&quot; <i>Mech Ageing Dev</i> <b>126</b>(9):960-966.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15927235">PubMed</a></p>

    <p align="justify">Stanley,
      J. F., Pye, D., and MacGregor, A. (1975). &quot;Comparison of doubling numbers
      attained by cultured animal cells with life span of species.&quot; <i>Nature</i>
      <b>255</b>(5504):158-159. <a href="http://www.ncbi.nlm.nih.gov/pubmed/805376">PubMed</a></p>

    <p align="justify">Stearns,
      S. C. (1992). <i>The Evolution of Life Histories</i>. Oxford University Press,
      Oxford.</p>

    <p align="justify">Stein,
      G. H., and Dulic, V. (1998). &quot;Molecular mechanisms for the senescent cell
      cycle arrest.&quot; <i>J Investig Dermatol Symp Proc</i> <b>3</b>(1):14-18.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9732051">PubMed</a></p>

    <p align="justify">Steinert,
      S., Shay, J. W., and Wright, W. E. (2000). &quot;Transient expression of human
      telomerase extends the life span of normal human fibroblasts.&quot; <i>Biochem
        Biophys Res Commun</i> <b>273</b>(3):1095-1098. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10891377">PubMed</a>
    </p>

    <p align="justify">Steinert,
      S., White, D. M., Zou, Y., Shay, J. W., and Wright, W. E. (2002).
      &quot;Telomere biology and cellular aging in nonhuman primate cells.&quot; <i>Exp
        Cell Res</i> <b>272</b>(2):146-152. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11777339">PubMed</a></p>

    <p align="justify">Stewart,
      S. A., and Weinberg, R. A. (2000). &quot;Telomerase and human
      tumorigenesis.&quot; <i>Semin Cancer Biol</i> <b>10</b>(6):399-406. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11170862">PubMed</a></p>

    <p align="justify">Stewart,
      S. A., Ben-Porath, I., Carey, V. J., O'Connor, B. F., Hahn, W. C., and
      Weinberg, R. A. (2003). &quot;Erosion of the telomeric single-strand overhang
      at replicative senescence.&quot; <i>Nat Genet</i> <b>33</b>(4):492-496.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12652299">PubMed</a></p>

    <p align="justify">Stewart,
      E. J., Madden, R., Paul, G., and Taddei, F. (2005). &quot;Aging and death in an
      organism that reproduces by morphologically symmetric division.&quot; <i>PLoS
        Biol</i> <b>3</b>(2):e45. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15685293">PubMed</a></p>

    <p align="justify">Stock,
      G. B. (2004). &quot;The pitfalls of planning for demographic change.&quot; <i>Ann
        N Y Acad Sci</i> <b>1019</b>:546-551. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15247083">PubMed</a></p>

    <p align="justify">Storer,
      M., Mas, A., Robert-Moreno, A., Pecoraro, M., Ortells, M. C., Di Giacomo, V.,
      Yosef, R., Pilpel, N., Krizhanovsky, V., Sharpe, J.<i>, et al.</i> (2013).
      &quot;Senescence is a developmental mechanism that contributes to embryonic
      growth and patterning.&quot; <i>Cell</i> <b>155</b>(5):1119-1130. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/24238961">PubMed</a></p>

    <p align="justify">Strehler,
      B. L. (1986). &quot;Genetic instability as the primary cause of human
      aging.&quot; <i>Exp Gerontol</i> <b>21</b>(4-5):283-319. <a href="http://www.ncbi.nlm.nih.gov/pubmed/3545872">PubMed</a>
    </p>

    <p align="justify">Strehler,
      B. L. (1999). <i>Time, Cells, and Aging</i>. Demetriades Brothers, Larnaca.</p>

    <p align="justify">Su,
      A. I., Wiltshire, T., Batalov, S., Lapp, H., Ching, K. A., Block, D., Zhang,
      J., Soden, R., Hayakawa, M., Kreiman, G.<i>, et al.</i> (2004). &quot;A gene
      atlas of the mouse and human protein-encoding transcriptomes.&quot; <i>Proc
        Natl Acad Sci U S A</i> <b>101</b>(16):6062-6067. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15075390">PubMed</a></p>

    <p align="justify">Suga,
      H., Kadoshima, T., Minaguchi, M., Ohgushi, M., Soen, M., Nakano, T., Takata,
      N., Wataya, T., Muguruma, K., Miyoshi, H.<i>, et al.</i> (2011).
      &quot;Self-formation of functional adenohypophysis in three-dimensional
      culture.&quot; <i>Nature</i> <b>480</b>(7375):57-62. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/22080957">PubMed</a></p>

    <p align="justify">Sugihara,
      M., Ohshima, K., Nakamura, H., Suzumiya, J., Nakayama, Y., Kanda, M., Haraoka,
      S., and Kikuchi, M. (1999). &quot;Decreased expression of telomerase-associated
      RNAs in the proliferation of stem cells in comparison with continuous
      expression in malignant tumors.&quot; <i>Int J Oncol</i> <b>15</b>(6):1075-1080.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10568811">PubMed</a></p>

    <p align="justify">Sugita,
      K., Suzuki, N., Fujii, K., and Niimi, H. (1995). &quot;Reduction of unscheduled
      DNA synthesis and plasminogen activator activity in Hutchinson-Gilford
      fibroblasts during passaging in vitro: partial correction by interferon-beta.&quot;
      <i>Mutat Res</i> <b>316</b>(3):133-138. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7862176">PubMed</a></p>

    <p align="justify">Sugrue,
      M. M., Shin, D. Y., Lee, S. W., and Aaronson, S. A. (1997). &quot;Wild-type p53
      triggers a rapid senescence program in human tumor cells lacking functional
      p53.&quot; <i>Proc Natl Acad Sci U S A</i> <b>94</b>(18):9648-9653. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9275177">PubMed</a></p>

    <p align="justify">Suh,
      Y., Atzmon, G., Cho, M. O., Hwang, D., Liu, B., Leahy, D. J., Barzilai, N., and
      Cohen, P. (2008). &quot;Functionally significant insulin-like growth factor I
      receptor mutations in centenarians.&quot; <i>Proc Natl Acad Sci U S A</i> <b>105</b>(9):3438-3442.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18316725">PubMed</a></p>

    <p align="justify">Suhr,
      S. T., Chang, E. A., Tjong, J., Alcasid, N., Perkins, G. A., Goissis, M. D.,
      Ellisman, M. H., Perez, G. I., and Cibelli, J. B. (2010). &quot;Mitochondrial
      rejuvenation after induced pluripotency.&quot; <i>PLoS One</i> <b>5</b>(11):e14095.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21124794">PubMed</a></p>

    <p align="justify">Sun,
      D., Muthukumar, A. R., Lawrence, R. A., and Fernandes, G. (2001). &quot;Effects
      of calorie restriction on polymicrobial peritonitis induced by cecum ligation
      and puncture in young C57BL/6 mice.&quot; <i>Clin Diagn Lab Immunol</i> <b>8</b>(5):1003-1011.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11527818">PubMed</a></p>

    <p align="justify">Sutphin,
      G. L., and Kaeberlein, M. (2008). &quot;Dietary restriction by bacterial
      deprivation increases life span in wild-derived nematodes.&quot; <i>Exp
        Gerontol</i> <b>43</b>(3):130-135. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18083317">PubMed</a></p>

    <p align="justify">Symes,
      J. F., Losordo, D. W., Vale, P. R., Lathi, K. G., Esakof, D. D., Mayskiy, M.,
      and Isner, J. M. (1999). &quot;Gene therapy with vascular endothelial growth
      factor for inoperable coronary artery disease.&quot; <i>Ann Thorac Surg</i> <b>68</b>(3):830-836;
      discussion 836-837. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10509970">PubMed</a></p>

    <p align="justify">Szilard,
      L. (1959). &quot;On the nature of the aging process.&quot; <i>Proc Natl Acad
        Sci U S A</i> <b>45</b>(1):30-45. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16590351">PubMed</a></p>

    <p align="justify">Tahara,
      H., Sato, E., Noda, A., and Ide, T. (1995). &quot;Increase in expression level
      of p21sdi1/cip1/waf1 with increasing division age in both normal and
      SV40-transformed human fibroblasts.&quot; <i>Oncogene</i> <b>10</b>(5):835-840.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7898925">PubMed</a></p>

    <p align="justify">Tahara,
      H., Yasui, W., Tahara, E., Fujimoto, J., Ito, K., Tamai, K., Nakayama, J.,
      Ishikawa, F., and Ide, T. (1999). &quot;Immuno-histochemical detection of human
      telomerase catalytic component, hTERT, in human colorectal tumor and non-tumor
      tissue sections.&quot; <i>Oncogene</i> <b>18</b>(8):1561-1567. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/10102626">PubMed</a></p>

    <p align="justify">Takahashi,
      K., and Yamanaka, S. (2006). &quot;Induction of pluripotent stem cells from
      mouse embryonic and adult fibroblast cultures by defined factors.&quot; <i>Cell</i>
      <b>126</b>(4):663-676. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16904174">PubMed</a></p>

    <p align="justify">Takubo,
      K., Izumiyama-Shimomura, N., Honma, N., Sawabe, M., Arai, T., Kato, M.,
      Oshimura, M., and Nakamura, K. I. (2002). &quot;Telomere lengths are
      characteristic in each human individual.&quot; <i>Exp Gerontol</i> <b>37</b>(4):523-531.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11830355">PubMed</a></p>

    <p align="justify">Tang,
      D. G., Tokumoto, Y. M., Apperly, J. A., Lloyd, A. C., and Raff, M. C. (2001).
      &quot;Lack of replicative senescence in cultured rat oligodendrocyte precursor
      cells.&quot; <i>Science</i> <b>291</b>(5505):868-871. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11157165">PubMed</a></p>

    <p align="justify">Tang,
      F., Watkins, J. W., Bermudez, M., Gray, R., Gaban, A., Portie, K., Grace, S.,
      Kleve, M., and Craciun, G. (2008). &quot;A life-span extending form of
      autophagy employs the vacuole-vacuole fusion machinery.&quot; <i>Autophagy</i> <b>4</b>(7):874-886.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/18690010">PubMed</a></p>

    <p align="justify">Tang,
      S., Wang, J., Lee, N. C., Milone, M., Halberg, M. C., Schmitt, E. S., Craigen,
      W. J., Zhang, W., and Wong, L. J. (2011). &quot;Mitochondrial DNA polymerase
      gamma mutations: an ever expanding molecular and clinical spectrum.&quot; <i>J
        Med Genet</i> <b>48</b>(10):669-681. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21880868">PubMed</a></p>

    <p align="justify">Tanhauser,
      S. M., and Laipis, P. J. (1995). &quot;Multiple deletions are detectable in
      mitochondrial DNA of aging mice.&quot; <i>J Biol Chem</i> <b>270</b>(42):24769-24775.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7559594">PubMed</a></p>

    <p align="justify">Tardif,
      S. D., Mansfield, K. G., Ratnam, R., Ross, C. N., and Ziegler, T. E. (2011).
      &quot;The marmoset as a model of aging and age-related diseases.&quot; <i>Ilar
        J</i> <b>52</b>(1):54-65. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21411858">PubMed</a></p>

    <p align="justify">Tatar,
      M., and Yin, C. (2001). &quot;Slow aging during insect reproductive diapause:
      why butterflies, grasshoppers and flies are like worms.&quot; <i>Exp Gerontol</i>
      <b>36</b>(4-6):723-738. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11295511">PubMed</a></p>

    <p align="justify">Tatar,
      M., Bartke, A., and Antebi, A. (2003). &quot;The endocrine regulation of aging
      by insulin-like signals.&quot; <i>Science</i> <b>299</b>(5611):1346-1351.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12610294">PubMed</a></p>

    <p align="justify">Taylor,
      R. S., Ramirez, R. D., Ogoshi, M., Chaffins, M., Piatyszek, M. A., and Shay, J.
      W. (1996). &quot;Detection of telomerase activity in malignant and nonmalignant
      skin conditions.&quot; <i>J Invest Dermatol</i> <b>106</b>(4):759-765.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8618017">PubMed</a></p>

    <p align="justify">te
      Poele, R. H., Okorokov, A. L., Jardine, L., Cummings, J., and Joel, S. P.
      (2002). &quot;DNA damage is able to induce senescence in tumor cells in vitro
      and in vivo.&quot; <i>Cancer Res</i> <b>62</b>(6):1876-1883. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/11912168">PubMed</a></p>

    <p align="justify">Tesco,
      G., Vergelli, M., Grassilli, E., Salomoni, P., Bellesia, E., Sikora, E.,
      Radziszewska, E., Barbieri, D., Latorraca, S., Fagiolo, U.<i>, et al.</i>
      (1998). &quot;Growth properties and growth factor responsiveness in skin
      fibroblasts from centenarians.&quot; <i>Biochem Biophys Res Commun</i> <b>244</b>(3):912-916.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9535767">PubMed</a></p>

    <p align="justify">Teter,
      B., and Finch, C. E. (2004). &quot;Caliban's heritance and the genetics of
      neuronal aging.&quot; <i>Trends Neurosci</i> <b>27</b>(10):627-632. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/15374675">PubMed</a></p>

    <p align="justify">Theys,
      J., Barbe, S., Landuyt, W., Nuyts, S., Van Mellaert, L., Wouters, B., Anne, J.,
      and Lambin, P. (2003). &quot;Tumor-specific gene delivery using genetically
      engineered bacteria.&quot; <i>Curr Gene Ther</i> <b>3</b>(3):207-221.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12762480">PubMed</a></p>

    <p align="justify">Timiras,
      P. S. (2002). <i>Physiological Basis of Aging and Geriatrics</i>. CRC Press,
      Boca Raton, FL.</p>

    <p align="justify">Timmers,
      S., Konings, E., Bilet, L., Houtkooper, R. H., van de Weijer, T., Goossens, G.
      H., Hoeks, J., van der Krieken, S., Ryu, D., Kersten, S.<i>, et al.</i> (2011).
      &quot;Calorie Restriction-like Effects of 30 Days of Resveratrol
      Supplementation on Energy Metabolism and Metabolic Profile in Obese
      Humans.&quot; <i>Cell Metab</i> <b>14</b>(5):612-622. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/22055504">PubMed</a></p>

    <p align="justify">Tissenbaum,
      H. A., and Guarente, L. (2002). &quot;Model organisms as a guide to mammalian
      aging.&quot; <i>Dev Cell</i> <b>2</b>(1):9-19. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11782310">PubMed</a>
    </p>

    <p align="justify">Toda,
      T., Satoh, M., Sugimoto, M., Goto, M., Furuichi, Y., and Kimura, N. (1998).
      &quot;A comparative analysis of the proteins between the fibroblasts from
      Werner's syndrome patients and age-matched normal individuals using
      two-dimensional gel electrophoresis.&quot; <i>Mech Ageing Dev</i> <b>100</b>(2):133-143.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9541134">PubMed</a></p>

    <p align="justify">Tolmasoff,
      J. M., Ono, T., and Cutler, R. G. (1980). &quot;Superoxide dismutase:
      correlation with life-span and specific metabolic rate in primate
      species.&quot; <i>Proc Natl Acad Sci U S A</i> <b>77</b>(5):2777-2781.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/6771758">PubMed</a></p>

    <p align="justify">Tomaru,
      U., Takahashi, S., Ishizu, A., Miyatake, Y., Gohda, A., Suzuki, S., Ono, A.,
      Ohara, J., Baba, T., Murata, S.<i>, et al.</i> (2012). &quot;Decreased
      proteasomal activity causes age-related phenotypes and promotes the development
      of metabolic abnormalities.&quot; <i>Am J Pathol</i> <b>180</b>(3):963-972.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22210478">PubMed</a></p>

    <p align="justify">Tomas-Loba,
      A., Flores, I., Fernandez-Marcos, P. J., Cayuela, M. L., Maraver, A., Tejera,
      A., Borras, C., Matheu, A., Klatt, P., Flores, J. M.<i>, et al.</i> (2008).
      &quot;Telomerase reverse transcriptase delays aging in cancer-resistant
      mice.&quot; <i>Cell</i> <b>135</b>(4):609-622. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19013273">PubMed</a>
    </p>

    <p align="justify">Toth,
      M. L., Sigmond, T., Borsos, E., Barna, J., Erdelyi, P., Takacs-Vellai, K.,
      Orosz, L., Kovacs, A. L., Csikos, G., Sass, M.<i>, et al.</i> (2008).
      &quot;Longevity pathways converge on autophagy genes to regulate life span in
      Caenorhabditis elegans.&quot; <i>Autophagy</i> <b>4</b>(3):330-338. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/18219227">PubMed</a></p>

    <p align="justify">Toussaint,
      O., Dumont, P., Dierick, J. F., Pascal, T., Frippiat, C., Chainiaux, F., Sluse,
      F., Eliaers, F., and Remacle, J. (2000). &quot;Stress-induced premature
      senescence. Essence of life, evolution, stress, and aging.&quot; <i>Ann N Y
        Acad Sci</i> <b>908</b>:85-98. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911950">PubMed</a></p>

    <p align="justify">Toussaint,
      O., Dumont, P., Remacle, J., Dierick, J. F., Pascal, T., Frippiat, C.,
      Magalhaes, J. P., Zdanov, S., and Chainiaux, F. (2002a). &quot;Stress-induced
      premature senescence or stress-induced senescence-like phenotype: one in vivo
      reality, two possible definitions?&quot; <i>ScientificWorldJournal</i> <b>2</b>(1):230-247.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12806055">PubMed</a></p>

    <p align="justify">Toussaint,
      O., Remacle, J., Dierick, J. F., Pascal, T., Frippiat, C., Zdanov, S.,
      Magalhaes, J. P., Royer, V., and Chainiaux, F. (2002b). &quot;From the Hayflick
      mosaic to the mosaics of ageing. Role of stress-induced premature senescence in
      human ageing.&quot; <i>Int J Biochem Cell Biol</i> <b>34</b>(11):1415-1429.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12200036">PubMed</a></p>

    <p align="justify">Tower,
      J. (2000). &quot;Transgenic methods for increasing Drosophila life span.&quot; <i>Mech
        Ageing Dev</i> <b>118</b>(1-2):1-14. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10989120">PubMed</a></p>

    <p align="justify">Townes-Anderson,
      E., Colantonio, A., and St Jules, R. S. (1998). &quot;Age-related changes in
      the tiger salamander retina.&quot; <i>Exp Eye Res</i> <b>66</b>(5):653-667.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9631666">PubMed</a></p>

    <p align="justify">Tredici,
      G., Miloso, M., Nicolini, G., Galbiati, S., Cavaletti, G., and Bertelli, A.
      (1999). &quot;Resveratrol, map kinases and neuronal cells: might wine be a
      neuroprotectant?&quot; <i>Drugs Exp Clin Res</i> <b>25</b>(2-3):99-103.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10370870">PubMed</a></p>

    <p align="justify">Trewavas,
      A. (2002). &quot;Malthus foiled again and again.&quot; <i>Nature</i> <b>418</b>(6898):668-670.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12167872">PubMed</a></p>

    <p align="justify">Trifunovic,
      A., Wredenberg, A., Falkenberg, M., Spelbrink, J. N., Rovio, A. T., Bruder, C.
      E., Bohlooly, Y. M., Gidlof, S., Oldfors, A., Wibom, R.<i>, et al.</i> (2004).
      &quot;Premature ageing in mice expressing defective mitochondrial DNA
      polymerase.&quot; <i>Nature</i> <b>429</b>(6990):417-423. <a href="http://www.ncbi.nlm.nih.gov/pubmed/15164064">PubMed</a>
    </p>

    <p align="justify">Trifunovic,
      A., Hansson, A., Wredenberg, A., Rovio, A. T., Dufour, E., Khvorostov, I.,
      Spelbrink, J. N., Wibom, R., Jacobs, H. T., and Larsson, N. G. (2005).
      &quot;Somatic mtDNA mutations cause aging phenotypes without affecting reactive
      oxygen species production.&quot; <i>Proc Natl Acad Sci U S A</i> <b>102</b>(50):17993-17998.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16332961">PubMed</a></p>

    <p align="justify">Trinei,
      M., Giorgio, M., Cicalese, A., Barozzi, S., Ventura, A., Migliaccio, E., Milia,
      E., Padura, I. M., Raker, V. A., Maccarana, M.<i>, et al.</i> (2002). &quot;A
      p53-p66Shc signalling pathway controls intracellular redox status, levels of
      oxidation-damaged DNA and oxidative stress-induced apoptosis.&quot; <i>Oncogene</i>
      <b>21</b>(24):3872-3878. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12032825">PubMed</a></p>

    <p align="justify">Tsuzuki,
      T., Egashira, A., Igarashi, H., Iwakuma, T., Nakatsuru, Y., Tominaga, Y.,
      Kawate, H., Nakao, K., Nakamura, K., Ide, F.<i>, et al.</i> (2001).
      &quot;Spontaneous tumorigenesis in mice defective in the MTH1 gene encoding
      8-oxo-dGTPase.&quot; <i>Proc Natl Acad Sci U S A</i> <b>98</b>(20):11456-11461.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11572992">PubMed</a></p>

    <p align="justify">Turbill,
      C., and Ruf, T. (2010). &quot;Senescence is more important in the natural lives
      of long- than short-lived mammals.&quot; <i>PLoS One</i> <b>5</b>(8):e12019.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20700508">PubMed</a></p>

    <p align="justify">Turbill,
      C., Bieber, C., and Ruf, T. (2011). &quot;Hibernation is associated with
      increased survival and the evolution of slow life histories among
      mammals.&quot; <i>Proc Biol Sci</i> <b>278</b>(1723):3355-3363. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/21450735">PubMed</a></p>

    <p align="justify">Tuschl,
      T. (2002). &quot;Expanding small RNA interference.&quot; <i>Nat Biotechnol</i> <b>20</b>(5):446-448.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11981553">PubMed</a></p>

    <p align="justify">Tyner,
      S. D., Venkatachalam, S., Choi, J., Jones, S., Ghebranious, N., Igelmann, H.,
      Lu, X., Soron, G., Cooper, B., Brayton, C.<i>, et al.</i> (2002). &quot;p53
      mutant mice that display early ageing-associated phenotypes.&quot; <i>Nature</i>
      <b>415</b>(6867):45-53. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11780111">PubMed</a></p>

    <p align="justify">Tyynismaa,
      H., Mjosund, K. P., Wanrooij, S., Lappalainen, I., Ylikallio, E., Jalanko, A.,
      Spelbrink, J. N., Paetau, A., and Suomalainen, A. (2005). &quot;Mutant
      mitochondrial helicase Twinkle causes multiple mtDNA deletions and a late-onset
      mitochondrial disease in mice.&quot; <i>Proc Natl Acad Sci U S A</i> <b>102</b>(49):17687-17692.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/16301523">PubMed</a></p>

    <p align="justify">Ueda,
      M., Ouhtit, A., Bito, T., Nakazawa, K., Lubbe, J., Ichihashi, M., Yamasaki, H.,
      and Nakazawa, H. (1997). &quot;Evidence for UV-associated activation of
      telomerase in human skin.&quot; <i>Cancer Res</i> <b>57</b>(3):370-374.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9012457">PubMed</a></p>

    <p align="justify">Umeda-Kameyama,
      Y., Tsuda, M., Ohkura, C., Matsuo, T., Namba, Y., Ohuchi, Y., and Aigaki, T.
      (2007). &quot;Thioredoxin suppresses Parkin-associated endothelin receptor-like
      receptor-induced neurotoxicity and extends longevity in Drosophila.&quot; <i>J
        Biol Chem</i> <b>282</b>(15):11180-11187. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17301052">PubMed</a></p>

    <p align="justify">Unal,
      E., Kinde, B., and Amon, A. (2011). &quot;Gametogenesis eliminates age-induced

      cellular damage and resets life span in yeast.&quot; <i>Science</i> <b>332</b>(6037):1554-1557.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21700873">PubMed</a></p>

    <p align="justify">Unwin,
      M. J., Kinnison, M. T., and Quinn, T. P. (1999). &quot;Exceptions to
      semelparity: postmaturation survival, morphology, and energetics of male
      chinook salmon (Oncorhynchus tshawytscha).&quot; <i>Can J Fish Aquat Sci,</i> <b>56</b>(7):1172-1181.
    </p>

    <p align="justify">Valdesalici,
      S., and Cellerino, A. (2003). &quot;Extremely short lifespan in the annual fish
      Nothobranchius furzeri.&quot; <i>Proc Biol Sci</i> <b>270 Suppl 2</b>:S189-191.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/14667379">PubMed</a></p>

    <p align="justify">Van
      Goethem, G., Dermaut, B., Lofgren, A., Martin, J. J., and Van Broeckhoven, C.
      (2001). &quot;Mutation of POLG is associated with progressive external
      ophthalmoplegia characterized by mtDNA deletions.&quot; <i>Nat Genet</i> <b>28</b>(3):211-212.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11431686">PubMed</a></p>

    <p align="justify">van
      Loon, A. A., Sonneveld, E., Hoogerbrugge, J., van der Schans, G. P.,
      Grootegoed, J. A., Lohman, P. H., and Baan, R. A. (1993). &quot;Induction and
      repair of DNA single-strand breaks and DNA base damage at different cellular
      stages of spermatogenesis of the hamster upon in vitro exposure to ionizing
      radiation.&quot; <i>Mutat Res</i> <b>294</b>(2):139-148. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7687006">PubMed</a>
    </p>

    <p align="justify">Van
      Raamsdonk, J. M., and Hekimi, S. (2009). &quot;Deletion of the mitochondrial
      superoxide dismutase sod-2 extends lifespan in Caenorhabditis elegans.&quot; <i>PLoS
        Genet</i> <b>5</b>(2):e1000361. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19197346">PubMed</a></p>

    <p align="justify">Van
      Remmen, H., Ikeno, Y., Hamilton, M., Pahlavani, M., Wolf, N., Thorpe, S. R.,
      Alderson, N. L., Baynes, J. W., Epstein, C. J., Huang, T. T.<i>, et al.</i>
      (2003). &quot;Life-long reduction in MnSOD activity results in increased DNA
      damage and higher incidence of cancer but does not accelerate aging.&quot; <i>Physiol
        Genomics</i> <b>16</b>(1):29-37. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14679299">PubMed</a></p>

    <p align="justify">van
      Steensel, B., and de Lange, T. (1997). &quot;Control of telomere length by the
      human telomeric protein TRF1.&quot; <i>Nature</i> <b>385</b>(6618):740-743. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/9034193">PubMed</a></p>

    <p align="justify">van
      Steensel, B., Smogorzewska, A., and de Lange, T. (1998). &quot;TRF2 protects
      human telomeres from end-to-end fusions.&quot; <i>Cell</i> <b>92</b>(3):401-413.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9476899">PubMed</a></p>

    <p align="justify">Van
      Zant, G., and Liang, Y. (2003). &quot;The role of stem cells in aging.&quot; <i>Exp
        Hematol</i> <b>31</b>(8):659-672. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12901970">PubMed</a></p>

    <p align="justify">Vanfleteren,
      J. R., and Braeckman, B. P. (1999). &quot;Mechanisms of life span determination
      in Caenorhabditis elegans.&quot; <i>Neurobiol Aging</i> <b>20</b>(5):487-502.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10638522">PubMed</a></p>

    <p align="justify">Varela,
      E., and Blasco, M. A. (2010). &quot;2009 nobel prize in physiology or medicine:
      telomeres and telomerase.&quot; <i>Oncogene</i> <b>29</b>(11):1561-1565.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/20237481">PubMed</a></p>

    <p align="justify">Varley,
      J. M., Evans, D. G., and Birch, J. M. (1997). &quot;Li-Fraumeni syndrome--a
      molecular and clinical review.&quot; <i>Br J Cancer</i> <b>76</b>(1):1-14.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9218725">PubMed</a></p>

    <p align="justify">Varon,
      R., Vissinga, C., Platzer, M., Cerosaletti, K. M., Chrzanowska, K. H., Saar,
      K., Beckmann, G., Seemanova, E., Cooper, P. R., Nowak, N. J.<i>, et al.</i>
      (1998). &quot;Nibrin, a novel DNA double-strand break repair protein, is
      mutated in Nijmegen breakage syndrome.&quot; <i>Cell</i> <b>93</b>(3):467-476.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9590180">PubMed</a></p>

    <p align="justify">Vaupel,
      J. W., Manton, K. G., and Stallard, E. (1979). &quot;The impact of
      heterogeneity in individual frailty on the dynamics of mortality.&quot; <i>Demography</i>
      <b>16</b>(3):439-454. <a href="http://www.ncbi.nlm.nih.gov/pubmed/510638">PubMed</a></p>

    <p align="justify">Vaupel,
      J. W., Carey, J. R., Christensen, K., Johnson, T. E., Yashin, A. I., Holm, N.
      V., Iachine, I. A., Kannisto, V., Khazaeli, A. A., Liedo, P.<i>, et al.</i>
      (1998). &quot;Biodemographic trajectories of longevity.&quot; <i>Science</i> <b>280</b>(5365):855-860.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9599158">PubMed</a></p>

    <p align="justify">Vaupel,
      J. W., Baudisch, A., Dolling, M., Roach, D. A., and Gampe, J. (2004). &quot;The
      case for negative senescence.&quot; <i>Theor Popul Biol</i> <b>65</b>(4):339-351.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15136009">PubMed</a></p>

    <p align="justify">Vaziri,
      H., and Benchimol, S. (1996). &quot;From telomere loss to p53 induction and
      activation of a DNA-damage pathway at senescence: the telomere loss/DNA damage
      model of cell aging.&quot; <i>Exp Gerontol</i> <b>31</b>(1-2):295-301.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8706799">PubMed</a></p>

    <p align="justify">Vaziri,
      H., West, M. D., Allsopp, R. C., Davison, T. S., Wu, Y. S., Arrowsmith, C. H.,
      Poirier, G. G., and Benchimol, S. (1997). &quot;ATM-dependent telomere loss in
      aging human diploid fibroblasts and DNA damage lead to the post-translational
      activation of p53 protein involving poly(ADP-ribose) polymerase.&quot; <i>Embo
        J</i> <b>16</b>(19):6018-6033. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9312059">PubMed</a></p>

    <p align="justify">Vermulst,
      M., Bielas, J. H., Kujoth, G. C., Ladiges, W. C., Rabinovitch, P. S., Prolla,
      T. A., and Loeb, L. A. (2007). &quot;Mitochondrial point mutations do not limit
      the natural lifespan of mice.&quot; <i>Nat Genet</i> <b>39</b>(4):540-543.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/17334366">PubMed</a></p>

    <p align="justify">Vijg,
      J. (2000). &quot;Somatic mutations and aging: a re-evaluation.&quot; <i>Mutat
        Res</i> <b>447</b>(1):117-135. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10686308">PubMed</a></p>

    <p align="justify">Vijg,
      J., and Dolle, M. E. (2002). &quot;Large genome rearrangements as a primary
      cause of aging.&quot; <i>Mech Ageing Dev</i> <b>123</b>(8):907-915. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/12044939">PubMed</a></p>

    <p align="justify">Villeda,
      S. A., Luo, J., Mosher, K. I., Zou, B., Britschgi, M., Bieri, G., Stan, T. M.,
      Fainberg, N., Ding, Z., Eggel, A.<i>, et al.</i> (2011). &quot;The ageing systemic
      milieu negatively regulates neurogenesis and cognitive function.&quot; <i>Nature</i>
      <b>477</b>(7362):90-94. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21886162">PubMed</a></p>

    <p align="justify">Villeda,
      S. A., Plambeck, K. E., Middeldorp, J., Castellano, J. M., Mosher, K. I., Luo,
      J., Smith, L. K., Bieri, G., Lin, K., Berdnik, D.<i>, et al.</i> (2014).
      &quot;Young blood reverses age-related impairments in cognitive function and
      synaptic plasticity in mice.&quot; <i>Nat Med</i> <b>20</b>(6):659-663.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/24793238">PubMed</a></p>

    <p align="justify">Viswanathan,
      M., and Guarente, L. (2011). &quot;Regulation of Caenorhabditis elegans
      lifespan by sir-2.1 transgenes.&quot; <i>Nature</i> <b>477</b>(7365):E1-2.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/21938026">PubMed</a></p>

    <p align="justify">Vleck,
      C. M., Haussmann, M. F., and Vleck, D. (2003). &quot;The natural history of
      telomeres: tools for aging animals and exploring the aging process.&quot; <i>Exp
        Gerontol</i> <b>38</b>(7):791-795. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12855289">PubMed</a></p>

    <p align="justify">Vogel,
      E. W., and Zijlstra, J. A. (1987). &quot;Somatic cell mutagenicity in
      Drosophila melanogaster in comparison with genetic damage in early germ-cell
      stages.&quot; <i>Mutat Res</i> <b>180</b>(2):189-200. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/3116419">PubMed</a></p>

    <p align="justify">Vogt,
      M., Haggblom, C., Yeargin, J., Christiansen-Weber, T., and Haas, M. (1998).
      &quot;Independent induction of senescence by p16INK4a and p21CIP1 in
      spontaneously immortalized human fibroblasts.&quot; <i>Cell Growth Differ</i> <b>9</b>(2):139-146.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9486850">PubMed</a></p>

    <p align="justify">Voituron,
      Y., de Fraipont, M., Issartel, J., Guillaume, O., and Clobert, J. (2011).
      &quot;Extreme lifespan of the human fish (Proteus anguinus): a challenge for
      ageing mechanisms.&quot; <i>Biol Lett</i> <b>7</b>(1):105-107. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/20659920">PubMed</a></p>

    <p align="justify">von
      Zglinicki, T., Saretzki, G., Docke, W., and Lotze, C. (1995). &quot;Mild
      hyperoxia shortens telomeres and inhibits proliferation of fibroblasts: a model
      for senescence?&quot; <i>Exp Cell Res</i> <b>220</b>(1):186-193. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/7664835">PubMed</a></p>

    <p align="justify">von
      Zglinicki, T. (2000). &quot;Role of oxidative stress in telomere length
      regulation and replicative senescence.&quot; <i>Ann N Y Acad Sci</i> <b>908</b>:99-110.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10911951">PubMed</a></p>

    <p align="justify">Vreeland,
      R. H., Rosenzweig, W. D., and Powers, D. W. (2000). &quot;Isolation of a 250
      million-year-old halotolerant bacterium from a primary salt crystal.&quot; <i>Nature</i>
      <b>407</b>(6806):897-900. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11057666">PubMed</a></p>

    <p align="justify">Vulliamy,
      T., Marrone, A., Goldman, F., Dearlove, A., Bessler, M., Mason, P. J., and
      Dokal, I. (2001). &quot;The RNA component of telomerase is mutated in autosomal
      dominant dyskeratosis congenita.&quot; <i>Nature</i> <b>413</b>(6854):432-435.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11574891">PubMed</a></p>

    <p align="justify">Wahlestedt,
      M., Norddahl, G. L., Sten, G., Ugale, A., Frisk, M. A., Mattsson, R.,
      Deierborg, T., Sigvardsson, M., and Bryder, D. (2013). &quot;An epigenetic
      component of hematopoietic stem cell aging amenable to reprogramming into a
      young state.&quot; <i>Blood</i> <b>121</b>(21):4257-4264. <a href="http://www.ncbi.nlm.nih.gov/pubmed/23476050">PubMed</a>
    </p>

    <p align="justify">Wakayama,
      T., Shinkai, Y., Tamashiro, K. L., Niida, H., Blanchard, D. C., Blanchard, R.
      J., Ogura, A., Tanemura, K., Tachibana, M., Perry, A. C.<i>, et al.</i> (2000).
      &quot;Cloning of mice to six generations.&quot; <i>Nature</i> <b>407</b>(6802):318-319.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11014179">PubMed</a></p>

    <p align="justify">Wallace,
      D. C. (1992). &quot;Mitochondrial genetics: a paradigm for aging and
      degenerative diseases?&quot; <i>Science</i> <b>256</b>(5057):628-632. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/1533953">PubMed</a></p>

    <p align="justify">Wallace,
      D. C. (1999). &quot;Mitochondrial diseases in man and mouse.&quot; <i>Science</i>
      <b>283</b>(5407):1482-1488. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10066162">PubMed</a></p>

    <p align="justify">Walter,
      C. A., Lu, J., Bhakta, M., Zhou, Z. Q., Thompson, L. H., and McCarrey, J. R.
      (1994). &quot;Testis and somatic Xrcc-1 DNA repair gene expression.&quot; <i>Somat
        Cell Mol Genet</i> <b>20</b>(6):451-461. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7892645">PubMed</a></p>

    <p align="justify">Walter,
      C. A., Walter, R. B., and McCarrey, J. R. (2003). &quot;Germline genomes--a
      biological fountain of youth?&quot; <i>Sci SAGE KE</i> <b>2003</b>(8):PE4.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12844546">PubMed</a></p>

    <p align="justify">Wang,
      S. M., Nishigori, C., Yagi, T., and Takebe, H. (1991). &quot;Reduced DNA repair
      in progeria cells and effects of gamma-ray irradiation on UV-induced
      unscheduled DNA synthesis in normal and progeria cells.&quot; <i>Mutat Res</i> <b>256</b>(1):59-66.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1944388">PubMed</a></p>

    <p align="justify">Wang,
      X., Tsao, S. W., Wong, Y. C., and Cheung, A. L. (2003). &quot;Induction of
      senescent-like growth arrest as a new target in anticancer treatment.&quot; <i>Curr
        Cancer Drug Targets</i> <b>3</b>(2):153-159. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12678717">PubMed</a>
    </p>

    <p align="justify">Wang,
      C., Jurk, D., Maddick, M., Nelson, G., Martin-Ruiz, C., and von Zglinicki, T.
      (2009). &quot;DNA damage response and cellular senescence in tissues of aging
      mice.&quot; <i>Aging Cell</i> <b>8</b>(3):311-323. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19627270">PubMed</a></p>

    <p align="justify">Wang,
      J., Geesman, G. J., Hostikka, S. L., Atallah, M., Blackwell, B., Lee, E., Cook,
      P. J., Pasaniuc, B., Shariat, G., Halperin, E.<i>, et al.</i> (2011).
      &quot;Inhibition of activated pericentromeric SINE/Alu repeat transcription in
      senescent human adult stem cells reinstates self-renewal.&quot; <i>Cell Cycle</i>
      <b>10</b>(17):3016-3030. <a href="http://www.ncbi.nlm.nih.gov/pubmed/21862875">PubMed</a></p>

    <p align="justify">Warner,
      H. R. (2001). &quot;Support for basic gerontological research in the USA.&quot;
      <i>Exp Gerontol</i> <b>36</b>(3):403-412. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11250113">PubMed</a></p>

    <p align="justify">Warner,
      H., Miller, R. A., and Carrington, J. (2002). &quot;Meeting report--National
      Institute on Aging Workshop on the Comparative Biology of Aging.&quot; <i>Sci
        Aging Knowledge Environ</i> <b>2002</b>(17):pe5. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/14602985">PubMed</a></p>

    <p align="justify">Warner,
      H. R. (2003). &quot;Subfield history: use of model organisms in the search for
      human aging genes.&quot; <i>Sci Aging Knowl Environ</i> <b>2003</b>(6):RE1.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12844551">PubMed</a></p>

    <p align="justify">Warner,
      H., Anderson, J., Austad, S., Bergamini, E., Bredesen, D., Butler, R., Carnes,
      B. A., Clark, B. F., Cristofalo, V., Faulkner, J.<i>, et al.</i> (2005).
      &quot;Science fact and the SENS agenda. What can we reasonably expect from
      ageing research?&quot; <i>EMBO Rep</i> <b>6</b>(11):1006-1008. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16264422">PubMed</a></p>

    <p align="justify">Warner,
      H. R. (2006). &quot;Scientific and ethical concerns regarding engineering human
      longevity.&quot; <i>Rejuvenation Res</i> <b>9</b>(4):440-442; discussion
      443-445. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17105382">PubMed</a></p>

    <p align="justify">Waters,
      H., and Walford, R. L. (1970). &quot;Latent period for outgrowth of human skin
      explants as a function of age.&quot; <i>J Gerontol</i> <b>25</b>(4):381-383.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/5456529">PubMed</a></p>

    <p align="justify">Watkinson,
      A. R., and White, J. (1985). &quot;Some life-history consequences of modular
      construction in plants.&quot; <i>Phil Trans Ro Soc</i> <b>313</b>:31-51. </p>

    <p align="justify">Watson,
      J. D. (1972). &quot;Origin of concatemeric T7 DNA.&quot; <i>Nat New Biol</i> <b>239</b>(94):197-201.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/4507727">PubMed</a></p>

    <p align="justify">Weeda,
      G., Donker, I., de Wit, J., Morreau, H., Janssens, R., Vissers, C. J., Nigg,
      A., van Steeg, H., Bootsma, D., and Hoeijmakers, J. H. (1997). &quot;Disruption
      of mouse ERCC1 results in a novel repair syndrome with growth failure, nuclear
      abnormalities and senescence.&quot; <i>Curr Biol</i> <b>7</b>(6):427-439.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9197240">PubMed</a></p>

    <p align="justify">Wei,
      W., and Sedivy, J. M. (1999). &quot;Differentiation between senescence (M1) and
      crisis (M2) in human fibroblast cultures.&quot; <i>Exp Cell Res</i> <b>253</b>(2):519-522.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10585275">PubMed</a></p>

    <p align="justify">Weindruch,
      R., Walford, R. L., Fligiel, S., and Guthrie, D. (1986). &quot;The retardation
      of aging in mice by dietary restriction: longevity, cancer, immunity and
      lifetime energy intake.&quot; <i>J Nutr</i> <b>116</b>(4):641-654. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/3958810">PubMed</a></p>

    <p align="justify">Weindruch,
      R., and Walford, R. L. (1988). <i>The Retardation of Aging and Disease by
        Dietary Restriction</i>. C. C. Thomas, Springfield.</p>

    <p align="justify">Weindruch,
      R. (1996). &quot;The retardation of aging by caloric restriction: studies in
      rodents and primates.&quot; <i>Toxicol Pathol</i> <b>24</b>(6):742-745.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8994305">PubMed</a></p>

    <p align="justify">Weinert,
      B. T., and Timiras, P. S. (2003). &quot;Invited review: Theories of
      aging.&quot; <i>J Appl Physiol</i> <b>95</b>(4):1706-1716. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12970376">PubMed</a>
    </p>

    <p align="justify">Weintraub,
      S. J., Prater, C. A., and Dean, D. C. (1992). &quot;Retinoblastoma protein
      switches the E2F site from positive to negative element.&quot; <i>Nature</i> <b>358</b>(6383):259-261.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1321348">PubMed</a></p>

    <p align="justify">Weischer,
      M., Bojesen, S. E., and Nordestgaard, B. G. (2014). &quot;Telomere shortening
      unrelated to smoking, body weight, physical activity, and alcohol intake: 4,576
      general population individuals with repeat measurements 10 years apart.&quot; <i>PLoS
        Genet</i> <b>10</b>(3):e1004191. <a href="http://www.ncbi.nlm.nih.gov/pubmed/24625632">PubMed</a></p>

    <p align="justify">Weismann,
      A. (1891). <i>On Heredity</i>. Claredon Press, Oxford.</p>

    <p align="justify">Wellinger,
      R. J., Ethier, K., Labrecque, P., and Zakian, V. A. (1996). &quot;Evidence for
      a new step in telomere maintenance.&quot; <i>Cell</i> <b>85</b>(3):423-433.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8616897">PubMed</a></p>

    <p align="justify">Werness,
      B. A., Levine, A. J., and Howley, P. M. (1990). &quot;Association of human
      papillomavirus types 16 and 18 E6 proteins with p53.&quot; <i>Science</i> <b>248</b>(4951):76-79.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/2157286">PubMed</a></p>

    <p align="justify">White,
      R. J., Albin, M. S., Verdura, J., Takaoka, Y., Massopust, L. C., Wolin, L. R.,
      Locke, G. E., Taslitz, N., and Yashon, D. (1996). &quot;The isolation and
      transplantation of the brain. An historical perspective emphasizing the
      surgical solutions to the design of these classical models.&quot; <i>Neurol Res</i>
      <b>18</b>(3):194-203. <a href="http://www.ncbi.nlm.nih.gov/pubmed/8837051">PubMed</a></p>

    <p align="justify">Whitlock,
      G., Lewington, S., Sherliker, P., Clarke, R., Emberson, J., Halsey, J.,
      Qizilbash, N., Collins, R., and Peto, R. (2009). &quot;Body-mass index and
      cause-specific mortality in 900 000 adults: collaborative analyses of 57
      prospective studies.&quot; <i>Lancet</i> <b>373</b>(9669):1083-1096. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/19299006">PubMed</a></p>

    <p align="justify">Wiley,
      C. (2005). &quot;Nanotechnology and molecular homeostasis.&quot; <i>J Am
        Geriatr Soc</i> <b>53</b>(9 Suppl):S295-298. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16131356">PubMed</a>
    </p>

    <p align="justify">Willcox,
      D. C., Willcox, B. J., Todoriki, H., Curb, J. D., and Suzuki, M. (2006).
      &quot;Caloric restriction and human longevity: what can we learn from the
      Okinawans?&quot; <i>Biogerontology</i> <b>7</b>(3):173-177. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16810568">PubMed</a>
    </p>

    <p align="justify">Williams,
      G. C. (1957). &quot;Pleiotropy, natural selection, and the evolution of
      senescence.&quot; <i>Evolution</i> <b>11</b>:398-411. </p>

    <p align="justify">Wilmut,
      I., Schnieke, A. E., McWhir, J., Kind, A. J., and Campbell, K. H. (1997).
      &quot;Viable offspring derived from fetal and adult mammalian cells.&quot; <i>Nature</i>
      <b>385</b>(6619):810-813. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9039911">PubMed</a></p>

    <p align="justify">Wilson,
      D. L. (1994). &quot;The analysis of survival (mortality) data: fitting
      Gompertz, Weibull, and logistic functions.&quot; <i>Mech Ageing Dev</i> <b>74</b>(1-2):15-33.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7934205">PubMed</a></p>

    <p align="justify">Witte,
      A. V., Fobker, M., Gellner, R., Knecht, S., and Floel, A. (2009). &quot;Caloric
      restriction improves memory in elderly humans.&quot; <i>Proc Natl Acad Sci U S
        A</i> <b>106</b>(4):1255-1260. <a href="http://www.ncbi.nlm.nih.gov/pubmed/19171901">PubMed</a></p>

    <p align="justify">Wodinsky,
      J. (1977). &quot;Hormonal inhibition of feeding of death in octopus. Control by
      optic gland secretion.&quot; <i>Science</i> <b>198</b>:948-951. </p>

    <p align="justify">Wolf,
      N., Penn, P., Pendergrass, W., Van Remmen, H., Bartke, A., Rabinovitch, P., and
      Martin, G. M. (2005). &quot;Age-related cataract progression in five mouse
      models for anti-oxidant protection or hormonal influence.&quot; <i>Exp Eye Res</i>
      <b>81</b>(3):276-285. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16129095">PubMed</a></p>

    <p align="justify">Wolkow,
      C. A., Kimura, K. D., Lee, M. S., and Ruvkun, G. (2000). &quot;Regulation of C.
      elegans life-span by insulinlike signaling in the nervous system.&quot; <i>Science</i>
      <b>290</b>(5489):147-150. <a href="http://www.ncbi.nlm.nih.gov/pubmed/11021802">PubMed</a></p>

    <p align="justify">Wong,
      H., and Riabowol, K. (1996). &quot;Differential CDK-inhibitor gene expression
      in aging human diploid fibroblasts.&quot; <i>Exp Gerontol</i> <b>31</b>(1-2):311-325.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8706801">PubMed</a></p>

    <p align="justify">Wong,
      A., Yang, J., Cavadini, P., Gellera, C., Lonnerdal, B., Taroni, F., and
      Cortopassi, G. (1999). &quot;The Friedreich's ataxia mutation confers cellular
      sensitivity to oxidant stress which is rescued by chelators of iron and calcium
      and inhibitors of apoptosis.&quot; <i>Hum Mol Genet</i> <b>8</b>(3):425-430.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9949201">PubMed</a></p>

    <p align="justify">Wong,
      K. K., Maser, R. S., Bachoo, R. M., Menon, J., Carrasco, D. R., Gu, Y., Alt, F.
      W., and DePinho, R. A. (2003). &quot;Telomere dysfunction and Atm deficiency
      compromises organ homeostasis and accelerates ageing.&quot; <i>Nature</i> <b>421</b>(6923):643-648.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12540856">PubMed</a></p>

    <p align="justify">Wong,
      E., and Cuervo, A. M. (2010). &quot;Autophagy gone awry in neurodegenerative
      diseases.&quot; <i>Nat Neurosci</i> <b>13</b>(7):805-811. <a href="http://www.ncbi.nlm.nih.gov/pubmed/20581817">PubMed</a>
    </p>

    <p align="justify">Wood,
      J. G., Rogina, B., Lavu, S., Howitz, K., Helfand, S. L., Tatar, M., and
      Sinclair, D. (2004). &quot;Sirtuin activators mimic caloric restriction and
      delay ageing in metazoans.&quot; <i>Nature</i> <b>430</b>(7000):686-689.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/15254550">PubMed</a></p>

    <p align="justify">Wright,
      W. E., and Hayflick, L. (1975). &quot;Nuclear control of cellular aging
      demonstrated by hybridization of anucleate and whole cultured normal human
      fibroblasts.&quot; <i>Exp Cell Res</i> <b>96</b>(1):113-121. <a href="http://www.ncbi.nlm.nih.gov/pubmed/172352">PubMed</a>
    </p>

    <p align="justify">Wright,
      W. E., and Shay, J. W. (2001). &quot;Cellular senescence as a tumor-protection
      mechanism: the essential role of counting.&quot; <i>Curr Opin Genet Dev</i> <b>11</b>(1):98-103.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11163158">PubMed</a></p>

    <p align="justify">Wyllie,
      F. S., Jones, C. J., Skinner, J. W., Haughton, M. F., Wallis, C.,
      Wynford-Thomas, D., Faragher, R. G., and Kipling, D. (2000). &quot;Telomerase
      prevents the accelerated cell ageing of Werner syndrome fibroblasts.&quot; <i>Nat
        Genet</i> <b>24</b>(1):16-17. <a href="http://www.ncbi.nlm.nih.gov/pubmed/10615119">PubMed</a></p>

    <p align="justify">Wynford-Thomas,
      D. (1999). &quot;Cellular senescence and cancer.&quot; <i>J Pathol</i> <b>187</b>(1):100-111.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10341711">PubMed</a></p>

    <p align="justify">Yang,
      J. H., Lee, H. C., Lin, K. J., and Wei, Y. H. (1994). &quot;A specific 4977-bp
      deletion of mitochondrial DNA in human ageing skin.&quot; <i>Arch Dermatol Res</i>
      <b>286</b>(7):386-390. <a href="http://www.ncbi.nlm.nih.gov/pubmed/7818280">PubMed</a></p>

    <p align="justify">Yang,
      X., and Tian, X. C. (2000). &quot;Cloning adult animals - what is the genetic
      age of the clones?&quot; <i>Cloning</i> <b>2</b>(3):123-128. <a
          href="http://www.ncbi.nlm.nih.gov/pubmed/16218848">PubMed</a></p>

    <p align="justify">Yang,
      H., Baur, J. A., Chen, A., Miller, C., and Sinclair, D. A. (2007). &quot;Design
      and synthesis of compounds that extend yeast replicative lifespan.&quot; <i>Aging
        Cell</i> <b>6</b>(1):35-43. <a href="http://www.ncbi.nlm.nih.gov/pubmed/17156081">PubMed</a></p>

    <p align="justify">Yawata,
      T., Kamino, H., Kugoh, H., Katoh, M., Nomura, N., Oishi, M., Horikawa, I.,
      Barrett, J. C., and Oshimura, M. (2003). &quot;Identification of a &lt;/=
      600-kb region on human chromosome 1q42.3 inducing cellular senescence.&quot; <i>Oncogene</i>
      <b>22</b>(2):281-290. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12527897">PubMed</a></p>

    <p align="justify">Young,
      L. E., Fernandes, K., McEvoy, T. G., Butterwith, S. C., Gutierrez, C. G.,
      Carolan, C., Broadbent, P. J., Robinson, J. J., Wilmut, I., and Sinclair, K. D.
      (2001). &quot;Epigenetic change in IGF2R is associated with fetal overgrowth
      after sheep embryo culture.&quot; <i>Nat Genet</i> <b>27</b>(2):153-154.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11175780">PubMed</a></p>

    <p align="justify">Yu,
      G. L., Bradley, J. D., Attardi, L. D., and Blackburn, E. H. (1990). &quot;In
      vivo alteration of telomere sequences and senescence caused by mutated
      Tetrahymena telomerase RNAs.&quot; <i>Nature</i> <b>344</b>(6262):126-132.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/1689810">PubMed</a></p>

    <p align="justify">Yu,
      C. E., Oshima, J., Fu, Y. H., Wijsman, E. M., Hisama, F., Alisch, R., Matthews,
      S., Nakura, J., Miki, T., Ouais, S.<i>, et al.</i> (1996). &quot;Positional
      cloning of the Werner's syndrome gene.&quot; <i>Science</i> <b>272</b>(5259):258-262.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/8602509">PubMed</a></p>

    <p align="justify">Yu,
      C. E., Oshima, J., Wijsman, E. M., Nakura, J., Miki, T., Piussan, C., Matthews,
      S., Fu, Y. H., Mulligan, J., Martin, G. M.<i>, et al.</i> (1997).
      &quot;Mutations in the consensus helicase domains of the Werner syndrome gene.
      Werner's Syndrome Collaborative Group.&quot; <i>Am J Hum Genet</i> <b>60</b>(2):330-341.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/9012406">PubMed</a></p>

    <p align="justify">Yu,
      C., Li, Y., Holmes, A., Szafranski, K., Faulkes, C. G., Coen, C. W.,
      Buffenstein, R., Platzer, M., de Magalhaes, J. P., and Church, G. M. (2011).
      &quot;RNA sequencing reveals differential expression of mitochondrial and
      oxidation reduction genes in the long-lived naked mole-rat when compared to
      mice.&quot; <i>PLoS One</i> <b>6</b>(11):e26729. <a href="http://www.ncbi.nlm.nih.gov/pubmed/22073188">PubMed</a>
    </p>

    <p align="justify">Yuan,
      H., Kaneko, T., and Matsuo, M. (1996). &quot;Increased susceptibility of late
      passage human diploid fibroblasts to oxidative stress.&quot; <i>Exp Gerontol</i>
      <b>31</b>(4):465-474. <a href="http://www.ncbi.nlm.nih.gov/pubmed/9415104">PubMed</a></p>

    <p align="justify">Zahavi,
      A. (1975). &quot;Mate selection-a selection for a handicap.&quot; <i>J Theor
        Biol</i> <b>53</b>(1):205-214. <a href="http://www.ncbi.nlm.nih.gov/pubmed/1195756">PubMed</a></p>

    <p align="justify">Zahn,
      J. M., Poosala, S., Owen, A. B., Ingram, D. K., Lustig, A., Carter, A.,
      Weeraratna, A. T., Taub, D. D., Gorospe, M., Mazan-Mamczarz, K.<i>, et al.</i>
      (2007). &quot;AGEMAP: a gene expression database for aging in mice.&quot; <i>PLoS
        Genet</i> <b>3</b>(11):e201. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18081424">PubMed</a></p>

    <p align="justify">Zec,
      R. F. (1995). &quot;The neuropsychology of aging.&quot; <i>Exp Gerontol</i> <b>30</b>(3-4):431-442.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/7556520">PubMed</a></p>

    <p align="justify">Zhang,
      C., and Cuervo, A. M. (2008). &quot;Restoration of chaperone-mediated autophagy
      in aging liver improves cellular maintenance and hepatic function.&quot; <i>Nat
        Med</i> <b>14</b>(9):959-965. <a href="http://www.ncbi.nlm.nih.gov/pubmed/18690243">PubMed</a></p>

    <p align="justify">Zhang,
      G., Li, J., Purkayastha, S., Tang, Y., Zhang, H., Yin, Y., Li, B., Liu, G., and
      Cai, D. (2013). &quot;Hypothalamic programming of systemic ageing involving
      IKK-beta, NF-kappaB and GnRH.&quot; <i>Nature</i> <b>497</b>(7448):211-216.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/23636330">PubMed</a></p>

    <p align="justify">Zhao,
      Z. Y., Xie, Y., Fu, Y. R., Bogdan, A., and Touitou, Y. (2002). &quot;Aging and
      the circadian rhythm of melatonin: a cross-sectional study of Chinese subjects
      30-110 yr of age.&quot; <i>Chronobiol Int</i> <b>19</b>(6):1171-1182.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/12511033">PubMed</a></p>

    <p align="justify">Zhou,
      Z. Q., Manguino, D., Kewitt, K., Intano, G. W., McMahan, C. A., Herbert, D. C.,
      Hanes, M., Reddick, R., Ikeno, Y., and Walter, C. A. (2001). &quot;Spontaneous
      hepatocellular carcinoma is reduced in transgenic mice overexpressing human O6-
      methylguanine-DNA methyltransferase.&quot; <i>Proc Natl Acad Sci U S A</i> <b>98</b>(22):12566-12571.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/11606727">PubMed</a></p>

    <p align="justify">Zhou,
      J., Liu, J., Cheng, C. J., Patel, T. R., Weller, C. E., Piepmeier, J. M.,
      Jiang, Z., and Saltzman, W. M. (2011). &quot;Biodegradable poly(amine-co-ester)
      terpolymers for targeted gene delivery.&quot; <i>Nat Mater</i> <b>11</b>(1):82-90.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/22138789">PubMed</a></p>

    <p align="justify">Zhu,
      J., Wang, H., Bishop, J. M., and Blackburn, E. H. (1999). &quot;Telomerase
      extends the lifespan of virus-transformed human cells without net telomere
      lengthening.&quot; <i>Proc Natl Acad Sci U S A</i> <b>96</b>(7):3723-3728.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/10097104">PubMed</a></p>

    <p align="justify">Zimmermann,
      S., Voss, M., Kaiser, S., Kapp, U., Waller, C. F., and Martens, U. M. (2003).
      &quot;Lack of telomerase activity in human mesenchymal stem cells.&quot; <i>Leukemia</i>
      <b>17</b>(6):1146-1149. <a href="http://www.ncbi.nlm.nih.gov/pubmed/12764382">PubMed</a></p>

    <p align="justify">Ziuganov,
      V. V. (2005). &quot;A long-lived parasite extending the host life span: the
      pearl mussel Margaritifera margaritifera elongates host life by turns out the
      program of accelerated senescence in salmon Salmo salar.&quot; <i>Dokl Biol Sci</i>
      <b>403</b>:291-294. <a href="http://www.ncbi.nlm.nih.gov/pubmed/16358576">PubMed</a></p>

    <p align="justify">Zuk,
      M. (2009). &quot;The sicker sex.&quot; <i>PLoS Pathog</i> <b>5</b>(1):e1000267.
      <a href="http://www.ncbi.nlm.nih.gov/pubmed/19180235">PubMed</a></p>

    <p align="justify">Zwaan,
      B. J. (2003). &quot;Linking development and aging.&quot; <i>Sci Aging Knowledge
        Environ</i> <b>2003</b>(47):pe32. <a href="http://www.ncbi.nlm.nih.gov/pubmed/14645858">PubMed</a></p>


  </div>
</template>

<script>
export default {
  name: "Bibliography"
}
</script>

<style>
h2 {
  padding-top: 30px;
}

.body {
  margin-left: 10px;
  margin-right: 10px;
}

.sidebar {
  position: -webkit-sticky;
  position: sticky;
  top: 3%;
}

</style>



// WEBPACK FOOTER //
// src/components/Bibliography.vue